console.log("VPX RENDER FILE LOADED — v69 (Fix: color picker drag bug, invisible header text; Shaker is now optional accessory; Avatar in PDF report; Rarity display, ready for backend)");

(function () {

    if (!window.VORYNEX) {
        console.error("VORYNEX namespace not found. Renderer aborted.");
        return;
    }

    const Renderer = {};

    /*
    ==========================================
    Analytics Tracking
    ------------------------------------------
    IMPORTANT ARCHITECTURE NOTE: Shopify's native
    "Google & YouTube" channel does NOT expose a
    global window.gtag to theme/app JavaScript —
    it runs Google's tag inside its own sandboxed
    "Web Pixels" runtime, which only forwards a
    fixed set of Shopify commerce events (page_view,
    add_to_cart, purchase, etc). Custom app-defined
    events like ours have no way to reach that
    sandbox. So this loads a SECOND, independent
    gtag.js — same GA4 Measurement ID, so everything
    lands in the same GA4 property/reports — purely
    to carry our own custom vpx_* events. It's told
    NOT to re-send page_view (send_page_view:false)
    since Shopify's own pixel already covers that;
    duplicating it would inflate pageview counts.

    If GA4 isn't connected via the Google & YouTube
    channel yet, GA4_MEASUREMENT_ID stays a
    placeholder and tracking is skipped entirely —
    nothing breaks, nothing queues waiting for it.

    Events fired:
      - vpx_quiz_start        — hero "start quiz" clicked
      - vpx_quiz_step_view    — each onboarding step shown (step_id, step_number, step_total)
      - vpx_quiz_step_next    — "Next" successfully advances past a step
      - vpx_quiz_complete     — blueprint calculated, Dashboard first shown
      - vpx_lead_captured     — email successfully captured (banner or Reports gate)
      - vpx_report_downloaded — PDF report print triggered
      - vpx_grocery_viewed    — Grocery List page opened
      - vpx_product_click     — a "View Product" link was clicked
    ==========================================
    */

    const GA4_MEASUREMENT_ID = "G-5WNDYMQHBD";

    let gtagReadyPromise = null;

    function loadGtagJs() {
        if (gtagReadyPromise) return gtagReadyPromise;

        gtagReadyPromise = new Promise(function (resolve, reject) {

            if (typeof window.gtag === "function") {
                resolve(window.gtag);
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
            window.gtag("js", new Date());
            window.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: false });

            const script = document.createElement("script");
            script.async = true;
            script.src = "https://www.googletagmanager.com/gtag/js?id=" + GA4_MEASUREMENT_ID;
            script.onload = function () { resolve(window.gtag); };
            script.onerror = function () { reject(new Error("Failed to load gtag.js")); };
            document.head.appendChild(script);
        });

        return gtagReadyPromise;
    }

    if (GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID.indexOf("G-") === 0) {
        loadGtagJs().catch(function (e) {
            console.warn("VORYNEX: analytics gtag.js failed to load —", e);
        });
    }

    function trackEvent(eventName, params) {
        try {
            if (typeof window.gtag === "function") {
                window.gtag("event", eventName, params || {});
            }
        } catch (e) {
            // Analytics must never break the app itself.
            console.warn("VORYNEX: trackEvent failed —", e);
        }
    }

    // "NEW" badge on the Grocery List quick action auto-hides itself
    // after this date — no need to remember to remove it manually.
    const NEW_BADGE_EXPIRY = new Date("2026-07-22T00:00:00Z").getTime();

    // Weekday order Mon→Sun (jsDay follows Date.getDay(): 0=Sun..6=Sat).
    // Module-scoped because both the Dashboard (today's label) and the
    // Meal Plan Week View reference it.
    const WEEK_VIEW_DAYS = [
        { jsDay: 1, key: "day_mon" }, { jsDay: 2, key: "day_tue" }, { jsDay: 3, key: "day_wed" },
        { jsDay: 4, key: "day_thu" }, { jsDay: 5, key: "day_fri" }, { jsDay: 6, key: "day_sat" },
        { jsDay: 0, key: "day_sun" }
    ];

    // Currently selected day in the Meal Plan Week View; defaults to
    // today and is snapped back to today by the midnight rollover timer.
    let mealWeekSelectedDay = new Date().getDay();

    // Which Dashboard mobile tab is active — survives unrelated
    // re-renders (e.g. dismissing the lead banner) so switching tabs
    // then triggering any render elsewhere doesn't silently snap back
    // to "Today".
    let dashboardMobileTab = "today";

    /*
    ==========================================
    i18n shortcut
    ==========================================
    */

    /*
    ==========================================
    View Transitions
    ------------------------------------------
    Uses the native View Transitions API for a
    smooth automatic cross-fade between screens
    (Hero → Onboarding → Dashboard, and step to
    step within the wizard) on browsers that
    support it. On browsers that don't, it just
    runs the update instantly — zero risk.
    ==========================================
    */

    function withViewTransition(updateFn) {
        if (document.startViewTransition) {
            const transition = document.startViewTransition(updateFn);
            // A transition interrupted by a navigation (the language switch) is "skipped": not an error.
            if (transition && transition.ready) transition.ready.catch(function () {});
        } else {
            updateFn();
        }
    }

    function t(key, vars) {
        if (window.VORYNEX.i18n && window.VORYNEX.i18n.t) {
            return window.VORYNEX.i18n.t(key, vars);
        }
        return key;
    }

    function currentLang() {
        return (window.VORYNEX.i18n && window.VORYNEX.i18n.getLang) ? window.VORYNEX.i18n.getLang() : "el";
    }

    // The storefront's locale root ("/", "/el/", "/de/") so links keep the visitor's language.
    function localeRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    }

    /*
    ==========================================
    Mock Data (fallback contract)
    ------------------------------------------
    Used only when window.VORYNEX.state has no
    "blueprint" section yet (engine has not run).
    ==========================================
    */

    const MOCK_DATA = {
        user: {
            name: "Vasilis",
            membership: "Premium Member",
            avatarUrl: "https://i.pravatar.cc/80?img=12"
        },
        targets: {
            calories: 2850,
            protein: 165,
            carbs: 315,
            fat: 75,
            water: 3.5
        },
        calorieBreakdown: [
            { labelKey: "macro_protein", pct: 23, kcal: 660, color: "var(--vpx-green)" },
            { labelKey: "macro_carbs", pct: 44, kcal: 1260, color: "var(--vpx-gold)" },
            { labelKey: "macro_fat", pct: 23, kcal: 675, color: "var(--vpx-red)" },
            { labelKey: "macro_other", pct: 10, kcal: 255, color: "var(--vpx-muted-2)" }
        ],
        macros: [
            { labelKey: "macro_protein", value: 165, unit: "g", pct: 23, color: "var(--vpx-green)" },
            { labelKey: "macro_carbs", value: 315, unit: "g", pct: 44, color: "var(--vpx-gold)" },
            { labelKey: "macro_fat", value: 75, unit: "g", pct: 23, color: "var(--vpx-red)" }
        ],
        scores: {
            overall: 87,
            nutrition: 92,
            training: 88,
            recovery: 85,
            hydration: 90,
            lifestyle: 80,
            mindset: 78
        },
        bodyFatPct: null,
        bodyFatSource: "none",
        meta: {
            tdee: 2666
        },
        goal: {
            phaseKey: "phase_cutting",
            weightDeltaKg: -7.9,
            currentWeight: 82.5,
            targetWeight: 74.5,
            unit: "kg",
            weeksRemaining: 8
        },
        weeklyProgress: [],
        mealPlan: [
            { icon: "🥣", nameKey: "meal_breakfast", imageFile: "food-breakfast.jpg", items: [
                { foodId: "oats", nameKey: "food_oats", grams: 60, kcal: 233, protein: 10.2, carbs: 39.6, fat: 4.2, alternatives: ["whole_wheat_bread"] },
                { foodId: "whey_protein", nameKey: "food_whey_protein", grams: 30, kcal: 120, protein: 24, carbs: 2.4, fat: 1.5, alternatives: [] },
                { foodId: "berries", nameKey: "food_berries", grams: 80, kcal: 46, protein: 0.6, carbs: 11.2, fat: 0.2, alternatives: ["banana", "apple"] }
            ], kcal: 650, protein: 35, carbs: 53, fat: 6 },
            { icon: "🍗", nameKey: "meal_lunch", imageFile: "food-lunch.jpg", items: [
                { foodId: "chicken_breast", nameKey: "food_chicken_breast", grams: 150, kcal: 248, protein: 46.5, carbs: 0, fat: 5.4, alternatives: ["lean_beef", "tuna"] },
                { foodId: "brown_rice", nameKey: "food_brown_rice", grams: 150, kcal: 185, protein: 4.1, carbs: 39, fat: 1.5, alternatives: ["white_rice", "quinoa", "sweet_potato"] },
                { foodId: "broccoli", nameKey: "food_broccoli", grams: 150, kcal: 51, protein: 4.2, carbs: 10.5, fat: 0.6, alternatives: ["cauliflower", "green_beans", "asparagus"] }
            ], kcal: 700, protein: 55, carbs: 50, fat: 8 },
            { icon: "🍌", nameKey: "meal_preWorkout", imageFile: "food-preworkout.jpg", items: [
                { foodId: "banana", nameKey: "food_banana", grams: 120, kcal: 107, protein: 1.3, carbs: 27.6, fat: 0.4, alternatives: ["apple", "berries"] },
                { foodId: "rice_cakes", nameKey: "food_rice_cakes", grams: 30, kcal: 116, protein: 2.4, carbs: 24.6, fat: 0.8, alternatives: ["whole_wheat_bread"] }
            ], kcal: 250, protein: 4, carbs: 64, fat: 1 },
            { icon: "🥤", nameKey: "meal_postWorkout", imageFile: "food-postworkout.jpg", items: [
                { foodId: "whey_protein", nameKey: "food_whey_protein", grams: 35, kcal: 140, protein: 28, carbs: 2.8, fat: 1.8, alternatives: [] },
                { foodId: "banana", nameKey: "food_banana", grams: 100, kcal: 89, protein: 1.1, carbs: 23, fat: 0.3, alternatives: ["apple", "berries"] }
            ], kcal: 220, protein: 29, carbs: 26, fat: 2 },
            { icon: "🐟", nameKey: "meal_dinner", imageFile: "food-dinner.jpg", items: [
                { foodId: "salmon", nameKey: "food_salmon", grams: 150, kcal: 312, protein: 30, carbs: 0, fat: 19.5, alternatives: ["tuna", "lean_beef"] },
                { foodId: "quinoa", nameKey: "food_quinoa", grams: 120, kcal: 144, protein: 5.3, carbs: 25.2, fat: 2.3, alternatives: ["brown_rice", "sweet_potato", "white_rice"] },
                { foodId: "spinach", nameKey: "food_spinach", grams: 100, kcal: 23, protein: 2.9, carbs: 3.6, fat: 0.4, alternatives: ["mixed_salad", "asparagus"] }
            ], kcal: 700, protein: 38, carbs: 29, fat: 27 }
        ],
        supplementRecommendations: [
            { collectionHandle: "proteins", reasonKey: "supp_reason_protein", minWeeks: null },
            { collectionHandle: "creatine-monohydrate", reasonKey: "supp_reason_creatine_general", minWeeks: 4 }
        ]
    };

    /*
    ==========================================
    Data Accessor
    ==========================================
    */

    function getData() {
        if (window.VORYNEX.state && window.VORYNEX.state.getSection) {
            const blueprint = window.VORYNEX.state.getSection("blueprint");
            if (blueprint) return blueprint;
        }
        return MOCK_DATA;
    }

    /*
    ==========================================
    SVG Chart Helpers
    ==========================================
    */

    Renderer.svgDonut = function (segments, size, strokeWidth, centerLabel, centerSub) {

        const radius = (size - strokeWidth) / 2;
        const circumference = 2 * Math.PI * radius;
        const center = size / 2;

        let offset = 0;
        let circles = "";

        segments.forEach(function (seg) {
            const segLength = (seg.pct / 100) * circumference;
            circles += `
                <circle
                    cx="${center}" cy="${center}" r="${radius}"
                    fill="none"
                    stroke="${seg.color}"
                    stroke-width="${strokeWidth}"
                    stroke-dasharray="${segLength} ${circumference - segLength}"
                    stroke-dashoffset="${-offset}"
                    transform="rotate(-90 ${center} ${center})"
                    stroke-linecap="round"
                />
            `;
            offset += segLength + 2;
        });

        return `
            <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" class="vpx-svg-reveal">
                <circle cx="${center}" cy="${center}" r="${radius}" fill="none" stroke="var(--vpx-surface-2)" stroke-width="${strokeWidth}" />
                ${circles}
                <text x="${center}" y="${center - 6}" text-anchor="middle" font-size="22" font-weight="700" class="vpx-chart-center vpx-chart-count" data-count-target="${centerLabel}">0</text>
                <text x="${center}" y="${center + 14}" text-anchor="middle" font-size="10" fill="var(--vpx-muted)">${centerSub}</text>
            </svg>
        `;
    };

    Renderer.svgRadar = function (data, size) {

        const center = size / 2;
        const radius = size / 2 - 36;
        const angleStep = (Math.PI * 2) / data.length;

        function pointAt(value, index) {
            const angle = angleStep * index - Math.PI / 2;
            const r = (value / 100) * radius;
            return {
                x: center + r * Math.cos(angle),
                y: center + r * Math.sin(angle)
            };
        }

        function labelPointAt(index) {
            const angle = angleStep * index - Math.PI / 2;
            const r = radius + 22;
            return {
                x: center + r * Math.cos(angle),
                y: center + r * Math.sin(angle)
            };
        }

        const gridLevels = [25, 50, 75, 100];
        let gridRings = "";

        gridLevels.forEach(function (level) {
            const ringPoints = data.map(function (d, i) {
                const p = pointAt(level, i);
                return `${p.x},${p.y}`;
            }).join(" ");
            gridRings += `<polygon points="${ringPoints}" fill="none" stroke="var(--vpx-border)" stroke-width="1" />`;
        });

        const valuePoints = data.map(function (d, i) {
            const p = pointAt(d.value, i);
            return `${p.x},${p.y}`;
        }).join(" ");

        let labels = "";

        data.forEach(function (d, i) {
            const lp = labelPointAt(i);
            labels += `<text x="${lp.x}" y="${lp.y}" text-anchor="middle" font-size="10" fill="var(--vpx-muted)">${d.label}</text>`;
        });

        return `
            <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" class="vpx-svg-reveal">
                ${gridRings}
                <polygon points="${valuePoints}" fill="var(--vpx-gold-dim)" stroke="var(--vpx-gold)" stroke-width="2" class="vpx-radar-poly" />
                ${labels}
                <text x="${center}" y="${center - 4}" text-anchor="middle" font-size="26" font-weight="700" class="vpx-chart-center vpx-chart-count" data-count-target="${data.overall || 0}">0</text>
            </svg>
        `;
    };

    Renderer.svgLine = function (points, width, height) {

        const values = points.map(function (p) { return p.value; });
        const min = Math.min.apply(null, values);
        const max = Math.max.apply(null, values);
        const pad = 16;

        const stepX = (width - pad * 2) / (points.length - 1);

        function yFor(value) {
            if (max === min) return height / 2;
            return height - pad - ((value - min) / (max - min)) * (height - pad * 2);
        }

        const coords = points.map(function (p, i) {
            return { x: pad + i * stepX, y: yFor(p.value) };
        });

        const linePath = coords.map(function (c, i) {
            return (i === 0 ? "M" : "L") + c.x + " " + c.y;
        }).join(" ");

        const dots = coords.map(function (c) {
            return `<circle cx="${c.x}" cy="${c.y}" r="3.5" fill="var(--vpx-gold)" />`;
        }).join("");

        const labels = points.map(function (p, i) {
            return `<text x="${coords[i].x}" y="${height - 2}" text-anchor="middle" font-size="9" fill="var(--vpx-muted)">${p.label}</text>`;
        }).join("");

        return `
            <svg viewBox="0 0 ${width} ${height + 12}" width="100%" height="${height + 12}" class="vpx-svg-reveal">
                <path d="${linePath}" fill="none" stroke="var(--vpx-gold)" stroke-width="2" />
                ${dots}
                ${labels}
            </svg>
        `;
    };

    /*
    ==========================================
    Language Switcher
    ==========================================
    */

    const FLAG_SVGS = {
        el: `<svg width="16" height="12" viewBox="0 0 16 12" style="vertical-align:-2px; margin-right:4px;">
            <rect width="16" height="12" fill="#0D5EAF"/>
            <rect y="1.33" width="16" height="1.33" fill="#fff"/>
            <rect y="4" width="16" height="1.33" fill="#fff"/>
            <rect y="6.67" width="16" height="1.33" fill="#fff"/>
            <rect y="9.33" width="16" height="1.33" fill="#fff"/>
            <rect width="6.67" height="6.67" fill="#0D5EAF"/>
            <rect x="2.67" width="1.33" height="6.67" fill="#fff"/>
            <rect y="2.67" width="6.67" height="1.33" fill="#fff"/>
        </svg>`,
        en: `<svg width="16" height="12" viewBox="0 0 16 12" style="vertical-align:-2px; margin-right:4px;">
            <rect width="16" height="12" fill="#00247D"/>
            <path d="M0,0 L16,12 M16,0 L0,12" stroke="#fff" stroke-width="2.4"/>
            <path d="M0,0 L16,12 M16,0 L0,12" stroke="#CF142B" stroke-width="0.8"/>
            <rect x="6.4" width="3.2" height="12" fill="#fff"/>
            <rect y="4" width="16" height="4" fill="#fff"/>
            <rect x="7.2" width="1.6" height="12" fill="#CF142B"/>
            <rect y="4.8" width="16" height="2.4" fill="#CF142B"/>
        </svg>`,
        de: `<svg width="16" height="12" viewBox="0 0 16 12" style="vertical-align:-2px; margin-right:4px;">
            <rect width="16" height="4" fill="#000"/>
            <rect y="4" width="16" height="4" fill="#DD0000"/>
            <rect y="8" width="16" height="4" fill="#FFCE00"/>
        </svg>`
    };

    Renderer.langSwitcher = function () {

        const langs = (window.VORYNEX.i18n && window.VORYNEX.i18n.supportedLangs) || ["el", "en", "de"];
        const active = currentLang();
        const labels = { el: "ΕΛ", en: "EN", de: "DE" };

        const buttons = langs.map(function (lang) {
            const activeClass = lang === active ? " is-active" : "";
            return `<button type="button" class="vpx-lang-btn${activeClass}" data-vpx-lang="${lang}">${FLAG_SVGS[lang] || ""}${labels[lang] || lang.toUpperCase()}</button>`;
        }).join("");

        return `<div class="vpx-lang-switcher">${buttons}</div>`;
    };

    // Switches the whole storefront through Shopify's localization form (the same
    // request the header's language picker sends), so the header, footer and app
    // always change language together. The app state lives in localStorage and
    // survives the reload.
    function switchStorefrontLanguage(lang) {
        const form = document.createElement("form");
        form.method = "post";
        form.action = localeRoot() + "localization";
        const fields = {
            form_type: "localization",
            utf8: "✓",
            _method: "put",
            locale_code: lang,
            return_to: window.location.pathname + window.location.search
        };
        Object.keys(fields).forEach(function (name) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = fields[name];
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
    }

    function bindLangSwitcherEvents(onChange) {
        // Only the app's own EN/ΕΛ/DE buttons. <html data-vpx-lang> is the language suggestion's state
        // (snippets/vpx-language-suggest.liquid): matched too, it turned every click on the page into a
        // "switch to none" and reloaded it.
        const supported = (window.VORYNEX.i18n && window.VORYNEX.i18n.supportedLangs) || ["el", "en", "de"];
        document.querySelectorAll("button.vpx-lang-btn[data-vpx-lang]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const lang = btn.getAttribute("data-vpx-lang");
                if (supported.indexOf(lang) === -1) return;
                if (window.VORYNEX.i18n && window.VORYNEX.i18n.setLang) {
                    window.VORYNEX.i18n.setLang(lang);
                }
                const pageLang = (document.documentElement.lang || "").slice(0, 2).toLowerCase();
                if (lang !== pageLang) {
                    switchStorefrontLanguage(lang);
                    return;
                }
                onChange();
            });
        });
    }

    /*
    ==========================================
    Shell — Sidebar
    ==========================================
    */

    const NAV_ITEMS = [
        { id: "dashboard", key: "nav_dashboard" },
        { id: "profile", key: "nav_profile" },
        { id: "goals", key: "nav_goals" },
        { id: "schedule", key: "nav_schedule" },
        { id: "workout", key: "nav_workout" },
        { id: "coach", key: "nav_coach" },
        { id: "nutrition-plan", key: "nav_nutritionPlan" },
        { id: "meal-plan", key: "nav_mealPlan" },
        { id: "supplements", key: "nav_supplements" },
        { id: "supplement-timing", key: "nav_supplementTiming" },
        { id: "progress", key: "nav_progress" },
        { id: "achievements", key: "nav_achievements" },
        { id: "performance-scores", key: "nav_performanceScores" },
        { id: "timeline", key: "nav_timeline" },
        { id: "reports", key: "nav_reports" },
        { id: "settings", key: "nav_settings" }
    ];

    const MOBILE_BOTTOM_NAV_ITEMS = [
        { id: "dashboard", icon: "🏠", key: "nav_dashboard" },
        { id: "meal-plan", icon: "🍽️", key: "nav_mealPlan" },
        { id: "reports", icon: "📄", key: "nav_reports" },
        { id: "schedule", icon: "📅", key: "nav_schedule" }
    ];

    Renderer.mobileBottomNav = function (activeId) {
        const items = MOBILE_BOTTOM_NAV_ITEMS.map(function (item) {
            const activeClass = item.id === activeId ? " is-active" : "";
            return `
                <button type="button" class="vpx-bottom-nav-item${activeClass}" data-vpx-nav="${item.id}">
                    <span class="vpx-bottom-nav-icon">${item.icon}</span>
                    <span class="vpx-bottom-nav-label">${t(item.key)}</span>
                </button>
            `;
        }).join("");
        return `<nav class="vpx-bottom-nav">${items}</nav>`;
    };

    Renderer.sidebar = function (activeId) {

        const items = NAV_ITEMS.map(function (item) {
            const activeClass = item.id === activeId ? " is-active" : "";
            return `
                <div class="vpx-nav-item${activeClass}" data-vpx-nav="${item.id}">
                    <span class="vpx-nav-icon">•</span>
                    <span>${t(item.key)}</span>
                </div>
            `;
        }).join("");

        return `
            <aside class="vpx-sidebar">

                <div class="vpx-sidebar-logo">
                    <span class="vpx-mark">◆</span>
                    <div class="vpx-logo-text">
                        <strong>VORYNEX</strong>
                        <span>${t("sidebar_tagline")}</span>
                    </div>
                </div>

                ${items}

                ${Renderer.langSwitcher()}

                <div class="vpx-sidebar-footer">
                    <div class="vpx-mark">◆</div>
                    <p>${t("sidebar_unlock")}</p>
                    <button class="vpx-btn-gold" type="button">${t("sidebar_upgrade")}</button>
                    <button class="vpx-btn-restart" type="button" data-vpx-action="restart-quiz">${t("btn_restartQuiz")}</button>
                </div>

            </aside>
        `;
    };

    /*
    ==========================================
    Shell — Header
    ==========================================
    */

    Renderer.header = function (data) {

        const customer = window.VPX_CUSTOMER;

        const displayName = customer
            ? (customer.firstName || (customer.email ? customer.email.split("@")[0] : t("header_guest_label")))
            : t("header_guest_label");

        const statusLabel = customer ? t("header_signed_in") : t("header_not_signed_in");
        const initial = displayName ? displayName.charAt(0).toUpperCase() : "V";

        const avatarThumb = (window.VORYNEX.avatar && window.VORYNEX.avatar.render)
            ? `<div class="vpx-user-avatar-thumb">${window.VORYNEX.avatar.render()}</div>`
            : `<div class="vpx-user-avatar-initial">${initial}</div>`;

        const chipInner = `
            ${avatarThumb}
            <div>
                <strong>${displayName}</strong>
                <span>${statusLabel}</span>
            </div>
        `;

        // Whole chip → Profile, for everyone. Profile is where the
        // avatar lives and where guests can still fully use the app
        // (same local-first philosophy as the rest of the Blueprint) —
        // actual Shopify sign-in stays a separate, explicit choice
        // inside Profile itself, not forced via this chip.
        const userChip = `<button type="button" class="vpx-user-chip vpx-user-chip-link" data-vpx-nav="profile">${chipInner}</button>`;

        return `
            <header class="vpx-header">
                <div>
                    <h1>${t("header_title")}</h1>
                    <p>${t("header_subtitle")}</p>
                </div>
                ${userChip}
            </header>
        `;
    };

    /*
    ==========================================
    Dashboard — Summary Stat Cards
    ==========================================
    */

    const STAT_ICONS = {
        calories: "🔥",
        protein: "🌿",
        carbs: "🌾",
        fat: "🥑",
        water: "💧"
    };

    Renderer.statCards = function (targets) {

        const order = [
            { key: "calories", labelKey: "stat_calories", unitKey: "unit_kcalDay" },
            { key: "protein", labelKey: "stat_protein", unitKey: "unit_gDay" },
            { key: "carbs", labelKey: "stat_carbs", unitKey: "unit_gDay" },
            { key: "fat", labelKey: "stat_fat", unitKey: "unit_gDay" },
            { key: "water", labelKey: "stat_water", unitKey: "unit_lDay" }
        ];

        const cards = order.map(function (item) {
            const decimals = item.key === "water" ? 1 : 0;
            return `
                <div class="vpx-card vpx-stat-card">
                    <div class="vpx-stat-top">
                        <span>${t(item.labelKey)}</span>
                        <span class="vpx-stat-icon">${STAT_ICONS[item.key]}</span>
                    </div>
                    <div class="vpx-stat-value">
                        <span class="vpx-count" data-count-target="${targets[item.key]}" data-count-decimals="${decimals}">0</span><small>${t(item.unitKey)}</small>
                    </div>
                </div>
            `;
        }).join("");

        return `<div class="vpx-grid vpx-grid-5">${cards}</div>`;
    };

    /*
    ==========================================
    Dashboard — Breakdown / Macros / Scores Row
    ==========================================
    */

    Renderer.calorieBreakdownCard = function (data) {

        const legend = data.calorieBreakdown.map(function (seg) {
            return `
                <div class="vpx-legend-row">
                    <span><span class="vpx-legend-dot" style="background:${seg.color}"></span>${t(seg.labelKey)} (${seg.pct}%)</span>
                    <span class="vpx-legend-val">${seg.kcal} kcal</span>
                </div>
            `;
        }).join("");

        const donut = Renderer.svgDonut(
            data.calorieBreakdown,
            160, 18,
            data.targets.calories,
            "kcal"
        );

        return `
            <div class="vpx-card">
                <h2>${t("card_calorieBreakdown")}</h2>
                <div style="display:flex; align-items:center; gap:20px; flex-wrap:wrap;">
                    <div style="flex:1; min-width:150px;">${donut}</div>
                    <div class="vpx-legend" style="flex:1; min-width:140px;">${legend}</div>
                </div>
            </div>
        `;
    };

    Renderer.macroDistributionCard = function (data) {

        const bars = data.macros.map(function (m) {
            return `
                <div class="vpx-bar-row">
                    <div class="vpx-bar-label">
                        <span>${t(m.labelKey)}</span>
                        <span>${m.value}${m.unit} · ${m.pct}%</span>
                    </div>
                    <div class="vpx-bar-track">
                        <div class="vpx-bar-fill vpx-fill-animated" data-fill-target="${m.pct}%" style="width:0%; background:${m.color};"></div>
                    </div>
                </div>
            `;
        }).join("");

        return `
            <div class="vpx-card">
                <h2>${t("card_macroDistribution")}</h2>
                ${bars}
            </div>
        `;
    };

    Renderer.performanceScoresCard = function (scores) {

        const radarData = [
            { label: t("radar_nutrition"), value: scores.nutrition },
            { label: t("radar_training"), value: scores.training },
            { label: t("radar_recovery"), value: scores.recovery },
            { label: t("radar_hydration"), value: scores.hydration },
            { label: t("radar_lifestyle"), value: scores.lifestyle },
            { label: t("radar_mindset"), value: scores.mindset }
        ];
        radarData.overall = scores.overall;

        return `
            <div class="vpx-card" style="text-align:center;">
                <h2>${t("card_performanceScores")}</h2>
                ${Renderer.svgRadar(radarData, 220)}
                <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase; letter-spacing:1px; margin-top:-6px;">${t("card_overallScore")}</div>
            </div>
        `;
    };

    const SCORE_EXPLAIN_KEYS = {
        nutrition: "score_explain_nutrition",
        training: "score_explain_training",
        recovery: "score_explain_recovery",
        hydration: "score_explain_hydration",
        lifestyle: "score_explain_lifestyle",
        mindset: "score_explain_mindset"
    };

    Renderer.viewPerformanceScores = function (data) {

        const scores = data.scores;
        const radarData = [
            { label: t("radar_nutrition"), value: scores.nutrition },
            { label: t("radar_training"), value: scores.training },
            { label: t("radar_recovery"), value: scores.recovery },
            { label: t("radar_hydration"), value: scores.hydration },
            { label: t("radar_lifestyle"), value: scores.lifestyle },
            { label: t("radar_mindset"), value: scores.mindset }
        ];
        radarData.overall = scores.overall;

        const rows = ["nutrition", "training", "recovery", "hydration", "lifestyle", "mindset"].map(function (key) {
            return `
                <div class="vpx-score-row">
                    <div class="vpx-score-row-top">
                        <span>${t("radar_" + key)}</span>
                        <strong>${scores[key]}/100</strong>
                    </div>
                    <div class="vpx-goal-bar-track" style="margin-bottom:6px;">
                        <div class="vpx-goal-bar-fill" style="width:${scores[key]}%;"></div>
                    </div>
                    <p class="vpx-score-explain">${t(SCORE_EXPLAIN_KEYS[key])}</p>
                </div>
            `;
        }).join("");

        return `
            <div class="vpx-grid">
                <div class="vpx-card" style="text-align:center;">
                    <h2>${t("page_scores_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_scores_hint")}</p>
                    ${Renderer.svgRadar(radarData, 260)}
                    <div style="font-size:13px; color:var(--vpx-gold); font-weight:600; margin-top:-4px;">${t("card_overallScore")}: ${scores.overall}/100</div>
                </div>
                <div class="vpx-card">
                    ${rows}
                </div>
                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    /*
    ==========================================
    Dashboard — Meal Preview / Goal / Weekly
    ==========================================
    */

    function foodMacrosForGrams(foodId, grams) {
        const food = window.VORYNEX.foods ? window.VORYNEX.foods[foodId] : null;
        if (!food) return { kcal: 0, protein: 0, carbs: 0, fat: 0 };
        const factor = grams / 100;
        return {
            kcal: food.kcal100g * factor,
            protein: food.protein100g * factor,
            carbs: food.carbs100g * factor,
            fat: food.fat100g * factor
        };
    }

    Renderer.mealPreviewCard = function (mealPlan) {

        const blocks = mealPlan.map(function (m, mealIdx) {

            const imageUrl = (window.VPX_MEAL_IMAGES && m.imageFile)
                ? window.VPX_MEAL_IMAGES[m.imageFile]
                : null;

            const thumb = imageUrl
                ? `<div class="vpx-list-icon vpx-list-icon-photo"><img src="${imageUrl}" alt="${t(m.nameKey)}" onerror="this.parentElement.innerHTML='${m.icon}';" /></div>`
                : `<div class="vpx-list-icon">${m.icon}</div>`;

            const ingredientRows = (m.items || []).map(function (item, itemIdx) {

                const alternatives = item.alternatives || [];
                const hasAlternatives = alternatives.length > 0;

                const options = alternatives.map(function (altFoodId) {
                    const altFood = window.VORYNEX.foods ? window.VORYNEX.foods[altFoodId] : null;
                    if (!altFood) return "";
                    return `<option value="${altFoodId}">${t(altFood.nameKey)}</option>`;
                }).join("");

                const swapControl = hasAlternatives
                    ? `
                        <select class="vpx-swap-select" data-meal-idx="${mealIdx}" data-item-idx="${itemIdx}">
                            <option value="">⇄</option>
                            ${options}
                        </select>
                    `
                    : "";

                return `
                    <div class="vpx-ingredient-row">
                        <span class="vpx-ingredient-name">${item.grams}g ${t(item.nameKey)}</span>
                        <span class="vpx-ingredient-kcal">${item.kcal} kcal</span>
                        ${swapControl}
                    </div>
                `;
            }).join("");

            const recipeSwitcher = (m.availableRecipes && m.availableRecipes.length > 1)
                ? `
                    <select class="vpx-recipe-select" data-slot-id="${m.slotId}">
                        ${m.availableRecipes.map(function (r) {
                            const selected = r.id === m.recipeId ? " selected" : "";
                            return `<option value="${r.id}"${selected}>${t(r.nameKey)}</option>`;
                        }).join("")}
                    </select>
                `
                : "";

            const selectedCondiments = m.selectedCondiments || [];

            const condimentsSection = (m.availableCondiments && m.availableCondiments.length > 0)
                ? (function () {

                    const chips = m.availableCondiments.map(function (c) {
                        const isOn = selectedCondiments.indexOf(c.id) !== -1;
                        return `
                            <button type="button" class="vpx-condiment-chip${isOn ? " is-active" : ""}"
                                data-slot-id="${m.slotId}" data-condiment-id="${c.id}">
                                ${c.icon} ${t(c.nameKey)} · ${c.grams}g (+${c.kcal} kcal)
                            </button>
                        `;
                    }).join("");

                    const extrasKcal = m.availableCondiments
                        .filter(function (c) { return selectedCondiments.indexOf(c.id) !== -1; })
                        .reduce(function (sum, c) { return sum + c.kcal; }, 0);

                    const totalLine = extrasKcal > 0
                        ? `<div class="vpx-extras-total">${t("extras_total", { total: m.kcal + extrasKcal })}</div>`
                        : "";

                    return `
                        <div class="vpx-condiments-block">
                            <div class="vpx-extras-label">${t("extras_label")}</div>
                            <div class="vpx-condiments-row">${chips}</div>
                            ${totalLine}
                        </div>
                    `;
                })()
                : "";

            return `
                <div class="vpx-meal-block">
                    <div class="vpx-list-row">
                        ${thumb}
                        <div class="vpx-list-text">
                            <strong>${t(m.nameKey)}</strong>
                            ${recipeSwitcher}
                        </div>
                        <div class="vpx-list-kcal">${m.kcal} kcal</div>
                    </div>
                    <div class="vpx-meal-ingredients">
                        ${ingredientRows}
                    </div>
                    ${condimentsSection}
                </div>
            `;
        }).join("");

        return `
            <div class="vpx-card">
                <h2>${t("card_mealPlanPreview")}</h2>
                ${blocks}
                <button class="vpx-btn-gold" style="margin-top:16px;" data-vpx-nav="meal-plan">${t("btn_viewFullMealPlan")}</button>
            </div>
        `;
    };

    function performFoodSwap(mealIdx, itemIdx, newFoodId) {

        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

        const blueprint = window.VORYNEX.state.getSection("blueprint");
        if (!blueprint || !blueprint.mealPlan) return;

        const meal = blueprint.mealPlan[mealIdx];
        if (!meal || !meal.items || !meal.items[itemIdx]) return;

        const oldItem = meal.items[itemIdx];
        const newFood = window.VORYNEX.foods ? window.VORYNEX.foods[newFoodId] : null;
        if (!newFood) return;

        // Preserve this item's calorie contribution to the meal by
        // scaling the new food's grams to match the old item's kcal —
        // keeps the daily calorie target intact after a swap.
        const targetKcal = oldItem.kcal;
        const newGrams = newFood.kcal100g > 0
            ? Math.max(1, Math.round(targetKcal / (newFood.kcal100g / 100)))
            : oldItem.grams;

        const macros = foodMacrosForGrams(newFoodId, newGrams);

        meal.items[itemIdx] = {
            foodId: newFoodId,
            nameKey: newFood.nameKey,
            icon: newFood.icon,
            grams: newGrams,
            kcal: Math.round(macros.kcal),
            protein: Math.round(macros.protein * 10) / 10,
            carbs: Math.round(macros.carbs * 10) / 10,
            fat: Math.round(macros.fat * 10) / 10,
            category: newFood.category,
            alternatives: newFood.alternatives || []
        };

        // Recompute meal-level totals from its (now updated) items.
        meal.kcal = Math.round(meal.items.reduce(function (s, i) { return s + i.kcal; }, 0));
        meal.protein = Math.round(meal.items.reduce(function (s, i) { return s + i.protein; }, 0));
        meal.carbs = Math.round(meal.items.reduce(function (s, i) { return s + i.carbs; }, 0));
        meal.fat = Math.round(meal.items.reduce(function (s, i) { return s + i.fat; }, 0));

        window.VORYNEX.state.setSection("blueprint", blueprint);
        Renderer.render();
    }

    function bindMealSwapEvents() {
        document.querySelectorAll(".vpx-swap-select").forEach(function (select) {
            select.addEventListener("change", function () {
                if (!select.value) return;
                const mealIdx = Number(select.getAttribute("data-meal-idx"));
                const itemIdx = Number(select.getAttribute("data-item-idx"));
                performFoodSwap(mealIdx, itemIdx, select.value);
            });
        });
    }

    function performRecipeChange(slotId, newRecipeId) {
        // The dashboard preview always shows TODAY — so a recipe change
        // made there is a per-day choice for today's weekday, identical
        // to making it inside the Week View. One code path, one truth.
        performDayRecipeChange(new Date().getDay(), slotId, newRecipeId);
    }

    function bindRecipeSwitcherEvents() {
        document.querySelectorAll(".vpx-recipe-select").forEach(function (select) {
            select.addEventListener("change", function () {

                // Inside the Week View, recipe choices are saved for the
                // SPECIFIC day being viewed (Monday omelette, Tuesday
                // pancakes...). Everywhere else (dashboard preview),
                // the original global behavior applies.
                const weekWrapper = select.closest("[data-vpx-week-day]");
                if (weekWrapper) {
                    performDayRecipeChange(
                        Number(weekWrapper.getAttribute("data-vpx-week-day")),
                        select.getAttribute("data-slot-id"),
                        select.value
                    );
                    return;
                }

                performRecipeChange(select.getAttribute("data-slot-id"), select.value);
            });
        });
    }

    function bindCondimentEvents() {
        document.querySelectorAll(".vpx-condiment-chip").forEach(function (chip) {
            chip.addEventListener("click", function () {

                if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

                const slotId = chip.getAttribute("data-slot-id");
                const condimentId = chip.getAttribute("data-condiment-id");

                const blueprint = window.VORYNEX.state.getSection("blueprint");
                if (!blueprint) return;

                blueprint.condimentSelections = blueprint.condimentSelections || {};
                const current = blueprint.condimentSelections[slotId] || [];
                const idx = current.indexOf(condimentId);

                if (idx === -1) {
                    current.push(condimentId);
                } else {
                    current.splice(idx, 1);
                }

                blueprint.condimentSelections[slotId] = current;

                const meal = blueprint.mealPlan.filter(function (m) { return m.slotId === slotId; })[0];
                if (meal) meal.selectedCondiments = current;

                window.VORYNEX.state.setSection("blueprint", blueprint);
                Renderer.render();
            });
        });
    }

    Renderer.goalProgressCard = function (goal, bodyFatPct, tdee, targetCalories) {

        // No elapsed-time tracking exists yet (that requires weekly
        // check-ins + persistence — the future Adaptive Engine). Until
        // then, showing a fake "% complete" would be misleading, so
        // this starts at 0 and will become real once that data exists.
        const fillPct = 0;

        const bodyFatRow = (bodyFatPct !== null && bodyFatPct !== undefined)
            ? `
                <div style="display:flex; justify-content:space-between; margin-bottom:14px;">
                    <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase;">${t("goal_bodyFat")}</div>
                    <strong style="font-size:14px; color:var(--vpx-gold-light);">${bodyFatPct}%</strong>
                </div>
            `
            : "";

        const calorieCompareRow = (tdee !== null && tdee !== undefined && targetCalories !== null && targetCalories !== undefined)
            ? `
                <div class="vpx-goal-calorie-compare">
                    <div class="vpx-goal-calorie-col">
                        <div class="vpx-goal-calorie-label">${t("goal_currentMaintenance")}</div>
                        <strong class="vpx-goal-calorie-value">${tdee} kcal</strong>
                    </div>
                    <div class="vpx-goal-calorie-arrow">→</div>
                    <div class="vpx-goal-calorie-col">
                        <div class="vpx-goal-calorie-label">${t("goal_targetCalories")}</div>
                        <strong class="vpx-goal-calorie-value vpx-goal-calorie-value-gold">${targetCalories} kcal</strong>
                    </div>
                </div>
            `
            : "";

        return `
            <div class="vpx-card">
                <h2>${t("card_goalProgress")}</h2>
                <div class="vpx-goal-phase">${t(goal.phaseKey)}</div>
                <div style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">
                    ${goal.weightDeltaKg > 0 ? "+" : ""}${goal.weightDeltaKg} kg · ${goal.weeksRemaining} ${t("goal_weeks")}
                </div>

                ${calorieCompareRow}

                <div style="display:flex; justify-content:space-between; margin-bottom:6px;">
                    <div>
                        <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase;">${t("goal_currentWeight")}</div>
                        <strong style="font-size:18px;">${formatWeightKg(goal.currentWeight)}</strong>
                    </div>
                    <div style="text-align:right;">
                        <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase;">${t("goal_targetWeight")}</div>
                        <strong style="font-size:18px;">${formatWeightKg(goal.targetWeight)}</strong>
                    </div>
                </div>

                <div class="vpx-goal-bar-track">
                    <div class="vpx-goal-bar-fill" style="width:${fillPct}%;"></div>
                </div>

                ${bodyFatRow}

                <div class="vpx-goal-remaining">${t("goal_timeRemaining")}: ${goal.weeksRemaining} ${t("goal_weeks")}</div>

                <button class="vpx-btn-gold" data-vpx-nav="timeline">${t("btn_viewTimeline")}</button>
            </div>
        `;
    };

    Renderer.weeklyProgressCard = function (weeklyProgress) {

        if (!weeklyProgress || weeklyProgress.length < 2) {
            return `
                <div class="vpx-card">
                    <h2>${t("card_weeklyProgress")}</h2>
                    <div style="font-size:12px; color:var(--vpx-muted); padding:30px 0; text-align:center;">
                        ${t("weekly_empty")}
                    </div>
                </div>
            `;
        }

        return `
            <div class="vpx-card">
                <h2>${t("card_weeklyProgress")}</h2>
                ${Renderer.svgLine(weeklyProgress, 320, 140)}
            </div>
        `;
    };

    /*
    ==========================================
    Dashboard — Supplements
    ==========================================
    */

    const COLLECTION_NAME_KEYS = {
        "proteins": "coll_proteins",
        "creatine-monohydrate": "coll_creatine_monohydrate",
        "pre-workout": "coll_pre_workout",
        "vitamins-electrolytes": "coll_vitamins_electrolytes",
        "collagen-joint-support": "coll_collagen_joint_support"
    };

    Renderer.workoutTeaserCard = function (workoutPlan) {

        if (!workoutPlan) {
            return `
                <div class="vpx-card">
                    <h2>${t("card_workout")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_workout_empty")}</p>
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="schedule">${t("page_workout_goto_schedule")}</button>
                </div>
            `;
        }

        const dayCount = workoutPlan.days.length;

        return `
            <div class="vpx-card">
                <h2>${t("card_workout")}</h2>
                <div class="vpx-goal-phase">${t(workoutPlan.splitNameKey)}</div>
                <div style="font-size:12px; color:var(--vpx-muted); margin-bottom:16px;">
                    ${dayCount} ${t("label_workout_days_per_week")}
                </div>
                <button type="button" class="vpx-btn-gold" data-vpx-nav="workout">${t("btn_viewFullProgram")}</button>
            </div>
        `;
    };

    function buildSupplementBlocks(recommendations) {

        return (recommendations || []).map(function (rec) {

            const products = (window.VPX_COLLECTIONS && window.VPX_COLLECTIONS[rec.collectionHandle]) || [];
            const topProduct = products.length ? products[0] : null;

            const nameKey = COLLECTION_NAME_KEYS[rec.collectionHandle] || rec.collectionHandle;

            const minWeeksNote = rec.minWeeks
                ? `<div class="vpx-supp-minweeks">${t("supp_min_weeks", { weeks: rec.minWeeks })}</div>`
                : "";

            const realVariants = (topProduct && topProduct.variants)
                ? topProduct.variants.filter(function (v) { return v.title && v.title !== "Default Title"; })
                : [];

            let optionChips = "";

            if (realVariants.length > 1) {
                // Same product, multiple variants (e.g. flavors set up as variants).
                optionChips = `<div class="vpx-supp-variants">${realVariants.map(function (v) {
                    return `<span class="vpx-supp-variant-chip">${v.title}</span>`;
                }).join("")}</div>`;
            } else if (products.length > 1) {
                // Flavors listed as separate sibling products in the same collection.
                optionChips = `<div class="vpx-supp-variants">${products.slice(1, 4).map(function (p) {
                    return `<a href="${p.url}" target="_blank" rel="noopener" class="vpx-supp-variant-chip vpx-supp-variant-chip-link">${p.title}</a>`;
                }).join("")}</div>`;
            }

            const productBlock = topProduct
                ? `
                    <a href="${topProduct.url}" target="_blank" rel="noopener" class="vpx-supp-product-row">
                        <div class="vpx-supp-product-photo"><img src="${topProduct.image}" alt="${topProduct.title}" /></div>
                        <div class="vpx-supp-product-text">
                            <strong>${topProduct.title}</strong>
                            <span>${topProduct.price}${topProduct.unit ? ` <small class="vpx-unit-price">(${topProduct.unit})</small>` : ""}</span>
                        </div>
                        <span class="vpx-supp-cta">${t("btn_viewProduct")}</span>
                    </a>
                    ${optionChips}
                `
                : `<a href="${localeRoot()}collections/${rec.collectionHandle}" target="_blank" rel="noopener" class="vpx-supp-cta-link">${t("btn_viewCollection")}</a>`;

            return `
                <div class="vpx-supp-block">
                    <div class="vpx-supp-block-header">${t(nameKey)}</div>
                    <p class="vpx-supp-reason">${t(rec.reasonKey)}</p>
                    ${minWeeksNote}
                    ${productBlock}
                </div>
            `;
        }).join("");
    }

    Renderer.supplementsCard = function (recommendations) {

        const blocks = buildSupplementBlocks(recommendations);

        return `
            <div class="vpx-card">
                <h2>${t("card_supplements")}</h2>
                ${blocks}
                <button type="button" class="vpx-btn-ghost" style="margin-top:14px; margin-bottom:14px;" data-vpx-nav="supplement-timing">${t("btn_viewTimingSchedule")}</button>
                <div class="vpx-supp-disclaimer">${t("supp_disclaimer")}</div>
            </div>
        `;
    };

    Renderer.viewSupplements = function (data) {

        const blocks = buildSupplementBlocks(data.supplementRecommendations);
        const hasAny = (data.supplementRecommendations || []).length > 0;

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_supplements_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_supplements_hint")}</p>
                    ${hasAny ? blocks : `<div style="font-size:12px; color:var(--vpx-muted); padding:20px 0;">${t("page_supptiming_empty")}</div>`}
                    <button type="button" class="vpx-btn-ghost" style="margin-top:14px; margin-bottom:14px;" data-vpx-nav="supplement-timing">${t("btn_viewTimingSchedule")}</button>
                    <div class="vpx-supp-disclaimer">${t("supp_disclaimer")}</div>
                </div>
                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    /*
    ==========================================
    Dashboard View — Composition
    ==========================================
    */

    /*
    ==========================================
    Consistency Streak
    ------------------------------------------
    Counts consecutive calendar days with at
    least one exercise logged (any exercise —
    this is a general consistency signal, not
    tied to a specific lift). If today has no
    entry yet, counting starts from yesterday
    instead, so the streak isn't shown as
    "broken" before the day is even over.
    ==========================================
    */

    function computeExerciseStreak() {

        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return 0;

        const all = window.VORYNEX.state.getSection("exerciseLogs") || {};
        const dateSet = {};
        Object.keys(all).forEach(function (exId) {
            (all[exId] || []).forEach(function (entry) { dateSet[entry.date] = true; });
        });

        if (!Object.keys(dateSet).length) return 0;

        const cursor = new Date();
        cursor.setHours(0, 0, 0, 0);

        const todayStr = cursor.toISOString().slice(0, 10);
        if (!dateSet[todayStr]) {
            cursor.setDate(cursor.getDate() - 1);
        }

        let streak = 0;
        while (dateSet[cursor.toISOString().slice(0, 10)]) {
            streak++;
            cursor.setDate(cursor.getDate() - 1);
        }

        return streak;
    }

    /*
    ==========================================
    Avatar Hero Widget (Dashboard)
    ------------------------------------------
    Small avatar preview + body-stage progress
    bar + Customize/Share buttons. Renders
    nothing (returns "") if vpx-avatar.js failed
    to load, so a missing module never breaks
    the Dashboard.
    ==========================================
    */

    function buildAvatarHeroWidget() {

        if (!window.VORYNEX.avatar || !window.VORYNEX.avatar.render) return "";

        const svg = window.VORYNEX.avatar.render();
        const stageInfo = window.VORYNEX.avatar.getBodyStage();
        const stagePct = Math.round((stageInfo.stage / 5) * 100);

        return `
            <div class="vpx-grid">
                <div class="vpx-card vpx-avatar-hero-card">
                    <div class="vpx-avatar-hero-preview">${svg}</div>
                    <div class="vpx-avatar-hero-info">
                        <div class="vpx-avatar-hero-title">${t("avatar_hero_title")}</div>
                        <div class="vpx-avatar-hero-stage">${t("avatar_hero_stage", { stage: stageInfo.stage })}</div>
                        <div class="vpx-avatar-hero-bar"><div class="vpx-avatar-hero-bar-fill" style="width:${stagePct}%;"></div></div>
                        <div class="vpx-avatar-hero-actions">
                            <button type="button" class="vpx-btn-gold vpx-avatar-hero-btn" data-vpx-nav="avatar-customize">${t("avatar_hero_customize")}</button>
                            <button type="button" class="vpx-btn-outline vpx-avatar-hero-btn" data-vpx-action="share-achievement">${t("avatar_hero_share")}</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    Renderer.viewDashboard = function (data) {

        const floorNote = (data.meta && data.meta.calorieFloorApplied)
            ? `<div class="vpx-onb-info-box">${t("dashboard_calorie_floor_note", { bmr: data.meta.bmr })}</div>`
            : "";

        // Calorie cycling: the headline stat cards show TODAY's target
        // (training vs rest day), not the flat weekly average — so the
        // numbers on screen always match today's meal plan below them.
        // Pre-v38 blueprints don't carry per-day targets → flat fallback.
        const hasCycling = data.targets.trainingDay && data.targets.restDay
            && data.targets.trainingDay.calories !== data.targets.restDay.calories;

        const todayIsTraining = isTodayTrainingDay();
        const todayTargets = hasCycling
            ? Object.assign({}, todayIsTraining ? data.targets.trainingDay : data.targets.restDay, { water: data.targets.water })
            : data.targets;

        const cyclingLine = hasCycling ? `
            <div class="vpx-onb-info-box" style="margin-bottom:20px;">
                ${t(todayIsTraining ? "dashboard_cycling_today_training" : "dashboard_cycling_today_rest")}
                ${t("dashboard_cycling_split", { train: data.targets.trainingDay.calories, rest: data.targets.restDay.calories })}
            </div>
        ` : "";

        // Which weekday today's plan belongs to — shown right above the
        // meal preview so it's obvious the plan auto-follows the calendar.
        const todayDayEntry = WEEK_VIEW_DAYS.filter(function (d) { return d.jsDay === new Date().getDay(); })[0];
        const todayPlanLabel = `
            <div style="font-size:12px; color:var(--vpx-gold); font-weight:600; letter-spacing:1px; text-transform:uppercase; margin-bottom:10px;">
                📅 ${t("dashboard_meal_today_label", { day: t(todayDayEntry.key) })}
            </div>
        `;

        const currentStreak = computeExerciseStreak();
        const streakBadge = currentStreak >= 1 ? `
            <div class="vpx-grid">
                <div class="vpx-streak-badge">
                    🔥 <strong>${currentStreak}</strong> ${currentStreak === 1 ? t("streak_day_singular") : t("streak_day_plural")}
                </div>
            </div>
        ` : "";

        const quickActions = `
            <div class="vpx-grid">
                <div class="vpx-card vpx-quick-actions-card">
                    <div class="vpx-quick-actions-row">
                        <button type="button" class="vpx-quick-action-btn" data-vpx-nav="grocery-list">
                            ${Date.now() < NEW_BADGE_EXPIRY ? '<span class="vpx-qa-new-badge">NEW</span>' : ""}
                            <span class="vpx-quick-action-icon">🛒</span>
                            <span>${t("dashboard_qa_grocery")}</span>
                        </button>
                        <button type="button" class="vpx-quick-action-btn" data-vpx-nav="schedule">
                            <span class="vpx-quick-action-icon">📅</span>
                            <span>${t("dashboard_qa_schedule")}</span>
                        </button>
                        <button type="button" class="vpx-quick-action-btn" data-vpx-nav="timeline">
                            <span class="vpx-quick-action-icon">📈</span>
                            <span>${t("dashboard_qa_timeline")}</span>
                        </button>
                        <button type="button" class="vpx-quick-action-btn" data-vpx-nav="reports">
                            <span class="vpx-quick-action-icon">📄</span>
                            <span>${t("dashboard_qa_reports")}</span>
                        </button>
                        <button type="button" class="vpx-quick-action-btn" data-vpx-nav="goals">
                            <span class="vpx-quick-action-icon">🎯</span>
                            <span>${t("dashboard_qa_goals")}</span>
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Larger hero version of the same donut used on desktop — same
        // component, same data, just featured bigger as the mobile
        // "Today" glanceable moment. Deliberately NOT a "calories
        // remaining" ring (that would need food-logging data this app
        // doesn't collect) — it's the plan's macro composition, which
        // is what we actually know and can show honestly.
        const heroRing = `
            <div class="vpx-hero-ring-wrap">
                ${Renderer.svgDonut(data.calorieBreakdown, 210, 22, todayTargets.calories, "kcal")}
            </div>
        `;

        return `
            ${Renderer.accountSyncBanner()}
            ${Renderer.leadCaptureBanner()}
            ${floorNote}

            <div class="vpx-mtab-bar">
                <button type="button" class="vpx-mtab-btn${dashboardMobileTab === "today" ? " is-active" : ""}" data-mtab="today">🏠 ${t("mtab_today")}</button>
                <button type="button" class="vpx-mtab-btn${dashboardMobileTab === "nutrition" ? " is-active" : ""}" data-mtab="nutrition">🍽️ ${t("mtab_nutrition")}</button>
                <button type="button" class="vpx-mtab-btn${dashboardMobileTab === "training" ? " is-active" : ""}" data-mtab="training">💪 ${t("mtab_training")}</button>
                <button type="button" class="vpx-mtab-btn${dashboardMobileTab === "progress" ? " is-active" : ""}" data-mtab="progress">📈 ${t("mtab_progress")}</button>
            </div>

            <div class="vpx-mtab-shell" data-active-mtab="${dashboardMobileTab}">

                <div class="vpx-mtab-group" data-mtab-group="today">
                    ${streakBadge}
                    <div class="vpx-mtab-hero-only">${heroRing}</div>
                    ${Renderer.statCards(todayTargets)}
                    ${cyclingLine}
                    ${quickActions}
                </div>

                <div class="vpx-mtab-group" data-mtab-group="nutrition">
                    <div class="vpx-grid">
                        <div>
                            ${todayPlanLabel}
                            ${Renderer.mealPreviewCard(data.mealPlan)}
                        </div>
                    </div>
                    <div class="vpx-grid vpx-grid-3">
                        ${Renderer.calorieBreakdownCard(data)}
                        ${Renderer.macroDistributionCard(data)}
                        ${Renderer.supplementsCard(data.supplementRecommendations)}
                    </div>
                </div>

                <div class="vpx-mtab-group" data-mtab-group="training">
                    <div class="vpx-grid">
                        ${Renderer.workoutTeaserCard(data.workoutPlan)}
                    </div>
                </div>

                <div class="vpx-mtab-group" data-mtab-group="progress">
                    <div class="vpx-grid vpx-grid-3b">
                        ${Renderer.performanceScoresCard(data.scores)}
                        ${Renderer.goalProgressCard(data.goal, data.bodyFatPct, data.meta ? data.meta.tdee : null, data.targets.calories)}
                        ${Renderer.weeklyProgressCard(data.weeklyProgress)}
                    </div>
                </div>

            </div>
        `;
    };

    function bindDashboardMobileTabs() {
        const shell = document.querySelector(".vpx-mtab-shell");
        if (!shell) return;

        document.querySelectorAll(".vpx-mtab-btn").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const target = btn.getAttribute("data-mtab");
                dashboardMobileTab = target;

                // Deliberately NOT calling Renderer.render() here — a
                // full rebuild for a pure visibility toggle would be
                // wasted work and can flicker. Direct DOM update instead:
                // instant, no re-render, no lost scroll position.
                shell.setAttribute("data-active-mtab", target);

                document.querySelectorAll(".vpx-mtab-btn").forEach(function (b) {
                    b.classList.toggle("is-active", b === btn);
                });

                window.scrollTo({ top: 0, behavior: "auto" });
            });
        });
    }

    /*
    ==========================================
    Hero / Landing
    ------------------------------------------
    Shown once, before the onboarding wizard.
    Product marquee and blurred background use
    REAL products pulled live from the store's
    5 collections (window.VPX_COLLECTIONS) —
    the same data source used for supplement
    recommendations. Falls back gracefully to a
    plain hero (no marquee/photos) if that data
    isn't available.
    ==========================================
    */

    function collectShowcaseProducts() {
        if (!window.VPX_COLLECTIONS) return [];
        const picks = [];
        Object.keys(window.VPX_COLLECTIONS).forEach(function (key) {
            (window.VPX_COLLECTIONS[key] || []).slice(0, 2).forEach(function (p) {
                if (p && p.image) picks.push(p);
            });
        });
        return picks;
    }

    Renderer.viewHero = function () {

        const products = collectShowcaseProducts();

        const marqueeCardsHtml = products.map(function (p) {
            return `
                <div class="vpx-hero-prod-card">
                    <div class="vpx-hero-prod-photo"><img src="${p.image}" alt="${p.title}" /></div>
                    <div><strong>${p.title}</strong><span>${p.price}${p.unit ? ` <small class="vpx-unit-price">(${p.unit})</small>` : ""}</span></div>
                </div>
            `;
        }).join("");

        const marqueeHtml = products.length
            ? `
                <div class="vpx-hero-marquee-row">
                    <div class="vpx-hero-marquee-track">${marqueeCardsHtml}${marqueeCardsHtml}</div>
                </div>
            `
            : "";

        const marqueeHtmlReverse = products.length
            ? `
                <div class="vpx-hero-marquee-row reverse">
                    <div class="vpx-hero-marquee-track">${marqueeCardsHtml}${marqueeCardsHtml}</div>
                </div>
            `
            : "";

        const blurPhotosHtml = products.slice(0, 4).map(function (p, i) {
            return `<div class="vpx-hero-blur-photo p${i + 1}" style="background-image:url('${p.image}');"></div>`;
        }).join("");

        return `
            <div class="vpx-hero">

                <div class="vpx-hero-blur-layer">${blurPhotosHtml}</div>
                <div class="vpx-hero-darken"></div>

                ${marqueeHtml}

                <div class="vpx-hero-glow-orb"></div>

                <div class="vpx-hero-center">

                    <div class="vpx-onb-top-row">
                        <div class="vpx-onb-logo"><span class="vpx-mark">◆</span> VORYNEX</div>
                        ${Renderer.langSwitcher()}
                    </div>

                    <h1 class="vpx-hero-title">${t("hero_title_html")}</h1>

                    <p class="vpx-hero-subhead">${t("hero_subhead")}</p>

                    <button type="button" class="vpx-hero-cta" data-hero-action="start">${t("hero_cta")}</button>

                    <div class="vpx-hero-badges">
                        <div class="vpx-hero-badge"><span class="vpx-hero-dot"></span> ${t("hero_badge_bmr")}</div>
                        <div class="vpx-hero-badge"><span class="vpx-hero-dot"></span> ${t("hero_badge_meals")}</div>
                        <div class="vpx-hero-badge"><span class="vpx-hero-dot"></span> ${t("hero_badge_supplements")}</div>
                        <div class="vpx-hero-badge"><span class="vpx-hero-dot"></span> EL / EN / DE</div>
                    </div>

                    <div class="vpx-hero-whatsinside">
                        <div class="vpx-hero-whatsinside-title">${t("hero_whatsinside_title")}</div>
                        <div class="vpx-hero-whatsinside-grid">
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_1")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_2")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_3")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_4")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_5")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_6")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_7")}</div>
                            <div class="vpx-hero-whatsinside-item">✓ ${t("hero_wi_8")}</div>
                        </div>
                    </div>

                </div>

                ${marqueeHtmlReverse}

            </div>
        `;
    };

    function bindHeroEvents() {

        const app = document.getElementById("vpx-app");
        if (!app) return;

        bindLangSwitcherEvents(function () {
            Renderer.renderHero();
        });

        const startBtn = app.querySelector('[data-hero-action="start"]');
        if (startBtn) {
            startBtn.addEventListener("click", function () {
                heroDismissed = true;
                trackEvent("vpx_quiz_start", {});
                Renderer.render();
            });
        }
    }

    Renderer.renderHero = function () {
        const app = document.getElementById("vpx-app");
        if (!app) return;
        withViewTransition(function () {
            app.innerHTML = Renderer.viewHero();
            bindHeroEvents();
        });
    };

    /*
    ==========================================
    Onboarding — Intake Wizard
    ==========================================
    */

    const ONBOARDING_STEPS = [
        { id: "gender", titleKey: "onb_step_gender" },
        { id: "basics", titleKey: "onb_step_basics" },
        { id: "bodyfat", titleKey: "onb_step_bodyfat" },
        { id: "measurements", titleKey: "onb_step_measurements" },
        { id: "lifestyle", titleKey: "onb_step_lifestyle" },
        { id: "training", titleKey: "onb_step_training" },
        { id: "stress", titleKey: "onb_step_stress" },
        { id: "goal", titleKey: "onb_step_goal" },
        { id: "pace", titleKey: "onb_step_pace", skipIf: function (a) { return a.goal === "maintenance"; } },
        { id: "review", titleKey: "onb_step_review" }
    ];

    const FIELD_LIMITS = {
        age: { min: 14, max: 100, hintKey: "onb_field_age_hint" },
        heightCm: { min: 100, max: 250, hintKey: "onb_field_height_hint" },
        weightKg: { min: 30, max: 300, hintKey: "onb_field_weight_hint" },
        trainingDaysPerWeek: { min: 0, max: 7, hintKey: "onb_field_trainingDays_hint" },
        sleepHours: { min: 3, max: 14, hintKey: "onb_field_sleepHours_hint" },
        neckCm: { min: 20, max: 60 },
        waistCm: { min: 50, max: 200 },
        hipCm: { min: 50, max: 200 }
    };

    function isFieldWithinLimits(field, value) {
        const limit = FIELD_LIMITS[field];
        if (!limit) return true;
        if (value === null || value === undefined || isNaN(value)) return false;
        return value >= limit.min && value <= limit.max;
    }

    function clampToLimits(field, value) {
        const limit = FIELD_LIMITS[field];
        if (!limit) return value;
        return Math.min(limit.max, Math.max(limit.min, value));
    }

    let heroDismissed = false;
    let currentView = "dashboard";

    // Draft avatar config while the customization screen is open — only
    // written to real state (and cloud-synced) when the person taps
    // Save, so browsing options never persists half-finished choices.
    let avatarDraft = null;

    // Tracks the view actually shown on the LAST render, so Renderer.render()
    // can tell "the page changed" apart from "the same page just re-rendered
    // itself" (checkbox toggles, banners, etc.) — see the scroll-to-top logic.
    let lastRenderedView = null;

    let onboardingState = {
        step: 0,
        answers: {
            gender: null,
            age: null,
            heightCm: null,
            weightKg: null,
            bodyFatPct: null,
            neckCm: null,
            waistCm: null,
            hipCm: null,
            jobType: null,
            trainingDaysPerWeek: null,
            trainingDays: [],
            sleepHours: null,
            stressLevel: null,
            goal: null,
            paceLevel: null,
            programWeeks: null
        }
    };

    function onboardingProgressBar() {
        const pct = Math.round(((onboardingState.step) / (ONBOARDING_STEPS.length - 1)) * 100);
        return `
            <div class="vpx-onb-progress-track">
                <div class="vpx-onb-progress-fill" style="width:${pct}%;"></div>
            </div>
            <div class="vpx-onb-progress-label">${t("onb_stepOf", { current: onboardingState.step + 1, total: ONBOARDING_STEPS.length })}</div>
        `;
    }

    function onboardingOptionGroup(name, options, selected) {
        return options.map(function (opt) {
            const activeClass = selected === opt.value ? " is-active" : "";
            return `
                <button type="button" class="vpx-onb-option${activeClass}" data-onb-field="${name}" data-onb-value="${opt.value}">
                    ${opt.icon ? `<span class="vpx-onb-option-icon">${opt.icon}</span>` : ""}
                    <span>${opt.label}</span>
                </button>
            `;
        }).join("");
    }

    function onboardingStepContent() {

        const a = onboardingState.answers;
        const stepId = ONBOARDING_STEPS[onboardingState.step].id;

        if (stepId === "gender") {
            return `
                <div class="vpx-onb-options vpx-onb-options-2">
                    ${onboardingOptionGroup("gender", [
                        { value: "male", label: t("onb_gender_male"), icon: "♂" },
                        { value: "female", label: t("onb_gender_female"), icon: "♀" }
                    ], a.gender)}
                </div>
            `;
        }

        if (stepId === "basics") {
            return `
                <div class="vpx-onb-form-grid">
                    <label class="vpx-onb-field">
                        <span>${t("onb_field_age")}</span>
                        <input type="number" data-onb-input="age"
                            min="${FIELD_LIMITS.age.min}" max="${FIELD_LIMITS.age.max}"
                            value="${a.age || ""}" placeholder="28" />
                        <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.age.hintKey)}</small>
                    </label>
                    <label class="vpx-onb-field">
                        <span>${t("onb_field_height")}</span>
                        <input type="number" data-onb-input="heightCm"
                            min="${FIELD_LIMITS.heightCm.min}" max="${FIELD_LIMITS.heightCm.max}"
                            value="${a.heightCm || ""}" placeholder="178" />
                        <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.heightCm.hintKey)}</small>
                    </label>
                    <label class="vpx-onb-field">
                        <span>${t("onb_field_weight")}</span>
                        <input type="number" data-onb-input="weightKg"
                            min="${FIELD_LIMITS.weightKg.min}" max="${FIELD_LIMITS.weightKg.max}"
                            value="${a.weightKg || ""}" placeholder="82.5" />
                        <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.weightKg.hintKey)}</small>
                    </label>
                </div>
            `;
        }

        if (stepId === "bodyfat") {
            const val = a.bodyFatPct !== null ? a.bodyFatPct : 20;
            const sliderPct = ((val - 5) / (50 - 5)) * 100;
            return `
                <div class="vpx-onb-slider-wrap">
                    <div class="vpx-onb-slider-value" id="vpx-onb-bodyfat-value">${a.bodyFatPct !== null ? a.bodyFatPct + "%" : t("onb_bodyfat_unknown")}</div>
                    <input type="range" min="5" max="50" step="0.5"
                        id="vpx-onb-bodyfat-slider"
                        data-onb-input="bodyFatPct"
                        value="${val}"
                        style="background: linear-gradient(to right, var(--vpx-gold) ${sliderPct}%, var(--vpx-surface-2) ${sliderPct}%);" />
                    <div class="vpx-onb-slider-hint">${t("onb_bodyfat_hint")}</div>
                    <button type="button" class="vpx-btn-ghost" data-onb-action="skip-bodyfat">${t("onb_bodyfat_skip")}</button>
                </div>
            `;
        }

        if (stepId === "measurements") {
            const showHip = a.gender === "female";
            return `
                <div class="vpx-onb-form-grid">
                    <label class="vpx-onb-field">
                        <span>${t("onb_measurements_neck")}</span>
                        <input type="number" step="0.5" data-onb-input="neckCm"
                            min="${FIELD_LIMITS.neckCm.min}" max="${FIELD_LIMITS.neckCm.max}"
                            value="${a.neckCm || ""}" placeholder="38" />
                    </label>
                    <label class="vpx-onb-field">
                        <span>${t("onb_measurements_waist")}</span>
                        <input type="number" step="0.5" data-onb-input="waistCm"
                            min="${FIELD_LIMITS.waistCm.min}" max="${FIELD_LIMITS.waistCm.max}"
                            value="${a.waistCm || ""}" placeholder="85" />
                    </label>
                    ${showHip ? `
                    <label class="vpx-onb-field">
                        <span>${t("onb_measurements_hip")}</span>
                        <input type="number" step="0.5" data-onb-input="hipCm"
                            min="${FIELD_LIMITS.hipCm.min}" max="${FIELD_LIMITS.hipCm.max}"
                            value="${a.hipCm || ""}" placeholder="95" />
                    </label>
                    ` : ""}
                </div>
                <div class="vpx-onb-slider-hint" style="margin-top:14px;">${t("onb_measurements_hint")}</div>
                <button type="button" class="vpx-btn-ghost" style="margin-top:10px;" data-onb-action="skip-measurements">${t("onb_measurements_skip")}</button>
            `;
        }

        if (stepId === "lifestyle") {
            return `
                <div class="vpx-onb-options vpx-onb-options-col">
                    ${onboardingOptionGroup("jobType", [
                        { value: "sedentary", label: t("onb_job_sedentary"), icon: "🪑" },
                        { value: "standing", label: t("onb_job_standing"), icon: "🚶" },
                        { value: "active", label: t("onb_job_active"), icon: "🏗️" }
                    ], a.jobType)}
                </div>
            `;
        }

        if (stepId === "training") {

            const WEEKDAY_PICKS = [
                { jsDay: 1, key: "day_mon" }, { jsDay: 2, key: "day_tue" }, { jsDay: 3, key: "day_wed" },
                { jsDay: 4, key: "day_thu" }, { jsDay: 5, key: "day_fri" }, { jsDay: 6, key: "day_sat" },
                { jsDay: 0, key: "day_sun" }
            ];
            const selectedDays = a.trainingDays || [];

            const dayButtons = WEEKDAY_PICKS.map(function (d) {
                const activeClass = selectedDays.indexOf(d.jsDay) !== -1 ? " is-active" : "";
                return `<button type="button" class="vpx-onb-daypick${activeClass}" data-onb-day="${d.jsDay}">${t(d.key)}</button>`;
            }).join("");

            return `
                <div class="vpx-onb-info-box">
                    ${t("onb_training_impact_explainer")}
                </div>

                <div class="vpx-onb-form-grid">
                    <label class="vpx-onb-field">
                        <span>${t("onb_field_sleepHours")}</span>
                        <input type="number" step="0.5" data-onb-input="sleepHours"
                            min="${FIELD_LIMITS.sleepHours.min}" max="${FIELD_LIMITS.sleepHours.max}"
                            value="${a.sleepHours || ""}" placeholder="7" />
                        <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.sleepHours.hintKey)}</small>
                    </label>
                </div>

                <div style="margin-top:20px;">
                    <div style="font-size:12px; color:var(--vpx-white); margin-bottom:4px;">${t("onb_field_trainingDaysPicker")}</div>
                    <div class="vpx-onb-slider-hint" style="margin-bottom:4px;">${t("onb_field_trainingDaysPicker_hint")}</div>
                    <div class="vpx-onb-daycount" data-onb-daycount>${t("onb_training_days_selected_count", { count: selectedDays.length })}</div>
                    <div class="vpx-onb-daypick-row" style="margin-top:10px;">${dayButtons}</div>
                </div>
            `;
        }

        if (stepId === "stress") {
            return `
                <div class="vpx-onb-options vpx-onb-options-row5">
                    ${onboardingOptionGroup("stressLevel", [
                        { value: "1", label: t("onb_stress_1") },
                        { value: "2", label: t("onb_stress_2") },
                        { value: "3", label: t("onb_stress_3") },
                        { value: "4", label: t("onb_stress_4") },
                        { value: "5", label: t("onb_stress_5") }
                    ], a.stressLevel)}
                </div>
            `;
        }

        if (stepId === "pace") {

            const isGain = a.goal === "muscle_gain";

            const paceOptions = isGain
                ? [
                    { value: "slow", label: t("onb_pace_slow_gain"), icon: "🐢" },
                    { value: "moderate", label: t("onb_pace_moderate_gain"), icon: "🚶" },
                    { value: "aggressive", label: t("onb_pace_aggressive_gain"), icon: "🏃" }
                ]
                : [
                    { value: "slow", label: t("onb_pace_slow_loss"), icon: "🐢" },
                    { value: "moderate", label: t("onb_pace_moderate_loss"), icon: "🚶" },
                    { value: "aggressive", label: t("onb_pace_aggressive_loss"), icon: "🏃" }
                ];

            const durationOptions = [
                { value: "4", label: t("onb_duration_4") },
                { value: "8", label: t("onb_duration_8") },
                { value: "12", label: t("onb_duration_12") },
                { value: "24", label: t("onb_duration_24") }
            ];

            return `
                <div class="vpx-onb-options vpx-onb-options-col">
                    ${onboardingOptionGroup("paceLevel", paceOptions, a.paceLevel)}
                </div>
                <div class="vpx-onb-slider-hint" style="margin-top:14px;">${t("onb_pace_hint")}</div>

                <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase; letter-spacing:1px; margin-top:22px; margin-bottom:10px;">
                    ${t("onb_duration_label")}
                </div>
                <div class="vpx-onb-options vpx-onb-options-row5">
                    ${onboardingOptionGroup("programWeeks", durationOptions, a.programWeeks)}
                </div>
            `;
        }

        if (stepId === "goal") {
            return `
                <div class="vpx-onb-options vpx-onb-options-col">
                    ${onboardingOptionGroup("goal", [
                        { value: "fat_loss", label: t("onb_goal_fatLoss"), icon: "🔥" },
                        { value: "maintenance", label: t("onb_goal_maintenance"), icon: "⚖️" },
                        { value: "muscle_gain", label: t("onb_goal_muscleGain"), icon: "💪" }
                    ], a.goal)}
                </div>
            `;
        }

        if (stepId === "review") {

            const measurementsText = (a.neckCm && a.waistCm)
                ? `${a.neckCm}/${a.waistCm}${a.hipCm ? "/" + a.hipCm : ""} cm`
                : t("onb_review_notGiven");

            return `
                <div class="vpx-onb-review">
                    <div class="vpx-onb-review-row"><span>${t("onb_review_gender")}</span><strong>${a.gender === "male" ? t("onb_gender_male") : a.gender === "female" ? t("onb_gender_female") : "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_age")}</span><strong>${a.age || "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_height")}</span><strong>${a.heightCm || "—"} cm</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_weight")}</span><strong>${a.weightKg ? formatWeightKg(a.weightKg) : "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_bodyFat")}</span><strong>${a.bodyFatPct !== null ? a.bodyFatPct + "%" : t("onb_review_notGiven")}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_measurements")}</span><strong>${measurementsText}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_jobType")}</span><strong>${a.jobType ? t("onb_job_" + a.jobType) : "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_trainingPerWeek")}</span><strong>${(a.trainingDays || []).length}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_sleep")}</span><strong>${a.sleepHours || "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_stress")}</span><strong>${a.stressLevel ? t("onb_stress_" + a.stressLevel) : "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_goal")}</span><strong>${a.goal === "fat_loss" ? t("onb_goal_fatLoss") : a.goal === "maintenance" ? t("onb_goal_maintenance") : a.goal === "muscle_gain" ? t("onb_goal_muscleGain") : "—"}</strong></div>
                    ${a.goal !== "maintenance" ? `
                    <div class="vpx-onb-review-row"><span>${t("onb_review_pace")}</span><strong>${a.paceLevel ? t("onb_pace_" + a.paceLevel + (a.goal === "muscle_gain" ? "_gain" : "_loss")) : "—"}</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("onb_review_duration")}</span><strong>${a.programWeeks ? a.programWeeks + " " + t("goal_weeks") : "—"}</strong></div>
                    ` : ""}
                </div>
            `;
        }

        return "";
    }

    function onboardingValidateCurrentStep() {
        const stepId = ONBOARDING_STEPS[onboardingState.step].id;
        const a = onboardingState.answers;

        if (stepId === "gender") return !!a.gender;

        if (stepId === "basics") {
            return isFieldWithinLimits("age", a.age)
                && isFieldWithinLimits("heightCm", a.heightCm)
                && isFieldWithinLimits("weightKg", a.weightKg);
        }

        if (stepId === "bodyfat") return true; // πάντα προαιρετικό

        if (stepId === "measurements") {
            if (a.neckCm !== null && !isFieldWithinLimits("neckCm", a.neckCm)) return false;
            if (a.waistCm !== null && !isFieldWithinLimits("waistCm", a.waistCm)) return false;
            if (a.hipCm !== null && !isFieldWithinLimits("hipCm", a.hipCm)) return false;
            return true;
        }

        if (stepId === "lifestyle") return !!a.jobType;

        if (stepId === "training") {
            return isFieldWithinLimits("sleepHours", a.sleepHours);
        }

        if (stepId === "stress") return !!a.stressLevel;
        if (stepId === "goal") return !!a.goal;

        if (stepId === "pace") {
            if (a.goal === "maintenance") return true;
            return !!a.paceLevel && !!a.programWeeks;
        }

        return true;
    }

    Renderer.viewOnboarding = function () {

        const isLast = onboardingState.step === ONBOARDING_STEPS.length - 1;
        const isFirst = onboardingState.step === 0;
        const canContinue = onboardingValidateCurrentStep();

        return `
            <div class="vpx-onb-shell">
                <div class="vpx-onb-card vpx-onb-step-anim">

                    <div class="vpx-onb-top-row">
                        <div class="vpx-onb-logo">
                            <span class="vpx-mark">◆</span> VORYNEX
                        </div>
                        ${Renderer.langSwitcher()}
                    </div>

                    ${onboardingProgressBar()}

                    <h2 class="vpx-onb-title">${t(ONBOARDING_STEPS[onboardingState.step].titleKey)}</h2>

                    <div class="vpx-onb-body">
                        ${onboardingStepContent()}
                    </div>

                    <div class="vpx-onb-actions">
                        ${!isFirst ? `<button type="button" class="vpx-btn-ghost" data-onb-action="back">${t("onb_back")}</button>` : `<span></span>`}
                        <button type="button" class="vpx-btn-gold" data-onb-action="${isLast ? "submit" : "next"}" ${canContinue ? "" : "disabled"}>
                            ${isLast ? t("onb_submit") : t("onb_continue")}
                        </button>
                    </div>

                </div>
            </div>
        `;
    };

    function bindOnboardingEvents() {

        const app = document.getElementById("vpx-app");
        if (!app) return;

        bindLangSwitcherEvents(function () {
            Renderer.renderOnboarding();
        });

        app.querySelectorAll("[data-onb-field]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const field = btn.getAttribute("data-onb-field");
                const value = btn.getAttribute("data-onb-value");
                onboardingState.answers[field] = value;

                // Update only the affected button group's active state —
                // no full re-render, so the box itself doesn't re-animate
                // every time an option is clicked (only on step change).
                const group = btn.parentElement;
                if (group) {
                    group.querySelectorAll("[data-onb-field]").forEach(function (sibling) {
                        sibling.classList.remove("is-active");
                    });
                }
                btn.classList.add("is-active");

                // Quick press feedback on just this button
                btn.classList.remove("vpx-onb-option-press");
                void btn.offsetWidth; // restart animation
                btn.classList.add("vpx-onb-option-press");

                const continueBtn = app.querySelector('[data-onb-action="next"], [data-onb-action="submit"]');
                if (continueBtn) {
                    continueBtn.disabled = !onboardingValidateCurrentStep();
                }
            });
        });

        app.querySelectorAll("[data-onb-day]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const day = Number(btn.getAttribute("data-onb-day"));
                const days = onboardingState.answers.trainingDays || [];
                const idx = days.indexOf(day);
                if (idx === -1) {
                    days.push(day);
                } else {
                    days.splice(idx, 1);
                }
                onboardingState.answers.trainingDays = days;
                // Kept in sync for any legacy reads of this field —
                // the weekday picker above is the actual source of truth.
                onboardingState.answers.trainingDaysPerWeek = days.length;
                btn.classList.toggle("is-active");

                const counterEl = app.querySelector("[data-onb-daycount]");
                if (counterEl) {
                    counterEl.textContent = t("onb_training_days_selected_count", { count: days.length });
                }
            });
        });

        const bodyfatSlider = app.querySelector("#vpx-onb-bodyfat-slider");
        if (bodyfatSlider) {
            bodyfatSlider.addEventListener("input", function () {
                const value = Number(bodyfatSlider.value);
                onboardingState.answers.bodyFatPct = value;

                const sliderPct = ((value - 5) / (50 - 5)) * 100;
                bodyfatSlider.style.background =
                    `linear-gradient(to right, var(--vpx-gold) ${sliderPct}%, var(--vpx-surface-2) ${sliderPct}%)`;

                const valueLabel = document.getElementById("vpx-onb-bodyfat-value");
                if (valueLabel) valueLabel.textContent = value + "%";

                const continueBtn = app.querySelector('[data-onb-action="next"], [data-onb-action="submit"]');
                if (continueBtn) continueBtn.disabled = !onboardingValidateCurrentStep();
            });
        }

        const skipBodyfatBtn = app.querySelector('[data-onb-action="skip-bodyfat"]');
        if (skipBodyfatBtn) {
            skipBodyfatBtn.addEventListener("click", function () {
                onboardingState.answers.bodyFatPct = null;
                onboardingState.step = onboardingState.step + 1;
                Renderer.renderOnboarding();
            });
        }

        const skipMeasurementsBtn = app.querySelector('[data-onb-action="skip-measurements"]');
        if (skipMeasurementsBtn) {
            skipMeasurementsBtn.addEventListener("click", function () {
                onboardingState.answers.neckCm = null;
                onboardingState.answers.waistCm = null;
                onboardingState.answers.hipCm = null;
                onboardingState.step = onboardingState.step + 1;
                Renderer.renderOnboarding();
            });
        }

        app.querySelectorAll('input[type="number"][data-onb-input]').forEach(function (input) {

            input.addEventListener("input", function () {
                const field = input.getAttribute("data-onb-input");
                onboardingState.answers[field] = input.value === "" ? null : Number(input.value);

                const valid = input.value === "" || isFieldWithinLimits(field, Number(input.value));
                input.classList.toggle("vpx-onb-field-invalid", !valid);

                const continueBtn = app.querySelector('[data-onb-action="next"], [data-onb-action="submit"]');
                if (continueBtn) {
                    continueBtn.disabled = !onboardingValidateCurrentStep();
                }
            });

            input.addEventListener("blur", function () {
                const field = input.getAttribute("data-onb-input");
                if (input.value === "") return;

                const clamped = clampToLimits(field, Number(input.value));
                input.value = clamped;
                onboardingState.answers[field] = clamped;
                input.classList.remove("vpx-onb-field-invalid");

                const continueBtn = app.querySelector('[data-onb-action="next"], [data-onb-action="submit"]');
                if (continueBtn) {
                    continueBtn.disabled = !onboardingValidateCurrentStep();
                }
            });
        });

        const backBtn = app.querySelector('[data-onb-action="back"]');
        if (backBtn) {
            backBtn.addEventListener("click", function () {
                let prevStep = onboardingState.step - 1;
                while (prevStep > 0 && ONBOARDING_STEPS[prevStep].skipIf && ONBOARDING_STEPS[prevStep].skipIf(onboardingState.answers)) {
                    prevStep--;
                }
                onboardingState.step = Math.max(0, prevStep);
                Renderer.renderOnboarding();
            });
        }

        const nextBtn = app.querySelector('[data-onb-action="next"]');
        if (nextBtn) {
            nextBtn.addEventListener("click", function () {
                if (!onboardingValidateCurrentStep()) return;
                const fromStep = ONBOARDING_STEPS[onboardingState.step];
                let nextStep = onboardingState.step + 1;
                while (nextStep < ONBOARDING_STEPS.length - 1 && ONBOARDING_STEPS[nextStep].skipIf && ONBOARDING_STEPS[nextStep].skipIf(onboardingState.answers)) {
                    nextStep++;
                }
                onboardingState.step = Math.min(ONBOARDING_STEPS.length - 1, nextStep);
                trackEvent("vpx_quiz_step_next", {
                    from_step_id: fromStep ? fromStep.id : "",
                    from_step_number: onboardingState.step // value before this click's step_view will confirm the new one
                });
                Renderer.renderOnboarding();
            });
        }

        const submitBtn = app.querySelector('[data-onb-action="submit"]');
        if (submitBtn) {
            submitBtn.addEventListener("click", function () {

                const profile = onboardingState.answers;

                if (!window.VORYNEX.engine || !window.VORYNEX.engine.calculate) {
                    console.error("Engine not loaded — cannot calculate blueprint.");
                    return;
                }

                const result = window.VORYNEX.engine.calculate(profile);

                if (window.VORYNEX.state && window.VORYNEX.state.setSection) {
                    window.VORYNEX.state.setSection("blueprint", result);
                    window.VORYNEX.state.setSection("onboarding", { completed: true, answers: profile });
                    window.VORYNEX.state.setSection("trainingSchedule", { days: profile.trainingDays || [] });
                }

                trackEvent("vpx_quiz_complete", {
                    goal: profile.goal,
                    training_days: (profile.trainingDays || []).length
                });

                currentView = "dashboard";
                Renderer.render();
            });
        }
    }

    Renderer.renderOnboarding = function () {
        const app = document.getElementById("vpx-app");
        if (!app) return;
        app.innerHTML = Renderer.viewOnboarding();
        bindOnboardingEvents();

        const step = ONBOARDING_STEPS[onboardingState.step];
        if (step) {
            trackEvent("vpx_quiz_step_view", {
                step_id: step.id,
                step_number: onboardingState.step + 1,
                step_total: ONBOARDING_STEPS.length
            });
        }
    };

    /*
    ==========================================
    Restart Quiz
    ------------------------------------------
    Clears persisted + in-memory blueprint and
    onboarding data, resets the wizard to step
    zero, and sends the user back to the Hero —
    a full, confirmed restart from scratch.
    ==========================================
    */

    function resetOnboardingState() {
        onboardingState = {
            step: 0,
            answers: {
                gender: null,
                age: null,
                heightCm: null,
                weightKg: null,
                bodyFatPct: null,
                neckCm: null,
                waistCm: null,
                hipCm: null,
                jobType: null,
                trainingDaysPerWeek: null,
                trainingDays: [],
                sleepHours: null,
                stressLevel: null,
                goal: null,
                paceLevel: null,
                programWeeks: null
            }
        };
    }

    function bindRestartQuizEvents() {
        const restartBtn = document.querySelector('[data-vpx-action="restart-quiz"]');
        if (!restartBtn) return;

        restartBtn.addEventListener("click", function () {
            const confirmed = window.confirm(t("confirm_restartQuiz"));
            if (!confirmed) return;

            if (window.VORYNEX.state && window.VORYNEX.state.reset) {
                window.VORYNEX.state.reset();
            }

            resetOnboardingState();
            heroDismissed = false;
            currentView = "dashboard";

            Renderer.render();
        });
    }

    /*
    ==========================================
    Mobile Sidebar Drawer
    ------------------------------------------
    On narrow screens the sidebar becomes an
    off-canvas drawer, opened via a hamburger
    button in the mobile top bar. Closes on
    overlay tap or when a nav item is tapped.
    ==========================================
    */

    function bindSidebarToggleEvents() {

        const sidebar = document.querySelector(".vpx-sidebar");
        const overlay = document.querySelector(".vpx-sidebar-overlay");
        const toggleBtn = document.querySelector('[data-vpx-action="toggle-sidebar"]');
        const closeBtn = document.querySelector('[data-vpx-action="close-sidebar"]');

        function openSidebar() {
            if (sidebar) sidebar.classList.add("is-open");
            if (overlay) overlay.classList.add("is-visible");
        }

        function closeSidebar() {
            if (sidebar) sidebar.classList.remove("is-open");
            if (overlay) overlay.classList.remove("is-visible");
        }

        if (toggleBtn) toggleBtn.addEventListener("click", openSidebar);
        if (closeBtn) closeBtn.addEventListener("click", closeSidebar);

        document.querySelectorAll("[data-vpx-nav]").forEach(function (item) {
            item.addEventListener("click", closeSidebar);
        });
    }

    /*
    ==========================================
    Dashboard Entrance Animations
    ------------------------------------------
    Count-up numbers, staggered card reveal,
    and animated bar/chart fill-ins — run once
    right after the dashboard DOM is inserted.
    ==========================================
    */

    function animateCountUp(el, target, decimals) {
        const duration = 1100;
        let startTime = null;

        function step(ts) {
            if (!startTime) startTime = ts;
            const progress = Math.min(1, (ts - startTime) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            const value = target * eased;
            el.textContent = decimals ? value.toFixed(decimals) : Math.round(value);
            if (progress < 1) {
                requestAnimationFrame(step);
            } else {
                el.classList.add("vpx-count-done");
            }
        }

        requestAnimationFrame(step);
    }

    function initDashboardAnimations() {

        document.querySelectorAll(".vpx-card").forEach(function (card, i) {
            card.classList.add("vpx-card-reveal");
            card.style.animationDelay = (i * 0.06) + "s";
        });

        document.querySelectorAll(".vpx-count[data-count-target]").forEach(function (el) {
            const target = parseFloat(el.getAttribute("data-count-target"));
            const decimals = parseInt(el.getAttribute("data-count-decimals") || "0", 10);
            if (!isNaN(target)) animateCountUp(el, target, decimals);
        });

        document.querySelectorAll(".vpx-chart-count[data-count-target]").forEach(function (el) {
            const target = parseFloat(el.getAttribute("data-count-target"));
            if (!isNaN(target)) animateCountUp(el, target, 0);
        });

        document.querySelectorAll("[data-fill-target]").forEach(function (bar) {
            const target = bar.getAttribute("data-fill-target");
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    bar.style.width = target;
                });
            });
        });
    }

    /*
    ==========================================
    Additional Pages — Meal Plan / Timeline / Coming Soon
    ==========================================
    */

    function buildProjectedWeightSeries(goal) {

        // Prefer the engine's adaptive week-by-week curve (accounts for
        // BMR falling with weight + metabolic adaptation). Older cached
        // blueprints (pre-v38) won't have it — fall back to linear.
        if (goal.weeklyCurve && goal.weeklyCurve.length > 1) {
            const curve = goal.weeklyCurve;
            const steps = Math.min(curve.length - 1, 12);
            const points = [];
            for (let i = 0; i <= steps; i++) {
                const idx = Math.round((i / steps) * (curve.length - 1));
                points.push({ label: "W" + curve[idx].week, value: curve[idx].weight });
            }
            return points;
        }

        const points = [];
        const weeks = goal.weeksRemaining || 0;
        if (weeks <= 0 || goal.currentWeight === goal.targetWeight) return points;

        const totalDelta = goal.targetWeight - goal.currentWeight;
        const steps = Math.min(weeks, 12);

        for (let i = 0; i <= steps; i++) {
            const weekNum = Math.round((i / steps) * weeks);
            const weight = goal.currentWeight + (totalDelta * (weekNum / weeks));
            points.push({ label: "W" + weekNum, value: Math.round(weight * 10) / 10 });
        }

        return points;
    }

    /*
    ==========================================
    Meal Plan — Week View
    ------------------------------------------
    Mon-Sun selector; each day's plan is computed
    on demand from that day's type (training/rest
    per the live Weekly Schedule) and that day's
    calorie-cycled targets. Recipe choices made
    while viewing a specific day persist ONLY for
    that day (blueprint.dailyRecipeSelections,
    keyed by jsDay) and override the global
    recipeSelections fallback — so Monday can be
    an omelette and Tuesday pancakes. Ingredient
    swaps remain available only on TODAY's plan
    (they mutate the stored plan in place; per-day
    swap persistence would need its own storage
    model — recipe-level choice covers the actual
    use case). The selected day's workout renders
    below the meals so the whole day is one view.
    ==========================================
    */

    function getDayRecipeSelections(blueprint, jsDay) {
        const global = blueprint.recipeSelections || {};
        const perDay = (blueprint.dailyRecipeSelections || {})[String(jsDay)] || {};
        return Object.assign({}, global, perDay);
    }

    /*
    ==========================================
    Grocery List Generator
    ------------------------------------------
    Rebuilds all 7 REAL days of the week (Sun-Sat)
    the exact same way the Week View does — same
    calorie-cycled targets per day type, same
    per-day recipe overrides — then sums every
    food item's grams across the whole week.
    Optional condiments use the person's saved
    global choice (condimentSelections isn't
    tracked per-day, so the same pick applies
    every day that meal slot exists — matching
    how condiments already behave elsewhere).
    Pure computation, no state writes — safe to
    recompute on every visit to the page.
    ==========================================
    */

    function buildWeeklyGroceryList(data) {

        const FOODS = window.VORYNEX.foods || {};
        if (!window.VORYNEX.mealGenerator || !Object.keys(FOODS).length) return {};

        const schedule = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("trainingSchedule") : null;
        const trainingDays = (schedule && schedule.days) ? schedule.days : [];

        const totals = {}; // foodId -> total grams across the week

        for (let jsDay = 0; jsDay <= 6; jsDay++) {

            const isTraining = trainingDays.indexOf(jsDay) !== -1;
            const dayTargets = isTraining
                ? (data.targets.trainingDay || data.targets)
                : (data.targets.restDay || data.targets);
            const selections = getDayRecipeSelections(data, jsDay);

            const plan = window.VORYNEX.mealGenerator.generate(dayTargets.calories, !isTraining, selections);
            if (window.VORYNEX.optimizer && window.VORYNEX.optimizer.optimize) {
                window.VORYNEX.optimizer.optimize(plan, {
                    protein: dayTargets.protein,
                    carbs: dayTargets.carbs,
                    fat: dayTargets.fat
                });
            }

            plan.forEach(function (meal) {

                meal.items.forEach(function (item) {
                    totals[item.foodId] = (totals[item.foodId] || 0) + item.grams;
                });

                const chosenCondiments = (data.condimentSelections && data.condimentSelections[meal.slotId]) || [];
                chosenCondiments.forEach(function (condId) {
                    const option = (meal.availableCondiments || []).filter(function (c) { return c.id === condId; })[0];
                    if (option) totals[condId] = (totals[condId] || 0) + option.grams;
                });
            });
        }

        const groups = {};
        Object.keys(totals).forEach(function (foodId) {
            const food = FOODS[foodId];
            if (!food) return;
            const cat = food.category || "other";
            groups[cat] = groups[cat] || [];
            groups[cat].push({ foodId: foodId, nameKey: food.nameKey, icon: food.icon, grams: Math.round(totals[foodId]) });
        });

        Object.keys(groups).forEach(function (cat) {
            groups[cat].sort(function (a, b) { return b.grams - a.grams; });
        });

        return groups;
    }

    function formatGroceryAmount(grams) {
        if (grams >= 1000) return (Math.round(grams / 100) / 10) + " kg";
        return grams + " g";
    }

    const GROCERY_CATEGORY_ORDER = ["protein", "carb", "vegetable", "fruit", "fat", "condiment", "other"];

    function buildGroceryGroupsHtml(groups, interactive) {
        return GROCERY_CATEGORY_ORDER
            .filter(function (cat) { return groups[cat] && groups[cat].length; })
            .map(function (cat) {
                const rows = groups[cat].map(function (item) {
                    return interactive ? `
                        <label class="vpx-grocery-item">
                            <input type="checkbox" data-grocery-check />
                            <span class="vpx-grocery-item-text">${item.icon} ${t(item.nameKey)}</span>
                            <strong>${formatGroceryAmount(item.grams)}</strong>
                        </label>
                    ` : `
                        <div class="vpx-grocery-item vpx-grocery-item-print">
                            <span class="vpx-grocery-item-text">☐ ${item.icon} ${t(item.nameKey)}</span>
                            <strong>${formatGroceryAmount(item.grams)}</strong>
                        </div>
                    `;
                }).join("");
                return `
                    <div style="margin-bottom:22px;">
                        <div style="font-size:11px; color:var(--vpx-gold); font-weight:bold; letter-spacing:1.5px; text-transform:uppercase; margin-bottom:10px; border-bottom:1px solid var(--vpx-border); padding-bottom:8px;">
                            ${t("grocery_cat_" + cat)}
                        </div>
                        <div class="vpx-grocery-list">${rows}</div>
                    </div>
                `;
            }).join("");
    }

    Renderer.viewGroceryList = function (data) {

        const groups = buildWeeklyGroceryList(data);
        const hasItems = Object.keys(groups).length > 0;

        trackEvent("vpx_grocery_viewed", { item_count: Object.keys(groups).reduce(function (n, k) { return n + groups[k].length; }, 0) });

        const content = hasItems
            ? buildGroceryGroupsHtml(groups, true)
            : `<div style="font-size:12px; color:var(--vpx-muted); padding:20px 0;">${t("page_grocery_empty")}</div>`;

        return `
            <div class="vpx-grid">
                <div class="vpx-card vpx-no-print">
                    <h2>${t("page_grocery_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:16px;">${t("page_grocery_hint")}</p>
                    <button type="button" class="vpx-btn-gold" data-lead-action="print-grocery">${t("page_grocery_print_btn")}</button>
                    <button type="button" class="vpx-btn-ghost" style="margin-left:12px;" data-vpx-nav="meal-plan">${t("page_grocery_back_btn")}</button>
                </div>
                <div class="vpx-card">
                    ${content}
                </div>
            </div>
        `;
    };

    function bindGroceryEvents() {
        document.querySelectorAll("[data-grocery-check]").forEach(function (box) {
            box.addEventListener("change", function () {
                const label = box.closest(".vpx-grocery-item");
                if (label) label.classList.toggle("is-checked", box.checked);
            });
        });
    }

    /*
    ==========================================
    Supplement Timing Schedule
    ------------------------------------------
    "What do I take, and when?" — turns the
    existing rule-based supplement recommendations
    into a chronological daily schedule, tied to
    whether TODAY is a training or rest day (per
    the live Weekly Schedule). Pre-workout only
    appears on training days (it has nothing to
    do on a rest day); everything else appears
    every day, with timing text that shifts
    slightly (e.g. protein: "after your workout"
    vs "with lunch or dinner"). Product links use
    the same window.VPX_COLLECTIONS data the
    Dashboard supplements card already relies on
    — this is the feature that ties the tool
    directly to product pages, daily.
    ==========================================
    */

    const SUPP_TIMING_RULES = {
        "vitamins-electrolytes": {
            icon: "🍊",
            training: { textKey: "timing_vitamins_daily", hour: 8 },
            rest: { textKey: "timing_vitamins_daily", hour: 8 }
        },
        "creatine-monohydrate": {
            icon: "💊",
            training: { textKey: "timing_creatine_daily", hour: 8.5 },
            rest: { textKey: "timing_creatine_daily", hour: 8.5 }
        },
        "pre-workout": {
            icon: "⚡",
            training: { textKey: "timing_preworkout_training", hour: 17 },
            rest: null
        },
        proteins: {
            icon: "🥛",
            training: { textKey: "timing_protein_training", hour: 18 },
            rest: { textKey: "timing_protein_rest", hour: 13 }
        },
        "collagen-joint-support": {
            icon: "🦴",
            training: { textKey: "timing_collagen_daily", hour: 22 },
            rest: { textKey: "timing_collagen_daily", hour: 22 }
        }
    };

    function buildSupplementTimingRows(data, isTraining) {

        const dayKey = isTraining ? "training" : "rest";

        const rows = (data.supplementRecommendations || []).map(function (rec) {

            const rule = SUPP_TIMING_RULES[rec.collectionHandle];
            if (!rule) return null;

            const dayRule = rule[dayKey];
            if (!dayRule) return null; // e.g. pre-workout has nothing to say on a rest day

            const nameKey = COLLECTION_NAME_KEYS[rec.collectionHandle] || rec.collectionHandle;
            const products = (window.VPX_COLLECTIONS && window.VPX_COLLECTIONS[rec.collectionHandle]) || [];
            const topProduct = products.length ? products[0] : null;

            return {
                icon: rule.icon,
                nameKey: nameKey,
                timingText: t(dayRule.textKey),
                hour: dayRule.hour,
                product: topProduct
            };
        }).filter(Boolean);

        rows.sort(function (a, b) { return a.hour - b.hour; });
        return rows;
    }

    Renderer.viewSupplementTiming = function (data) {

        const isTraining = isTodayTrainingDay();
        const rows = buildSupplementTimingRows(data, isTraining);

        const rowsHtml = rows.length ? rows.map(function (r) {
            const cta = r.product
                ? `<a href="${r.product.url}" target="_blank" rel="noopener" class="vpx-timing-cta">${t("btn_viewProduct")}</a>`
                : "";
            return `
                <div class="vpx-timing-row">
                    <div class="vpx-timing-icon">${r.icon}</div>
                    <div class="vpx-timing-text">
                        <strong>${t(r.nameKey)}</strong>
                        <div class="vpx-timing-subtext">${r.timingText}</div>
                    </div>
                    ${cta}
                </div>
            `;
        }).join("") : `<div style="font-size:12px; color:var(--vpx-muted); padding:20px 0;">${t("page_supptiming_empty")}</div>`;

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_supptiming_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_supptiming_hint")}</p>
                    <div class="vpx-onb-info-box">
                        ${isTraining ? "💪 " + t("schedule_training") : "🎨 " + t("schedule_rest")} — ${t("page_supptiming_auto_note")}
                    </div>
                </div>
                <div class="vpx-card">
                    ${rowsHtml}
                </div>
                <div class="vpx-card">
                    <div class="vpx-supp-disclaimer">${t("supp_disclaimer")}</div>
                    <button type="button" class="vpx-btn-gold" style="margin-top:16px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function buildPlanForDay(data, jsDay) {

        const schedule = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("trainingSchedule") : null;
        const trainingDays = (schedule && schedule.days) ? schedule.days : [];
        const isTraining = trainingDays.indexOf(jsDay) !== -1;

        const dayTargets = isTraining
            ? (data.targets.trainingDay || data.targets)
            : (data.targets.restDay || data.targets);

        const selections = getDayRecipeSelections(data, jsDay);

        const plan = window.VORYNEX.mealGenerator
            ? window.VORYNEX.mealGenerator.generate(dayTargets.calories, !isTraining, selections)
            : [];

        if (window.VORYNEX.optimizer && window.VORYNEX.optimizer.optimize && plan.length) {
            window.VORYNEX.optimizer.optimize(plan, {
                protein: dayTargets.protein,
                carbs: dayTargets.carbs,
                fat: dayTargets.fat
            });
        }

        return { plan: plan, isTraining: isTraining, dayTargets: dayTargets };
    }

    function weekDayWorkoutBlock(data, jsDay, isTraining) {

        if (!isTraining) {
            return `
                <div class="vpx-card">
                    <h3>${t("nav_workout")}</h3>
                    <div style="font-size:13px; color:var(--vpx-muted); padding:14px 0;">😴 ${t("week_view_rest_day_workout")}</div>
                </div>
            `;
        }

        const day = (data.workoutPlan && data.workoutPlan.days)
            ? data.workoutPlan.days.filter(function (d) { return d.jsDay === jsDay; })[0]
            : null;

        if (!day) return "";

        const rows = day.exercises.map(function (ex) {
            const superset = ex.supersetGroup ? ` <span style="color:var(--vpx-gold);">[${t("label_superset")} ${ex.supersetGroup}]</span>` : "";
            return `
                <div class="vpx-onb-review-row">
                    <span>${t(ex.nameKey)}${superset}</span>
                    <strong>${ex.sets}×${ex.reps} · ${t("label_rir")}: ${t(ex.rirKey)}</strong>
                </div>
            `;
        }).join("");

        return `
            <div class="vpx-card">
                <h3>${t("nav_workout")} — ${t(day.nameKey)}</h3>
                <div class="vpx-onb-review" style="margin-top:12px;">${rows}</div>
            </div>
        `;
    }

    Renderer.viewMealPlanFull = function (data) {

        const jsDay = mealWeekSelectedDay;
        const isToday = jsDay === new Date().getDay();

        const dayButtons = WEEK_VIEW_DAYS.map(function (d) {
            const schedule = (window.VORYNEX.state && window.VORYNEX.state.getSection)
                ? window.VORYNEX.state.getSection("trainingSchedule") : null;
            const isTrainingDay = schedule && schedule.days && schedule.days.indexOf(d.jsDay) !== -1;
            const activeClass = d.jsDay === jsDay ? " is-active" : "";
            const badge = isTrainingDay ? "💪" : "🎨";
            return `<button type="button" class="vpx-onb-daypick${activeClass}" data-week-view-day="${d.jsDay}">${t(d.key)}<br><span style="font-size:13px;">${badge}</span></button>`;
        }).join("");

        // Today: show the STORED plan (keeps swaps intact, swaps active).
        // Other days: computed on the fly, swaps hidden via CSS wrapper.
        let dayResult;
        if (isToday) {
            dayResult = {
                plan: data.mealPlan,
                isTraining: isTodayTrainingDay(),
                dayTargets: isTodayTrainingDay()
                    ? (data.targets.trainingDay || data.targets)
                    : (data.targets.restDay || data.targets)
            };
        } else {
            dayResult = buildPlanForDay(data, jsDay);
        }

        const wrapperClass = isToday ? "" : " vpx-week-readonly-swaps";
        const dayLabel = WEEK_VIEW_DAYS.filter(function (d) { return d.jsDay === jsDay; })[0];

        const swapHint = isToday
            ? ""
            : `<div style="font-size:11px; color:var(--vpx-muted); margin-bottom:12px;">${t("week_view_swap_hint")}</div>`;

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("nav_mealPlan")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("week_view_hint")}</p>
                    <div class="vpx-onb-daypick-row">${dayButtons}</div>
                    <div style="margin-top:14px; font-size:13px;">
                        <strong style="color:var(--vpx-gold);">${t(dayLabel.key)}</strong> —
                        ${dayResult.isTraining ? "💪 " + t("schedule_training") : "🎨 " + t("schedule_rest")} ·
                        ${dayResult.dayTargets.calories} kcal · P ${dayResult.dayTargets.protein}g / C ${dayResult.dayTargets.carbs}g / F ${dayResult.dayTargets.fat}g
                    </div>
                    <button type="button" class="vpx-btn-gold" style="margin-top:16px;" data-vpx-nav="grocery-list">${t("btn_groceryList")}</button>
                </div>
            </div>
            <div class="vpx-grid${wrapperClass}" data-vpx-week-day="${jsDay}">
                ${swapHint}
                ${Renderer.mealPreviewCard(dayResult.plan)}
            </div>
            <div class="vpx-grid">
                ${weekDayWorkoutBlock(data, jsDay, dayResult.isTraining)}
            </div>
            <div class="vpx-grid">
                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function performDayRecipeChange(jsDay, slotId, newRecipeId) {

        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

        const blueprint = window.VORYNEX.state.getSection("blueprint");
        if (!blueprint) return;

        // Per-day ONLY — the global recipeSelections fallback is left
        // untouched, so choosing an omelette for Monday never silently
        // changes what Wednesday shows.
        blueprint.dailyRecipeSelections = blueprint.dailyRecipeSelections || {};
        const key = String(jsDay);
        blueprint.dailyRecipeSelections[key] = blueprint.dailyRecipeSelections[key] || {};
        blueprint.dailyRecipeSelections[key][slotId] = newRecipeId;

        // If the edited day is TODAY, the stored today-plan must be
        // rebuilt — invalidate its calorie-basis marker so
        // ensureTodaysMealPlan regenerates it with the new recipe.
        if (jsDay === new Date().getDay()) {
            blueprint.mealPlanCalorieBasis = null;
        }

        window.VORYNEX.state.setSection("blueprint", blueprint);
        Renderer.render();
    }

    function bindWeekViewEvents() {
        document.querySelectorAll("[data-week-view-day]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                mealWeekSelectedDay = Number(btn.getAttribute("data-week-view-day"));
                Renderer.render();
            });
        });
    }

    /*
    ==========================================
    Weekly Check-in — Adaptive Recalibration
    ------------------------------------------
    The single most-respected feature among
    premium nutrition apps (MacroFactor et al.):
    don't just predict once and walk away —
    compare the REAL observed weight trend
    against what the plan predicted, honestly.
    This never auto-changes calories (that would
    be surprising/risky without the person's
    explicit choice) — it surfaces the comparison
    and points to Goals if they want to act on it.
    One check-in per calendar date (same overwrite
    pattern as exercise logs).
    ==========================================
    */

    /*
    ==========================================
    Units Preference
    ------------------------------------------
    Weights are always STORED in kg (single
    source of truth — the engine, cloud sync,
    and localStorage all deal exclusively in kg,
    so nothing has to guess which unit an old
    saved number was in). "formatWeightKg" is
    the only place that converts, purely for
    DISPLAY, based on the person's saved
    preference. Entry fields (Weekly Check-in,
    onboarding) still collect kg for now — full
    imperial entry support is a natural next step
    if this turns out to matter to customers.
    ==========================================
    */

    function getUnitsPreference() {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return "metric";
        const settings = window.VORYNEX.state.getSection("settings");
        return (settings && settings.units) || "metric";
    }

    function formatWeightKg(kg) {
        if (getUnitsPreference() === "imperial") {
            const lb = Math.round(kg * 2.20462 * 10) / 10;
            return lb + " lb";
        }
        return kg + " kg";
    }

    Renderer.viewSettings = function () {

        const units = getUnitsPreference();
        const isSignedIn = !!window.VPX_CUSTOMER;

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_settings_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_settings_hint")}</p>

                    <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("page_settings_units_label")}</div>
                    <div class="vpx-onb-options vpx-onb-options-col">
                        <button type="button" class="vpx-onb-option${units === "metric" ? " is-active" : ""}" data-settings-units="metric">${t("page_settings_units_metric")}</button>
                        <button type="button" class="vpx-onb-option${units === "imperial" ? " is-active" : ""}" data-settings-units="imperial">${t("page_settings_units_imperial")}</button>
                    </div>
                    <div class="vpx-onb-slider-hint" style="margin-top:10px;">${t("page_settings_units_note")}</div>
                </div>

                <div class="vpx-card">
                    <h3>${t("page_settings_privacy_title")}</h3>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">
                        ${isSignedIn ? t("page_settings_privacy_text_account") : t("page_settings_privacy_text_guest")}
                    </p>
                    ${isSignedIn ? `
                        <button type="button" class="vpx-btn-ghost vpx-btn-danger" data-settings-delete-start>${t("page_settings_delete_btn")}</button>
                        <div data-settings-delete-confirm hidden style="margin-top:12px;">
                            <div class="vpx-onb-info-box vpx-onb-info-box-danger">${t("page_settings_delete_confirm_text")}</div>
                            <button type="button" class="vpx-btn-danger-solid" style="margin-top:10px;" data-settings-delete-confirm-btn>${t("page_settings_delete_confirm_btn")}</button>
                            <button type="button" class="vpx-btn-ghost" style="margin-top:10px; margin-left:8px;" data-settings-delete-cancel>${t("page_settings_delete_cancel")}</button>
                        </div>
                    ` : ""}
                </div>

                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function bindSettingsEvents() {

        document.querySelectorAll("[data-settings-units]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                if (!window.VORYNEX.state || !window.VORYNEX.state.setSection) return;
                window.VORYNEX.state.setSection("settings", { units: btn.getAttribute("data-settings-units") });
                showToast(t("page_settings_saved"));
                Renderer.render();
            });
        });

        const startBtn = document.querySelector("[data-settings-delete-start]");
        const confirmPanel = document.querySelector("[data-settings-delete-confirm]");
        if (startBtn && confirmPanel) {
            startBtn.addEventListener("click", function () {
                confirmPanel.hidden = false;
                startBtn.hidden = true;
            });
        }

        const cancelBtn = document.querySelector("[data-settings-delete-cancel]");
        if (cancelBtn) {
            cancelBtn.addEventListener("click", function () {
                Renderer.render(); // re-render collapses the confirm panel back
            });
        }

        const confirmDeleteBtn = document.querySelector("[data-settings-delete-confirm-btn]");
        if (confirmDeleteBtn) {
            confirmDeleteBtn.addEventListener("click", function () {

                if (!window.VORYNEX.state || !window.VORYNEX.state.deleteAccountData) return;

                confirmDeleteBtn.disabled = true;
                confirmDeleteBtn.textContent = t("page_settings_deleting");

                window.VORYNEX.state.deleteAccountData()
                    .then(function () {
                        showToast(t("page_settings_delete_success"));
                        heroDismissed = false; // fresh start — show the hero/quiz landing again
                        currentView = "dashboard";
                        Renderer.render();
                    })
                    .catch(function (e) {
                        console.warn("VORYNEX: delete failed", e);
                        showToast(t("page_settings_delete_error"), "error");
                        confirmDeleteBtn.disabled = false;
                        confirmDeleteBtn.textContent = t("page_settings_delete_confirm_btn");
                    });
            });
        }
    }

    function getWeightCheckIns() {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return [];
        return (window.VORYNEX.state.getSection("weightCheckIns") || [])
            .slice()
            .sort(function (a, b) { return a.date < b.date ? -1 : 1; });
    }

    function addWeightCheckIn(weightKg) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection || !window.VORYNEX.state.setSection) return;

        const list = window.VORYNEX.state.getSection("weightCheckIns") || [];
        const today = new Date().toISOString().slice(0, 10);
        const idx = list.findIndex(function (e) { return e.date === today; });
        const entry = { date: today, weightKg: weightKg };

        if (idx !== -1) { list[idx] = entry; } else { list.push(entry); }
        window.VORYNEX.state.setSection("weightCheckIns", list);
    }

    function buildWeightRecalibrationInsight(data) {

        const checkIns = getWeightCheckIns();
        if (checkIns.length < 2) return null;

        const first = checkIns[0];
        const last = checkIns[checkIns.length - 1];

        const daysBetween = (new Date(last.date + "T00:00:00") - new Date(first.date + "T00:00:00")) / 86400000;
        if (daysBetween < 6) return null; // too little spread to say anything meaningful yet

        const weeksBetween = daysBetween / 7;
        const actualWeeklyRate = Math.round(((last.weightKg - first.weightKg) / weeksBetween) * 100) / 100;

        const predictedMagnitude = data.goal.weeklyRateKg || 0;
        if (predictedMagnitude === 0) return null;
        const predictedDirection = (data.goal.weightDeltaKg || 0) < 0 ? -1 : 1;
        const predictedWeeklyRate = Math.round(predictedMagnitude * predictedDirection * 100) / 100;

        const diffRatio = Math.abs(actualWeeklyRate - predictedWeeklyRate) / predictedMagnitude;

        return {
            actualWeeklyRate: actualWeeklyRate,
            predictedWeeklyRate: predictedWeeklyRate,
            significant: diffRatio >= 0.4 && Math.abs(actualWeeklyRate - predictedWeeklyRate) >= 0.1,
            slower: Math.abs(actualWeeklyRate) < Math.abs(predictedWeeklyRate)
        };
    }

    function buildWeightCheckinPanel(data) {

        const checkIns = getWeightCheckIns();
        const insight = buildWeightRecalibrationInsight(data);

        const historyRows = checkIns.slice(-5).reverse().map(function (entry) {
            return `<div class="vpx-ex-history-row"><span>${formatLogDate(entry.date)}</span><strong>${formatWeightKg(entry.weightKg)}</strong></div>`;
        }).join("");

        const insightHtml = insight ? `
            <div class="vpx-onb-info-box" style="margin-top:12px;">
                ${insight.significant
                    ? t(insight.slower ? "weekly_checkin_slower" : "weekly_checkin_faster", {
                        actual: Math.abs(insight.actualWeeklyRate),
                        predicted: Math.abs(insight.predictedWeeklyRate)
                    })
                    : t("weekly_checkin_on_track")}
                ${insight.significant ? `<button type="button" class="vpx-btn-ghost" style="margin-top:10px; display:block;" data-vpx-nav="goals">${t("weekly_checkin_goto_goals")}</button>` : ""}
            </div>
        ` : "";

        return `
            <div class="vpx-card">
                <h3>${t("weekly_checkin_title")}</h3>
                <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("weekly_checkin_explainer")}</p>

                <div class="vpx-ex-progress-input-row" style="margin-top:0;">
                    <input type="number" step="0.1" min="30" max="300" placeholder="${t('weekly_checkin_placeholder')}" data-checkin-weight-input />
                    <button type="button" class="vpx-btn-gold" data-checkin-save>${t('weekly_checkin_save_btn')}</button>
                </div>

                ${checkIns.length > 0 ? `<div class="vpx-ex-history" style="margin-top:12px;">${historyRows}</div>` : ""}
                ${insightHtml}
            </div>
        `;
    }

    function bindWeightCheckinEvents() {

        const saveBtn = document.querySelector("[data-checkin-save]");
        if (!saveBtn) return;

        saveBtn.addEventListener("click", function () {
            const input = document.querySelector("[data-checkin-weight-input]");
            const weight = input ? parseFloat(input.value) : NaN;

            if (isNaN(weight) || weight < 30 || weight > 300) {
                showToast(t("weekly_checkin_invalid"), "error");
                return;
            }

            addWeightCheckIn(weight);
            showToast(t("exercise_progress_saved"));
            Renderer.render();
        });
    }

    /*
    ==========================================
    Progress Hub
    ------------------------------------------
    Pulls together everything that was previously
    scattered — streak, weight check-in history,
    and strength progress across EVERY exercise
    ever logged (not just today's program) — into
    one page. Reuses the exact same panels/helpers
    the Timeline and Training Program pages
    already use, so there's a single source of
    truth for each piece of data.
    ==========================================
    */

    function buildAllExerciseProgressRows() {

        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return "";

        const allLogs = window.VORYNEX.state.getSection("exerciseLogs") || {};
        const catalogue = (window.VORYNEX.workout && window.VORYNEX.workout.exercises) || {};

        const exerciseIds = Object.keys(allLogs).filter(function (id) {
            return allLogs[id] && allLogs[id].length > 0;
        });

        if (exerciseIds.length === 0) {
            return `<div class="vpx-ex-progress-empty">${t("page_progress_no_exercises")}</div>`;
        }

        return exerciseIds.map(function (exerciseId) {

            const logs = getExerciseLogs(exerciseId);
            const first = logs[0];
            const last = logs[logs.length - 1];
            const firstTop = getEntryTopSet(first);
            const lastTop = getEntryTopSet(last);
            const weightDelta = Math.round((lastTop.weight - firstTop.weight) * 10) / 10;

            const exerciseEntry = catalogue[exerciseId];
            const displayName = exerciseEntry ? t(exerciseEntry.nameKey) : exerciseId;

            const deltaHtml = logs.length > 1
                ? `<span class="${weightDelta >= 0 ? 'vpx-ex-delta-up' : 'vpx-ex-delta-down'}">${weightDelta >= 0 ? "+" : ""}${weightDelta}kg</span>`
                : `<span style="color:var(--vpx-muted);">${t("page_progress_single_entry")}</span>`;

            return `
                <div class="vpx-onb-review-row">
                    <span>${displayName}</span>
                    <strong>${lastTop.weight}kg × ${lastTop.reps} &nbsp;${deltaHtml}</strong>
                </div>
            `;
        }).join("");
    }

    Renderer.viewProgress = function (data) {

        const streak = computeExerciseStreak();
        const streakHtml = streak >= 1
            ? `<div class="vpx-streak-badge">🔥 <strong>${streak}</strong> ${streak === 1 ? t("streak_day_singular") : t("streak_day_plural")}</div>`
            : "";

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_progress_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_progress_hint")}</p>
                    ${streakHtml}
                </div>

                ${buildWeightCheckinPanel(data)}

                <div class="vpx-card">
                    <h3>${t("page_progress_strength_title")}</h3>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_progress_strength_hint")}</p>
                    <div class="vpx-onb-review">${buildAllExerciseProgressRows()}</div>
                    <button type="button" class="vpx-btn-ghost" style="margin-top:14px;" data-vpx-nav="workout">${t("page_progress_goto_workout")}</button>
                </div>

                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    /*
    ==========================================
    Nutrition Plan — Deep Dive
    ------------------------------------------
    The "show your work" page — walks through
    exactly how the numbers on the Dashboard were
    calculated, using the person's own real
    figures (not generic examples). Pure
    education/trust-building, no interactivity.
    ==========================================
    */

    Renderer.viewNutritionPlan = function (data) {

        const meta = data.meta || {};
        const confidence = meta.confidence || { score: 0, checklist: [] };
        const missingItems = (confidence.checklist || []).filter(function (item) { return !item.present; });

        const macroRows = (data.macros || []).map(function (m) {
            return `<div class="vpx-onb-review-row"><span>${t(m.labelKey)}</span><strong>${m.value}${m.unit}</strong></div>`;
        }).join("");

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_nutritionplan_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:14px;">${t("page_nutritionplan_hint")}</p>
                </div>

                <div class="vpx-card">
                    <h3>${t("page_nutritionplan_bmr_title")}</h3>
                    <p style="font-size:13px; color:var(--vpx-white); line-height:1.7; margin-bottom:14px;">
                        ${t("page_nutritionplan_bmr_text", { bmr: meta.bmr, equation: meta.bmrEquationUsed })}
                    </p>
                    <div class="vpx-onb-review-row"><span>${t("page_nutritionplan_bmr_label")}</span><strong>${meta.bmr} kcal</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("page_nutritionplan_tdee_label")}</span><strong>${meta.tdee} kcal</strong></div>
                    <div class="vpx-onb-review-row"><span>${t("page_nutritionplan_target_label")}</span><strong>${data.targets.calories} kcal</strong></div>
                    ${meta.calorieFloorApplied ? `<div class="vpx-onb-info-box" style="margin-top:12px;">${t("dashboard_calorie_floor_note", { bmr: meta.bmr })}</div>` : ""}
                </div>

                <div class="vpx-card">
                    <h3>${t("page_nutritionplan_macros_title")}</h3>
                    <p style="font-size:13px; color:var(--vpx-white); line-height:1.7; margin-bottom:14px;">${t("page_nutritionplan_macros_text")}</p>
                    <div class="vpx-onb-review">${macroRows}</div>
                </div>

                <div class="vpx-card">
                    <h3>${t("page_nutritionplan_confidence_title")}</h3>
                    <p style="font-size:13px; color:var(--vpx-white); line-height:1.7; margin-bottom:10px;">${t("page_nutritionplan_confidence_text", { score: confidence.score })}</p>
                    ${missingItems.length > 0 ? `
                        <p style="font-size:12px; color:var(--vpx-gold); margin-bottom:8px;">${t("page_nutritionplan_confidence_missing")}</p>
                        <div class="vpx-onb-review">
                            ${missingItems.map(function (item) { return `<div class="vpx-onb-review-row"><span>${t(item.labelKey || "")}</span><strong>—</strong></div>`; }).join("")}
                        </div>
                        <button type="button" class="vpx-btn-ghost" style="margin-top:14px;" data-vpx-nav="profile">${t("page_nutritionplan_goto_profile")}</button>
                    ` : `<p style="font-size:12px; color:var(--vpx-green);">${t("page_nutritionplan_confidence_full")}</p>`}
                </div>

                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    Renderer.viewTimeline = function (data) {

        const series = buildProjectedWeightSeries(data.goal);

        const chartHtml = series.length > 1
            ? Renderer.svgLine(series, 600, 220)
            : `<div style="font-size:12px; color:var(--vpx-muted); padding:30px 0; text-align:center;">${t("weekly_empty")}</div>`;

        // The differentiator: show what a naive linear calculator would
        // have promised vs our metabolism-aware number — only when the
        // adaptive curve exists AND the difference is meaningful.
        const hasComparison = data.goal.linearTargetWeight !== undefined
            && data.goal.weeklyCurve && data.goal.weeklyCurve.length > 1
            && Math.abs(data.goal.linearTargetWeight - data.goal.targetWeight) >= 0.3;

        const comparisonHtml = hasComparison ? `
            <div class="vpx-onb-info-box" style="margin-top:16px;">
                ${t("timeline_adaptive_explainer", {
                    naive: data.goal.linearTargetWeight,
                    adaptive: data.goal.targetWeight
                })}
            </div>
        ` : "";

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_timeline_title")}</h2>
                    <div class="vpx-goal-phase">${t(data.goal.phaseKey)}</div>
                    <div style="font-size:12px; color:var(--vpx-muted); margin-bottom:20px;">
                        ${t("page_timeline_projected")}: ${formatWeightKg(data.goal.currentWeight)} → ${formatWeightKg(data.goal.targetWeight)} (${data.goal.weeksRemaining} ${t("goal_weeks")})
                    </div>
                    ${chartHtml}
                    ${comparisonHtml}
                    <div class="vpx-supp-disclaimer" style="margin-top:20px;">${t("page_timeline_disclaimer")}</div>
                </div>

                ${buildWeightCheckinPanel(data)}

                <div class="vpx-card">
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    Renderer.viewComingSoon = function () {
        return `
            <div class="vpx-grid">
                <div class="vpx-card" style="text-align:center; padding:60px 20px;">
                    <div style="font-size:40px; margin-bottom:16px;">🚧</div>
                    <h2 style="margin-bottom:8px;">${t("page_comingSoon_title")}</h2>
                    <p style="font-size:13px; color:var(--vpx-muted); margin-bottom:24px;">${t("page_comingSoon_text")}</p>
                    <button type="button" class="vpx-btn-gold" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function bindNavEvents() {
        document.querySelectorAll("[data-vpx-nav]").forEach(function (item) {
            item.addEventListener("click", function () {
                const target = item.getAttribute("data-vpx-nav");
                // A fresh visit to the Dashboard (from elsewhere) starts
                // on "Today" — only in-page tab clicks (handled by
                // bindDashboardMobileTabs) should change this mid-visit.
                if (target === "dashboard") dashboardMobileTab = "today";
                currentView = target;
                Renderer.render();
            });
        });
    }

    /*
    ==========================================
    Day-Aware Meal Plan
    ------------------------------------------
    Calorie/macro targets stay fixed from the
    blueprint, but the MEAL PLAN itself is
    resolved per-day: training days get the full
    5-meal plan, rest days get 3 meals (no
    Pre/Post-Workout). Swaps a user makes persist
    for as long as today's mode (training/rest)
    matches what's stored — crossing into a
    different mode regenerates fresh.
    ==========================================
    */

    function isTodayTrainingDay() {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return true;
        const schedule = window.VORYNEX.state.getSection("trainingSchedule");
        if (!schedule || !schedule.days || schedule.days.length === 0) return true; // no schedule set — default to full plan
        return schedule.days.indexOf(new Date().getDay()) !== -1;
    }

    /*
    ==========================================
    Day-Aware Workout Plan
    ------------------------------------------
    The workout PROGRAM (which split, which days
    do what) is rebuilt fresh from the LIVE weekly
    schedule every render — never frozen from
    onboarding-time answers. This way, editing
    the Weekly Schedule page immediately updates
    the training program too, and the program is
    never gated on what day "today" happens to be
    — it always shows the full weekly plan.
    ==========================================
    */

    function ensureWorkoutPlan(data) {

        if (!window.VORYNEX.workout || !window.VORYNEX.workout.buildPlan) {
            return data.workoutPlan || null;
        }

        const scheduleSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("trainingSchedule")
            : null;
        const liveDays = (scheduleSection && scheduleSection.days) ? scheduleSection.days : [];

        const onboardingSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("onboarding")
            : null;
        const goal = (onboardingSection && onboardingSection.answers && onboardingSection.answers.goal)
            || "maintenance";

        return window.VORYNEX.workout.buildPlan({
            trainingDaysPerWeek: liveDays.length,
            trainingDays: liveDays,
            goal: goal
        });
    }

    function ensureTodaysMealPlan(data) {

        if (!window.VORYNEX.mealGenerator) return data;

        const todayJsDay = new Date().getDay();
        const todayStamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        const restDay = !isTodayTrainingDay();
        const desiredMode = restDay ? "rest" : "training";
        const expectedMealCount = restDay ? 3 : 5;

        // Today's plan uses the MERGED recipe selections: the global
        // fallback plus any per-day override the person set for this
        // weekday in the Week View — so "Monday = omelette" shows up
        // on the Dashboard too, not just inside the Week View.
        const todaySelections = getDayRecipeSelections(data, todayJsDay);

        // Calorie cycling: each day type has its own calorie/macro
        // target (training days higher, rest days lower — same weekly
        // total). Blueprints calculated before v38 won't carry the
        // per-day targets, so fall back to the flat average for those.
        const dayTargets = restDay
            ? (data.targets.restDay || data.targets)
            : (data.targets.trainingDay || data.targets);

        // Cache is only valid for THE SAME calendar date: crossing
        // midnight invalidates it even if mode/count/calories happen to
        // match (e.g. Tue→Wed both training days), because the new day
        // may carry different per-day recipe choices.
        const cacheIsValid = data.mealPlanMode === desiredMode
            && data.mealPlan
            && data.mealPlan.length === expectedMealCount
            && data.mealPlanCalorieBasis === dayTargets.calories
            && data.mealPlanDateStamp === todayStamp;

        if (cacheIsValid) {
            return data; // already correct for today, keep any swaps intact
        }

        const freshPlan = window.VORYNEX.mealGenerator.generate(dayTargets.calories, restDay, todaySelections);

        // Re-apply any previously chosen condiments (informational only,
        // never affects the calculated base kcal/macros above).
        if (data.condimentSelections) {
            freshPlan.forEach(function (m) {
                if (data.condimentSelections[m.slotId]) {
                    m.selectedCondiments = data.condimentSelections[m.slotId];
                }
            });
        }

        if (window.VORYNEX.optimizer && window.VORYNEX.optimizer.optimize) {
            window.VORYNEX.optimizer.optimize(freshPlan, {
                protein: dayTargets.protein,
                carbs: dayTargets.carbs,
                fat: dayTargets.fat
            });
        }

        data.mealPlan = freshPlan;
        data.mealPlanMode = desiredMode;
        data.mealPlanCalorieBasis = dayTargets.calories;
        data.mealPlanDateStamp = todayStamp;

        if (window.VORYNEX.state && window.VORYNEX.state.setSection) {
            window.VORYNEX.state.setSection("blueprint", data);
        }

        return data;
    }

    /*
    ==========================================
    Weekly Schedule Page — training/rest picker
    + per-day personal notes
    ==========================================
    */

    function getCurrentWeekDates() {
        const today = new Date();
        const dow = today.getDay(); // 0=Sun..6=Sat
        const mondayOffset = dow === 0 ? -6 : 1 - dow;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);

        const dates = [];
        for (let i = 0; i < 7; i++) {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            dates.push(d);
        }
        return dates;
    }

    function formatDateKey(d) {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return y + "-" + m + "-" + day;
    }

    Renderer.viewSchedule = function () {

        const scheduleSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("trainingSchedule") : null;
        const trainingDays = (scheduleSection && scheduleSection.days) ? scheduleSection.days : [];

        const notes = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? (window.VORYNEX.state.getSection("dailyNotes") || {}) : {};

        const weekDates = getCurrentWeekDates();
        const todayKey = formatDateKey(new Date());
        const dayLabelKeys = ["day_mon", "day_tue", "day_wed", "day_thu", "day_fri", "day_sat", "day_sun"];

        const dayCards = weekDates.map(function (d, i) {
            const jsDay = d.getDay();
            const key = formatDateKey(d);
            const isTraining = trainingDays.indexOf(jsDay) !== -1;
            const isToday = key === todayKey;
            const noteValue = notes[key] || "";

            return `
                <div class="vpx-sched-day${isToday ? " is-today" : ""}">
                    <div class="vpx-sched-day-label">${t(dayLabelKeys[i])}</div>
                    <div class="vpx-sched-day-date">${d.getDate()}/${d.getMonth() + 1}</div>
                    <button type="button" class="vpx-sched-toggle${isTraining ? " is-training" : " is-rest"}" data-sched-day="${jsDay}">
                        ${isTraining ? "💪" : "😴"}
                    </button>
                    <div class="vpx-sched-toggle-label">${isTraining ? t("schedule_training") : t("schedule_rest")}</div>
                    <textarea class="vpx-sched-note" data-note-date="${key}" placeholder="${t("schedule_notePlaceholder")}">${noteValue}</textarea>
                </div>
            `;
        }).join("");

        const firstTimeExplainer = trainingDays.length === 0
            ? `<div class="vpx-onb-info-box">${t("page_schedule_first_time_explainer")}</div>`
            : "";

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_schedule_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:12px;">${t("page_schedule_hint")}</p>
                    ${firstTimeExplainer}
                    <div class="vpx-sched-warning">${t("page_schedule_warning")}</div>
                    <div class="vpx-sched-week">${dayCards}</div>
                    <button type="button" class="vpx-btn-gold" style="margin-top:20px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function bindScheduleEvents() {

        document.querySelectorAll("[data-sched-day]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

                const jsDay = Number(btn.getAttribute("data-sched-day"));
                const current = window.VORYNEX.state.getSection("trainingSchedule") || { days: [] };
                const days = current.days || [];
                const idx = days.indexOf(jsDay);

                if (idx === -1) {
                    days.push(jsDay);
                } else {
                    days.splice(idx, 1);
                }

                window.VORYNEX.state.setSection("trainingSchedule", { days: days });

                // FULL recalculation — not just meals/workout. Training
                // frequency feeds exercise calories inside TDEE, so
                // changing schedule days must also change the calorie/
                // macro targets. Previously only the meal distribution
                // and workout split updated, leaving the calorie target
                // frozen at whatever was set during onboarding.
                const onboardingSection = window.VORYNEX.state.getSection("onboarding");
                if (onboardingSection && onboardingSection.answers && window.VORYNEX.engine && window.VORYNEX.engine.calculate) {

                    const updatedAnswers = Object.assign({}, onboardingSection.answers, {
                        trainingDays: days.slice(),
                        trainingDaysPerWeek: days.length
                    });

                    const oldBlueprint = window.VORYNEX.state.getSection("blueprint");
                    const result = window.VORYNEX.engine.calculate(updatedAnswers);

                    // Keep the person's recipe/condiment choices across
                    // the recalculation — only the numbers should change.
                    if (oldBlueprint) {
                        result.recipeSelections = oldBlueprint.recipeSelections || {};
                        result.condimentSelections = oldBlueprint.condimentSelections || {};
                    }

                    window.VORYNEX.state.setSection("blueprint", result);
                    window.VORYNEX.state.setSection("onboarding", { completed: true, answers: updatedAnswers });
                }

                Renderer.render();
            });
        });

        document.querySelectorAll("[data-note-date]").forEach(function (textarea) {
            textarea.addEventListener("blur", function () {
                if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

                const key = textarea.getAttribute("data-note-date");
                const current = window.VORYNEX.state.getSection("dailyNotes") || {};
                current[key] = textarea.value;
                window.VORYNEX.state.setSection("dailyNotes", current);
            });
        });
    }

    /*
    ==========================================
    Training Program Page
    ------------------------------------------
    Optional — clearly framed as an add-on, never
    required for the nutrition side to work.
    ==========================================
    */

    const WEEKDAY_LABEL_KEYS_BY_JSDAY = {
        1: "day_mon", 2: "day_tue", 3: "day_wed", 4: "day_thu",
        5: "day_fri", 6: "day_sat", 0: "day_sun"
    };

    /*
    ==========================================
    Exercise Progress Logging
    ------------------------------------------
    Manual, per-exercise strength/rep tracking —
    turns the Training Program from "here's what
    to do" into "here's what you've actually
    done and how far you've come". Discreet by
    design: collapsed by default behind a small
    "📊 Πρόοδος" toggle on each exercise row, so
    it never clutters the plan itself, but is one
    tap away. One entry per calendar date per
    exercise (logging again today overwrites
    today's entry rather than duplicating it —
    the person adjusting their number mid-day
    shouldn't create a second row).
    ==========================================
    */

    // Which exercises currently have their progress panel open — kept
    // in memory only (not persisted): a fresh page load starts collapsed,
    // matching the "discreet" intent; it survives re-renders triggered
    // by saving a new entry so the panel doesn't snap shut right after use.
    let expandedExerciseProgress = {};

    function getExerciseLogs(exerciseId) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return [];
        const all = window.VORYNEX.state.getSection("exerciseLogs") || {};
        return (all[exerciseId] || []).slice().sort(function (a, b) { return a.date < b.date ? -1 : 1; });
    }

    // Entries may be either the new per-set shape ({date, sets:[...]})
    // or the older single-number shape ({date, weight, reps}) from
    // before multi-set logging existed — this normalizes either into
    // "the heaviest set of that session" for progress comparisons,
    // the same convention Strong/Hevy use for tracking strength trend.
    function getEntryTopSet(entry) {
        if (entry.sets && entry.sets.length) {
            return entry.sets.reduce(function (best, s) { return s.weight > best.weight ? s : best; }, entry.sets[0]);
        }
        return { weight: entry.weight || 0, reps: entry.reps || 0 };
    }

    function formatEntrySets(entry) {
        if (entry.sets && entry.sets.length) {
            return entry.sets.map(function (s) { return s.weight + "kg×" + s.reps; }).join(", ");
        }
        return entry.weight + "kg×" + entry.reps;
    }

    function addExerciseLogEntry(exerciseId, sets) {
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection || !window.VORYNEX.state.setSection) return;

        const all = window.VORYNEX.state.getSection("exerciseLogs") || {};
        const list = all[exerciseId] || [];
        const today = new Date().toISOString().slice(0, 10);

        const existingIdx = list.findIndex(function (e) { return e.date === today; });
        const entry = { date: today, sets: sets };

        if (existingIdx !== -1) {
            list[existingIdx] = entry;
        } else {
            list.push(entry);
        }

        all[exerciseId] = list;
        window.VORYNEX.state.setSection("exerciseLogs", all);
    }

    function formatLogDate(dateStr) {
        const d = new Date(dateStr + "T00:00:00");
        const dayEntry = WEEK_VIEW_DAYS.filter(function (w) { return w.jsDay === d.getDay(); })[0];
        const dayLabel = dayEntry ? t(dayEntry.key) : "";
        return dayLabel + " " + d.getDate() + "/" + (d.getMonth() + 1);
    }

    function buildSetRowHtml(setNumber, prefillWeight, prefillReps) {
        const weightVal = prefillWeight !== undefined && prefillWeight !== null ? ` value="${prefillWeight}"` : "";
        const repsVal = prefillReps !== undefined && prefillReps !== null ? ` value="${prefillReps}"` : "";
        return `
            <div class="vpx-ex-set-row">
                <div class="vpx-ex-set-label">${t("exercise_progress_set_label")} ${setNumber}</div>
                <div class="vpx-ex-set-inputs">
                    <input type="number" step="0.5" min="0" placeholder="${t('exercise_progress_weight_placeholder')}" data-ex-set-weight${weightVal} />
                    <input type="number" step="1" min="0" placeholder="${t('exercise_progress_reps_placeholder')}" data-ex-set-reps${repsVal} />
                </div>
            </div>
        `;
    }

    function buildExerciseProgressPanel(exerciseId, prescribedSets) {

        const logs = getExerciseLogs(exerciseId);
        const isOpen = !!expandedExerciseProgress[exerciseId];

        let summaryHtml = `<div class="vpx-ex-progress-empty">${t("exercise_progress_empty")}</div>`;
        let quickFillBtn = "";

        if (logs.length > 0) {
            const first = logs[0];
            const last = logs[logs.length - 1];
            const firstTop = getEntryTopSet(first);
            const lastTop = getEntryTopSet(last);
            const weightDelta = Math.round((lastTop.weight - firstTop.weight) * 10) / 10;
            const repsDelta = lastTop.reps - firstTop.reps;

            const weightDeltaHtml = logs.length > 1
                ? `<span class="${weightDelta >= 0 ? 'vpx-ex-delta-up' : 'vpx-ex-delta-down'}">${weightDelta >= 0 ? "+" : ""}${weightDelta}kg</span>`
                : "";
            const repsDeltaHtml = logs.length > 1
                ? `<span class="${repsDelta >= 0 ? 'vpx-ex-delta-up' : 'vpx-ex-delta-down'}">${repsDelta >= 0 ? "+" : ""}${repsDelta} ${t("label_reps")}</span>`
                : "";

            const historyRows = logs.slice(-5).reverse().map(function (entry) {
                return `<div class="vpx-ex-history-row"><span>${formatLogDate(entry.date)}</span><strong>${formatEntrySets(entry)}</strong></div>`;
            }).join("");

            summaryHtml = `
                <div class="vpx-ex-progress-summary">
                    <div>${t("exercise_progress_latest")}: <strong>${formatEntrySets(last)}</strong></div>
                    ${logs.length > 1 ? `<div class="vpx-ex-progress-trend">${t("exercise_progress_since_start")} (${t("exercise_progress_top_set")}): ${weightDeltaHtml} ${repsDeltaHtml}</div>` : ""}
                </div>
                <div class="vpx-ex-history">${historyRows}</div>
            `;

            // Most sessions repeat similar weights — prefilling from last
            // time turns logging into "confirm or tweak" instead of typing
            // everything from scratch. Only offered if today has no entry
            // yet (there's something meaningful to copy FROM).
            const today = new Date().toISOString().slice(0, 10);
            if (last.date !== today) {
                quickFillBtn = `<button type="button" class="vpx-ex-quickfill" data-ex-quickfill="${exerciseId}">↺ ${t("exercise_progress_quickfill")}</button>`;
            }
        }

        const setCount = Math.max(1, prescribedSets || 3);
        const setRows = [];
        for (let i = 1; i <= setCount; i++) {
            setRows.push(buildSetRowHtml(i));
        }

        return `
            <div class="vpx-ex-progress" data-exercise-id="${exerciseId}">
                <button type="button" class="vpx-ex-progress-toggle" data-ex-progress-toggle="${exerciseId}">
                    📊 ${t("exercise_progress_toggle")}
                </button>
                <div class="vpx-ex-progress-panel"${isOpen ? "" : " hidden"}>
                    ${summaryHtml}
                    ${quickFillBtn}
                    <div class="vpx-ex-sets-list" data-ex-sets-list>${setRows.join("")}</div>
                    <button type="button" class="vpx-ex-add-set" data-ex-add-set>+ ${t("exercise_progress_add_set")}</button>
                    <button type="button" class="vpx-btn-gold" style="margin-top:10px; width:100%;" data-ex-progress-save="${exerciseId}">${t('exercise_progress_save_btn')}</button>
                    <div class="vpx-ex-progress-caption">${t('exercise_progress_caption')}</div>
                </div>
            </div>
        `;
    }

    function bindExerciseProgressEvents() {

        document.querySelectorAll("[data-ex-progress-toggle]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const exerciseId = btn.getAttribute("data-ex-progress-toggle");
                expandedExerciseProgress[exerciseId] = !expandedExerciseProgress[exerciseId];
                Renderer.render();
            });
        });

        document.querySelectorAll("[data-ex-add-set]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const wrapper = btn.closest(".vpx-ex-progress");
                const list = wrapper ? wrapper.querySelector("[data-ex-sets-list]") : null;
                if (!list) return;
                const nextNum = list.children.length + 1;
                const holder = document.createElement("div");
                holder.innerHTML = buildSetRowHtml(nextNum).trim();
                list.appendChild(holder.firstChild);
            });
        });

        document.querySelectorAll("[data-ex-quickfill]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const exerciseId = btn.getAttribute("data-ex-quickfill");
                const wrapper = btn.closest(".vpx-ex-progress");
                const list = wrapper ? wrapper.querySelector("[data-ex-sets-list]") : null;
                if (!list) return;

                const logs = getExerciseLogs(exerciseId);
                if (!logs.length) return;
                const last = logs[logs.length - 1];
                const lastSets = (last.sets && last.sets.length) ? last.sets : [{ weight: last.weight, reps: last.reps }];

                list.innerHTML = lastSets.map(function (s, idx) {
                    return buildSetRowHtml(idx + 1, s.weight, s.reps);
                }).join("");
            });
        });

        document.querySelectorAll("[data-ex-progress-save]").forEach(function (btn) {
            btn.addEventListener("click", function () {

                const exerciseId = btn.getAttribute("data-ex-progress-save");
                const wrapper = btn.closest(".vpx-ex-progress");
                if (!wrapper) return;

                const rows = wrapper.querySelectorAll(".vpx-ex-set-row");
                const sets = [];

                rows.forEach(function (row) {
                    const weightInput = row.querySelector("[data-ex-set-weight]");
                    const repsInput = row.querySelector("[data-ex-set-reps]");
                    const weight = weightInput ? parseFloat(weightInput.value) : NaN;
                    const reps = repsInput ? parseInt(repsInput.value, 10) : NaN;
                    if (!isNaN(weight) && !isNaN(reps) && weight > 0 && reps > 0) {
                        sets.push({ weight: weight, reps: reps });
                    }
                });

                if (sets.length === 0) {
                    showToast(t("exercise_progress_invalid"), "error");
                    return;
                }

                addExerciseLogEntry(exerciseId, sets);
                expandedExerciseProgress[exerciseId] = true; // keep it open to show the new entry
                showToast(t("exercise_progress_saved"));
                Renderer.render();
            });
        });
    }

    Renderer.viewWorkout = function (data) {

        const plan = data.workoutPlan;

        if (!plan) {
            return `
                <div class="vpx-grid">
                    <div class="vpx-card" style="text-align:center; padding:50px 20px;">
                        <div style="font-size:36px; margin-bottom:16px;">🏋️</div>
                        <h2 style="margin-bottom:12px;">${t("page_workout_title")}</h2>
                        <p style="font-size:13px; color:var(--vpx-muted); margin-bottom:24px; max-width:420px; margin-left:auto; margin-right:auto;">
                            ${t("page_workout_empty")}
                        </p>
                        <button type="button" class="vpx-btn-gold" data-vpx-nav="schedule">${t("page_workout_goto_schedule")}</button>
                    </div>
                </div>
            `;
        }

        const dayCards = plan.days.map(function (day) {

            const dayLabel = (day.jsDay !== null && day.jsDay !== undefined)
                ? t(WEEKDAY_LABEL_KEYS_BY_JSDAY[day.jsDay])
                : "";

            let lastGroup = null;

            const exerciseRows = day.exercises.map(function (ex) {

                const groupOpenTag = (ex.supersetGroup && ex.supersetGroup !== lastGroup)
                    ? `<div class="vpx-superset-label">${t("label_superset")} ${ex.supersetGroup}</div><div class="vpx-superset-group">`
                    : "";

                const isLastInGroup = !day.exercises[day.exercises.indexOf(ex) + 1]
                    || day.exercises[day.exercises.indexOf(ex) + 1].supersetGroup !== ex.supersetGroup;

                const groupCloseTag = (ex.supersetGroup && isLastInGroup) ? `</div>` : "";

                lastGroup = ex.supersetGroup;

                const tempoLine = ex.tempoKey
                    ? `<div class="vpx-workout-exercise-meta">${t("label_tempo")}: ${t(ex.tempoKey)}</div>`
                    : "";

                const progressPanel = buildExerciseProgressPanel(ex.id, ex.sets);

                return `
                    ${groupOpenTag}
                    <div class="vpx-workout-exercise-row${ex.supersetGroup ? " is-in-superset" : ""}">
                        <div class="vpx-workout-exercise-main">
                            <span class="vpx-workout-exercise-name">${t(ex.nameKey)}</span>
                            <span class="vpx-workout-exercise-scheme">${ex.sets} ${t("label_sets")} × ${ex.reps} ${t("label_reps")}</span>
                        </div>
                        <div class="vpx-workout-exercise-meta">${t("label_rest")}: ${t(ex.restKey)} · ${t("label_rir")}: ${t(ex.rirKey)}</div>
                        ${tempoLine}
                        ${progressPanel}
                    </div>
                    ${groupCloseTag}
                `;
            }).join("");

            return `
                <div class="vpx-workout-day-card">
                    <div class="vpx-workout-day-header">
                        ${dayLabel ? `<span class="vpx-workout-day-badge">${dayLabel}</span>` : ""}
                        <strong>${t(day.nameKey)}</strong>
                    </div>
                    ${exerciseRows}
                </div>
            `;
        }).join("");

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_workout_title")}</h2>
                    <div class="vpx-goal-phase">${t(plan.splitNameKey)}</div>
                    <div class="vpx-workout-optional-note">${t("page_workout_optional_note")}</div>
                    <div class="vpx-workout-coach-notes">
                        <div class="vpx-workout-note">${t("note_warmup")}</div>
                        <div class="vpx-workout-note">${t("note_progressive_overload")}</div>
                    </div>
                    <div class="vpx-workout-days">${dayCards}</div>
                    <button type="button" class="vpx-btn-gold" style="margin-top:20px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    /*
    ==========================================
    AI Coach Page — free, curated Q&A
    ------------------------------------------
    Chat-styled, but answers are pre-written
    templates filled with the user's real data —
    not a live AI. Free-text input is matched via
    keyword scoring (vpx-coach.js) to the closest
    question. Conversation is in-memory only
    (resets on reload, no need to persist).
    ==========================================
    */

    let coachConversation = [];
    let coachWidgetOpen = false;

    function tLang(lang, key, vars) {
        if (window.VORYNEX.i18n && window.VORYNEX.i18n.tIn) {
            return window.VORYNEX.i18n.tIn(lang, key, vars);
        }
        return t(key, vars);
    }

    function buildCoachAnswer(questionId, data, lang) {

        if (!window.VORYNEX.coach) return "";

        const answerLang = lang || currentLang();
        const vars = window.VORYNEX.coach.getAnswerVars(questionId, data);

        if (questionId === "calories") {
            return tLang(answerLang, "coach_a_calories", {
                calories: vars.calories,
                tdee: vars.tdee,
                diff: vars.diff,
                directionKey: tLang(answerLang, vars.directionKey)
            });
        }

        if (questionId === "restday") {
            return vars.isTrainingToday ? tLang(answerLang, "coach_a_restday_training") : tLang(answerLang, "coach_a_restday_rest");
        }

        if (questionId === "splitchoice") {
            if (!vars.hasPlan) return tLang(answerLang, "coach_a_splitchoice_no");
            return tLang(answerLang, "coach_a_splitchoice_yes", {
                dayCount: vars.dayCount,
                splitNameKey: tLang(answerLang, vars.splitNameKey),
                supersetNote: vars.hasSuperset ? tLang(answerLang, "coach_superset_note") : ""
            });
        }

        // Every other question follows the simple pattern: coach_a_<id> + vars
        return tLang(answerLang, "coach_a_" + questionId, vars);
    }

    function askCoachQuestion(questionId, data, questionLabelOverride) {

        coachConversation.push({ role: "user", text: questionLabelOverride || t("coach_q_" + questionId) });
        Renderer.render();

        // Small "thinking" delay for a more natural feel — purely cosmetic.
        setTimeout(function () {
            coachConversation.push({ role: "coach", text: buildCoachAnswer(questionId, data) });
            Renderer.render();
        }, 450);
    }

    function askCoachFreeText(inputText, data) {

        const trimmed = inputText.trim();
        if (!trimmed) return;

        coachConversation.push({ role: "user", text: trimmed });
        Renderer.render();

        setTimeout(function () {
            const detectedLang = window.VORYNEX.coach ? window.VORYNEX.coach.detectLanguage(trimmed) : currentLang();
            const matchedId = window.VORYNEX.coach ? window.VORYNEX.coach.matchQuestion(trimmed, detectedLang) : null;
            const answer = matchedId ? buildCoachAnswer(matchedId, data, detectedLang) : tLang(detectedLang, "coach_no_match");
            coachConversation.push({ role: "coach", text: answer });
            Renderer.render();
        }, 450);
    }

    function renderCoachThread(idPrefix) {
        const messages = coachConversation.map(function (msg) {
            const bubbleClass = msg.role === "user" ? "vpx-coach-bubble-user" : "vpx-coach-bubble-coach";
            return `<div class="vpx-coach-bubble ${bubbleClass}">${msg.text}</div>`;
        }).join("");

        return `
            <div class="vpx-coach-thread" id="${idPrefix}-thread">
                <div class="vpx-coach-bubble vpx-coach-bubble-coach">${t("coach_intro")}</div>
                ${messages}
            </div>
        `;
    }

    function renderCoachInputRow(idPrefix) {
        return `
            <div class="vpx-coach-input-row">
                <input type="text" class="vpx-coach-input" id="${idPrefix}-input" placeholder="${t("coach_placeholder")}" />
                <button type="button" class="vpx-btn-gold vpx-coach-send-btn" data-coach-send="${idPrefix}">${t("coach_send")}</button>
            </div>
        `;
    }

    Renderer.viewCoach = function (data) {

        const questionIds = window.VORYNEX.coach ? window.VORYNEX.coach.getQuestionIds() : [];

        const chips = questionIds.map(function (id) {
            return `<button type="button" class="vpx-coach-chip" data-coach-question="${id}">${t("coach_q_" + id)}</button>`;
        }).join("");

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <div style="display:flex; justify-content:space-between; align-items:center;">
                        <h2 style="margin-bottom:0;">${t("page_coach_title")}</h2>
                        <button type="button" class="vpx-btn-ghost" data-coach-clear="page">${t("coach_clear")}</button>
                    </div>
                    <div class="vpx-workout-optional-note">${t("page_coach_disclaimer")}</div>

                    ${renderCoachThread("vpx-coach-page")}
                    ${renderCoachInputRow("vpx-coach-page")}

                    <div class="vpx-coach-chips" style="margin-top:16px;">${chips}</div>

                    <button type="button" class="vpx-btn-gold" style="margin-top:20px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    Renderer.viewCoachWidget = function (data) {

        if (currentView === "coach") return ""; // no point floating over the full page

        const questionIds = window.VORYNEX.coach ? window.VORYNEX.coach.getQuestionIds().slice(0, 6) : [];

        const chips = questionIds.map(function (id) {
            return `<button type="button" class="vpx-coach-chip" data-coach-question="${id}">${t("coach_q_" + id)}</button>`;
        }).join("");

        return `
            <button type="button" class="vpx-coach-fab" data-coach-toggle="1" aria-label="AI Coach">💬</button>
            <div class="vpx-coach-widget${coachWidgetOpen ? " is-open" : ""}">
                <div class="vpx-coach-widget-header">
                    <strong>${t("page_coach_title")}</strong>
                    <div>
                        <button type="button" class="vpx-btn-ghost" data-coach-clear="widget">${t("coach_clear")}</button>
                        <button type="button" class="vpx-coach-widget-close" data-coach-toggle="1">✕</button>
                    </div>
                </div>
                ${renderCoachThread("vpx-coach-widget")}
                ${renderCoachInputRow("vpx-coach-widget")}
                <div class="vpx-coach-chips">${chips}</div>
            </div>
        `;
    };

    function bindCoachEvents(data) {

        document.querySelectorAll("[data-coach-question]").forEach(function (chip) {
            chip.addEventListener("click", function () {
                askCoachQuestion(chip.getAttribute("data-coach-question"), data);
            });
        });

        document.querySelectorAll("[data-coach-send]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const prefix = btn.getAttribute("data-coach-send");
                const input = document.getElementById(prefix + "-input");
                if (input && input.value.trim()) {
                    askCoachFreeText(input.value, data);
                    input.value = "";
                }
            });
        });

        document.querySelectorAll(".vpx-coach-input").forEach(function (input) {
            input.addEventListener("keydown", function (e) {
                if (e.key === "Enter" && input.value.trim()) {
                    askCoachFreeText(input.value, data);
                    input.value = "";
                }
            });
        });

        document.querySelectorAll("[data-coach-clear]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                coachConversation = [];
                Renderer.render();
            });
        });

        document.querySelectorAll("[data-coach-toggle]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                coachWidgetOpen = !coachWidgetOpen;
                Renderer.render();
            });
        });

        ["vpx-coach-page-thread", "vpx-coach-widget-thread"].forEach(function (id) {
            const thread = document.getElementById(id);
            if (thread) thread.scrollTop = thread.scrollHeight;
        });
    }

    /*
    ==========================================
    Profile Page
    ------------------------------------------
    Edit personal stats (NOT goal/pace — that
    lives on the future Goals page) without
    redoing the full onboarding wizard. Saving
    merges changes into the existing answers and
    fully recalculates the blueprint, same engine
    call the wizard itself uses.
    ==========================================
    */

    let profileSavedMessage = false;

    Renderer.viewProfile = function () {

        const onboardingSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("onboarding") : null;
        const a = (onboardingSection && onboardingSection.answers) ? onboardingSection.answers : {};

        const scheduleSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("trainingSchedule") : null;
        const liveTrainingDaysCount = (scheduleSection && scheduleSection.days) ? scheduleSection.days.length : 0;

        const savedBanner = profileSavedMessage
            ? `<div class="vpx-profile-saved">${t("profile_saved_message")}</div>`
            : "";

        return `
            ${buildAvatarHeroWidget()}
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_profile_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:20px;">${t("page_profile_hint")}</p>

                    ${savedBanner}

                    <div style="margin-bottom:18px;">
                        <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_gender")}</div>
                        <div class="vpx-onb-options vpx-onb-options-2">
                            <button type="button" class="vpx-onb-option${a.gender === "male" ? " is-active" : ""}" data-profile-field="gender" data-profile-value="male">♂ <span>${t("onb_gender_male")}</span></button>
                            <button type="button" class="vpx-onb-option${a.gender === "female" ? " is-active" : ""}" data-profile-field="gender" data-profile-value="female">♀ <span>${t("onb_gender_female")}</span></button>
                        </div>
                    </div>

                    <div class="vpx-onb-form-grid" style="margin-bottom:18px;">
                        <label class="vpx-onb-field">
                            <span>${t("onb_field_age")}</span>
                            <input type="number" data-profile-input="age" min="${FIELD_LIMITS.age.min}" max="${FIELD_LIMITS.age.max}" value="${a.age || ""}" />
                            <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.age.hintKey)}</small>
                        </label>
                        <label class="vpx-onb-field">
                            <span>${t("onb_field_height")}</span>
                            <input type="number" data-profile-input="heightCm" min="${FIELD_LIMITS.heightCm.min}" max="${FIELD_LIMITS.heightCm.max}" value="${a.heightCm || ""}" />
                            <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.heightCm.hintKey)}</small>
                        </label>
                        <label class="vpx-onb-field">
                            <span>${t("onb_field_weight")}</span>
                            <input type="number" data-profile-input="weightKg" min="${FIELD_LIMITS.weightKg.min}" max="${FIELD_LIMITS.weightKg.max}" value="${a.weightKg || ""}" />
                            <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.weightKg.hintKey)}</small>
                        </label>
                    </div>

                    <div style="margin-bottom:18px;">
                        <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_bodyfat")}</div>
                        <input type="range" min="5" max="50" step="0.5" data-profile-input="bodyFatPct" value="${a.bodyFatPct !== null && a.bodyFatPct !== undefined ? a.bodyFatPct : 20}" style="width:100%; accent-color:var(--vpx-gold);" />
                        <div style="font-size:11px; color:var(--vpx-muted); margin-top:4px;" id="vpx-profile-bodyfat-value">${a.bodyFatPct !== null && a.bodyFatPct !== undefined ? a.bodyFatPct + "%" : t("onb_bodyfat_unknown")}</div>
                    </div>

                    <div class="vpx-onb-form-grid" style="margin-bottom:18px;">
                        <label class="vpx-onb-field">
                            <span>${t("onb_measurements_neck")}</span>
                            <input type="number" step="0.5" data-profile-input="neckCm" min="${FIELD_LIMITS.neckCm.min}" max="${FIELD_LIMITS.neckCm.max}" value="${a.neckCm || ""}" />
                        </label>
                        <label class="vpx-onb-field">
                            <span>${t("onb_measurements_waist")}</span>
                            <input type="number" step="0.5" data-profile-input="waistCm" min="${FIELD_LIMITS.waistCm.min}" max="${FIELD_LIMITS.waistCm.max}" value="${a.waistCm || ""}" />
                        </label>
                        ${a.gender === "female" ? `
                        <label class="vpx-onb-field">
                            <span>${t("onb_measurements_hip")}</span>
                            <input type="number" step="0.5" data-profile-input="hipCm" min="${FIELD_LIMITS.hipCm.min}" max="${FIELD_LIMITS.hipCm.max}" value="${a.hipCm || ""}" />
                        </label>
                        ` : ""}
                    </div>

                    <div style="margin-bottom:18px;">
                        <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_lifestyle")}</div>
                        <div class="vpx-onb-options vpx-onb-options-col">
                            <button type="button" class="vpx-onb-option${a.jobType === "sedentary" ? " is-active" : ""}" data-profile-field="jobType" data-profile-value="sedentary">🪑 <span>${t("onb_job_sedentary")}</span></button>
                            <button type="button" class="vpx-onb-option${a.jobType === "standing" ? " is-active" : ""}" data-profile-field="jobType" data-profile-value="standing">🚶 <span>${t("onb_job_standing")}</span></button>
                            <button type="button" class="vpx-onb-option${a.jobType === "active" ? " is-active" : ""}" data-profile-field="jobType" data-profile-value="active">🏗️ <span>${t("onb_job_active")}</span></button>
                        </div>
                    </div>

                    <div class="vpx-onb-form-grid" style="margin-bottom:18px;">
                        <label class="vpx-onb-field">
                            <span>${t("onb_field_trainingDays")}</span>
                            <div class="vpx-onb-info-box" style="margin:0;">
                                ${t("profile_trainingDays_readonly", { count: liveTrainingDaysCount })}
                                <button type="button" class="vpx-btn-ghost" style="margin-top:10px; display:block;" data-vpx-nav="schedule">${t("profile_trainingDays_goto_schedule")}</button>
                            </div>
                        </label>
                        <label class="vpx-onb-field">
                            <span>${t("onb_field_sleepHours")}</span>
                            <input type="number" step="0.5" data-profile-input="sleepHours" min="${FIELD_LIMITS.sleepHours.min}" max="${FIELD_LIMITS.sleepHours.max}" value="${a.sleepHours || ""}" />
                            <small class="vpx-onb-field-hint">${t(FIELD_LIMITS.sleepHours.hintKey)}</small>
                        </label>
                    </div>

                    <div style="margin-bottom:24px;">
                        <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_stress")}</div>
                        <div class="vpx-onb-options vpx-onb-options-row5">
                            ${[1, 2, 3, 4, 5].map(function (n) {
                                return `<button type="button" class="vpx-onb-option${String(a.stressLevel) === String(n) ? " is-active" : ""}" data-profile-field="stressLevel" data-profile-value="${n}"><span>${t("onb_stress_" + n)}</span></button>`;
                            }).join("")}
                        </div>
                    </div>

                    <button type="button" class="vpx-btn-gold" data-profile-action="save">${t("btn_saveChanges")}</button>
                    <button type="button" class="vpx-btn-ghost" style="margin-left:12px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function bindProfileEvents() {

        const app = document.getElementById("vpx-app");
        if (!app) return;

        const workingAnswers = {};

        app.querySelectorAll("[data-profile-field]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const field = btn.getAttribute("data-profile-field");
                const value = btn.getAttribute("data-profile-value");
                workingAnswers[field] = value;

                const group = btn.parentElement;
                if (group) {
                    group.querySelectorAll("[data-profile-field]").forEach(function (sib) {
                        sib.classList.remove("is-active");
                    });
                }
                btn.classList.add("is-active");
            });
        });

        app.querySelectorAll('input[data-profile-input]').forEach(function (input) {
            input.addEventListener("input", function () {
                const field = input.getAttribute("data-profile-input");
                workingAnswers[field] = input.value === "" ? null : Number(input.value);

                if (field === "bodyFatPct") {
                    const label = document.getElementById("vpx-profile-bodyfat-value");
                    if (label) label.textContent = input.value + "%";
                }
            });
        });

        const saveBtn = app.querySelector('[data-profile-action="save"]');
        if (saveBtn) {
            saveBtn.addEventListener("click", function () {

                if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

                const onboardingSection = window.VORYNEX.state.getSection("onboarding");
                const currentAnswers = (onboardingSection && onboardingSection.answers) ? onboardingSection.answers : {};

                // Merge: only fields actually touched on this page override
                // the existing answers — goal/pace/programWeeks untouched.
                const updatedAnswers = Object.assign({}, currentAnswers, workingAnswers);

                if (!window.VORYNEX.engine || !window.VORYNEX.engine.calculate) return;

                const result = window.VORYNEX.engine.calculate(updatedAnswers);

                window.VORYNEX.state.setSection("blueprint", result);
                window.VORYNEX.state.setSection("onboarding", { completed: true, answers: updatedAnswers });
                window.VORYNEX.state.setSection("trainingSchedule", { days: updatedAnswers.trainingDays || [] });

                profileSavedMessage = true;
                Renderer.render();

                setTimeout(function () {
                    profileSavedMessage = false;
                }, 3000);
            });
        }
    }

    /*
    ==========================================
    Goals Page
    ------------------------------------------
    Edit goal / pace / program duration without
    redoing the full onboarding wizard — same
    pattern as the Profile page. Pace + Duration
    are only shown when the SAVED goal isn't
    "maintenance" (mirrors the wizard's own
    skipIf logic for the pace step); this reads
    the saved goal rather than reacting live to
    an in-progress click, same limitation the
    Profile page already accepts for its
    gender→hip-field visibility. Saving merges
    the touched fields into the existing answers
    and fully recalculates the blueprint through
    the same engine.calculate() call the wizard
    and Profile page both use — which is also
    what keeps Training Program in sync, since
    ensureWorkoutPlan() reads goal live from
    onboarding.answers on every render.
    ==========================================
    */

    let goalsSavedMessage = false;

    function goalsOptionGroup(name, options, selected) {
        return options.map(function (opt) {
            const activeClass = selected === opt.value ? " is-active" : "";
            return `
                <button type="button" class="vpx-onb-option${activeClass}" data-goals-field="${name}" data-goals-value="${opt.value}">
                    ${opt.icon ? `<span class="vpx-onb-option-icon">${opt.icon}</span>` : ""}
                    <span>${opt.label}</span>
                </button>
            `;
        }).join("");
    }

    /*
    ==========================================
    Avatar Customization Screen
    ==========================================
    */

    const AVATAR_OPTION_KEYS = {
        gender: { male: "avatar_gender_male", female: "avatar_gender_female" },
        hairStyle: { short: "avatar_hair_short", long: "avatar_hair_long", bun: "avatar_hair_bun", bald: "avatar_hair_bald", curly: "avatar_hair_curly", ponytail: "avatar_hair_ponytail" },
        outfitType: { tshirt_shorts: "avatar_outfit_tshirt", tank_shorts: "avatar_outfit_tank", hoodie: "avatar_outfit_hoodie", tracksuit: "avatar_outfit_tracksuit", leggings: "avatar_outfit_leggings" },
        accessory: { none: "avatar_accessory_none", glasses: "avatar_accessory_glasses", cap: "avatar_accessory_cap", headphones: "avatar_accessory_headphones", gymbag: "avatar_accessory_gymbag", shaker: "avatar_accessory_shaker" }
    };

    const SKIN_TONE_HEX = { tone1: "#f4d0ae", tone2: "#e2b28a", tone3: "#c68a5f", tone4: "#9c6539", tone5: "#5c3a22" };

    const HAIR_COLOR_PRESETS = ["#0a0a0a", "#2b2118", "#5c3a22", "#8b5a2b", "#c9a227", "#a83232", "#c9c9c9", "#3a6ea5", "#8a3aa5"];

    function lockedLegend(field, options) {
        const lines = options
            .filter(function (opt) { return !window.VORYNEX.avatar.isUnlocked(field, opt); })
            .map(function (opt) {
                const req = window.VORYNEX.avatar.getUnlockRequirement(field, opt);
                const label = t(AVATAR_OPTION_KEYS[field][opt]);
                const reqLabel = req ? (t("ach_tier_" + req.tier) + " · " + t("ach_cat_" + req.category)) : "";
                return `<div class="vpx-avatar-lock-row">🔒 ${label} — ${reqLabel}</div>`;
            }).join("");
        return lines ? `<div class="vpx-avatar-lock-legend">${lines}</div>` : "";
    }

    function optionButtonGroup(field, options, current) {
        return options.map(function (opt) {
            const activeClass = opt === current ? " is-active" : "";
            const label = t(AVATAR_OPTION_KEYS[field][opt]);
            const unlocked = window.VORYNEX.avatar.isUnlocked(field, opt);

            if (!unlocked) {
                const req = window.VORYNEX.avatar.getUnlockRequirement(field, opt);
                const reqLabel = req ? (t("ach_tier_" + req.tier) + " · " + t("ach_cat_" + req.category)) : "";
                return `<button type="button" class="vpx-avatar-opt-btn is-locked" disabled title="${t("avatar_locked_tooltip", { requirement: reqLabel })}">🔒 ${label}</button>`;
            }

            return `<button type="button" class="vpx-avatar-opt-btn${activeClass}" data-vpx-avatar-field="${field}" data-vpx-avatar-value="${opt}">${label}</button>`;
        }).join("");
    }

    Renderer.viewAvatarCustomize = function () {

        if (!window.VORYNEX.avatar) return Renderer.viewComingSoon();

        if (!avatarDraft) avatarDraft = window.VORYNEX.avatar.getConfig();
        const opts = window.VORYNEX.avatar.getOptions();
        const previewSvg = window.VORYNEX.avatar.render(avatarDraft);

        const skinSwatches = opts.skinTones.map(function (tone) {
            const activeClass = tone === avatarDraft.skinTone ? " is-active" : "";
            return `<button type="button" class="vpx-avatar-swatch${activeClass}" style="background:${SKIN_TONE_HEX[tone]};" data-vpx-avatar-field="skinTone" data-vpx-avatar-value="${tone}"></button>`;
        }).join("");

        return `
            <div class="vpx-grid">
                <div class="vpx-card vpx-avatar-customize-card">

                    <h2>${t("avatar_customize_title")}</h2>
                    <p class="vpx-avatar-locked-hint">${t("avatar_customize_locked_hint")}</p>

                    <div class="vpx-avatar-customize-preview">${previewSvg}</div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_gender")}</div>
                        <div class="vpx-avatar-opt-row">${optionButtonGroup("gender", opts.genders, avatarDraft.gender)}</div>
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_skin")}</div>
                        <div class="vpx-avatar-opt-row">${skinSwatches}</div>
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_hairstyle")}</div>
                        <div class="vpx-avatar-opt-row">${optionButtonGroup("hairStyle", opts.hairStyles, avatarDraft.hairStyle)}</div>
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_haircolor")}</div>
                        <div class="vpx-avatar-opt-row">
                            ${HAIR_COLOR_PRESETS.map(function (hex) {
                                const activeClass = hex.toLowerCase() === (avatarDraft.hairColor || "").toLowerCase() ? " is-active" : "";
                                return `<button type="button" class="vpx-avatar-swatch${activeClass}" style="background:${hex};" data-vpx-avatar-field="hairColor" data-vpx-avatar-value="${hex}"></button>`;
                            }).join("")}
                            <input type="color" class="vpx-avatar-color-picker" id="vpx-avatar-haircolor" value="${avatarDraft.hairColor}" title="${t("avatar_customize_haircolor_custom")}">
                        </div>
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_outfit")}</div>
                        <div class="vpx-avatar-opt-row">${optionButtonGroup("outfitType", opts.outfitTypes, avatarDraft.outfitType)}</div>
                        ${lockedLegend("outfitType", opts.outfitTypes)}
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_outfitcolor")}</div>
                        <input type="color" class="vpx-avatar-color-picker" id="vpx-avatar-outfitcolor" value="${avatarDraft.outfitColor}">
                    </div>

                    <div class="vpx-avatar-opt-section">
                        <div class="vpx-avatar-opt-label">${t("avatar_customize_accessory")}</div>
                        <div class="vpx-avatar-opt-row">${optionButtonGroup("accessory", opts.accessories, avatarDraft.accessory)}</div>
                        ${lockedLegend("accessory", opts.accessories)}
                    </div>

                    <div class="vpx-avatar-customize-actions">
                        <button type="button" class="vpx-btn-outline" data-vpx-nav="profile" data-vpx-avatar-cancel="1">${t("avatar_customize_back")}</button>
                        <button type="button" class="vpx-btn-gold" data-vpx-action="save-avatar">${t("avatar_customize_save")}</button>
                    </div>

                </div>
            </div>
        `;
    };

    function updateAvatarPreviewLive() {
        const previewEl = document.querySelector(".vpx-avatar-customize-preview");
        if (previewEl && window.VORYNEX.avatar) {
            previewEl.innerHTML = window.VORYNEX.avatar.render(avatarDraft);
        }
    }

    function bindAvatarEvents() {

        if (!window.VORYNEX.avatar) return;

        document.querySelectorAll("[data-vpx-avatar-field]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const field = btn.getAttribute("data-vpx-avatar-field");
                const value = btn.getAttribute("data-vpx-avatar-value");
                if (!avatarDraft) avatarDraft = window.VORYNEX.avatar.getConfig();
                avatarDraft[field] = value;
                Renderer.render();
            });
        });

        const hairColorInput = document.getElementById("vpx-avatar-haircolor");
        if (hairColorInput) {
            hairColorInput.addEventListener("input", function () {
                if (!avatarDraft) avatarDraft = window.VORYNEX.avatar.getConfig();
                avatarDraft.hairColor = hairColorInput.value;
                updateAvatarPreviewLive();
            });
            hairColorInput.addEventListener("change", function () {
                Renderer.render();
            });
        }

        const outfitColorInput = document.getElementById("vpx-avatar-outfitcolor");
        if (outfitColorInput) {
            outfitColorInput.addEventListener("input", function () {
                if (!avatarDraft) avatarDraft = window.VORYNEX.avatar.getConfig();
                avatarDraft.outfitColor = outfitColorInput.value;
                updateAvatarPreviewLive();
            });
            outfitColorInput.addEventListener("change", function () {
                Renderer.render();
            });
        }

        const cancelBtn = document.querySelector("[data-vpx-avatar-cancel]");
        if (cancelBtn) {
            cancelBtn.addEventListener("click", function () { avatarDraft = null; });
        }

        const saveBtn = document.querySelector('[data-vpx-action="save-avatar"]');
        if (saveBtn) {
            saveBtn.addEventListener("click", function () {
                window.VORYNEX.avatar.setConfig(avatarDraft);
                avatarDraft = null;
                currentView = "profile";
                Renderer.render();
            });
        }
    }

    /*
    ==========================================
    Share Card Generator
    ------------------------------------------
    Renders avatar + streak + best badge onto a
    1080x1920 canvas (Instagram Story ratio),
    then either hands off to the native share
    sheet (mobile, when available) or falls back
    to a plain PNG download (desktop). 100%
    client-side — no server round-trip, no
    external image service.
    ==========================================
    */

    function bindShareAchievementEvents() {
        const btn = document.querySelector('[data-vpx-action="share-achievement"]');
        if (!btn) return;
        btn.addEventListener("click", generateShareCard);
    }

    function generateShareCard() {

        if (!window.VORYNEX.avatar || !window.VORYNEX.achievements) return;

        const svgString = window.VORYNEX.avatar.render();
        const achResult = window.VORYNEX.achievements.evaluate();
        const streak = computeExerciseStreak();

        const svgBlob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(svgBlob);
        const img = new Image();

        img.onload = function () {

            const canvas = document.createElement("canvas");
            canvas.width = 1080;
            canvas.height = 1920;
            const ctx = canvas.getContext("2d");

            ctx.fillStyle = "#141414";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.fillStyle = "#c9a227";
            ctx.font = "bold 48px sans-serif";
            ctx.textAlign = "center";
            ctx.fillText("VORYNEX", canvas.width / 2, 150);

            const avatarW = 500, avatarH = 650;
            ctx.drawImage(img, (canvas.width - avatarW) / 2, 300, avatarW, avatarH);
            URL.revokeObjectURL(url);

            let bestIcon = "";
            achResult.categories.forEach(function (c) {
                if (c.tierName) bestIcon = window.VORYNEX.achievements.getTierIcon(c.tierName);
            });
            if (achResult.capstoneUnlocked) bestIcon = achResult.capstoneIcon;

            if (bestIcon) {
                ctx.font = "130px sans-serif";
                ctx.fillText(bestIcon, canvas.width / 2, 1150);
            }

            if (streak >= 1) {
                ctx.fillStyle = "#f5f5f5";
                ctx.font = "bold 64px sans-serif";
                const streakLabel = streak + " " + t(streak === 1 ? "streak_day_singular" : "streak_day_plural");
                ctx.fillText(streakLabel, canvas.width / 2, 1320);
            }

            ctx.font = "34px sans-serif";
            ctx.fillStyle = "#9a9a9a";
            ctx.fillText(t("share_card_tagline"), canvas.width / 2, 1400);

            ctx.font = "28px sans-serif";
            ctx.fillStyle = "#666";
            ctx.fillText("vorynexus.com", canvas.width / 2, 1850);

            canvas.toBlob(function (blob) {
                if (!blob) return;
                const file = new File([blob], "vorynex-achievement.png", { type: "image/png" });

                if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                    navigator.share({ files: [file], title: "VORYNEX Blueprint" }).catch(function () {});
                } else {
                    const link = document.createElement("a");
                    link.href = URL.createObjectURL(blob);
                    link.download = "vorynex-achievement.png";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }
            }, "image/png");
        };

        img.onerror = function () {
            URL.revokeObjectURL(url);
            console.warn("VORYNEX: share card image failed to load.");
        };

        img.src = url;
    }

    Renderer.viewAchievements = function () {

        if (!window.VORYNEX.achievements) return Renderer.viewComingSoon();

        const result = window.VORYNEX.achievements.evaluate();

        const categoryCards = result.categories.map(function (cat) {
            const tierBadges = ["bronze", "silver", "gold", "diamond"].map(function (tierName, i) {
                const unlocked = cat.tiersUnlocked.indexOf(tierName) !== -1;
                const icon = window.VORYNEX.achievements.getTierIcon(tierName);
                const stateClass = unlocked ? " is-unlocked" : " is-locked";
                return `<div class="vpx-ach-tier-badge${stateClass}" title="${t("ach_tier_" + tierName)}">${icon}</div>`;
            }).join("");

            const nextLine = cat.nextThreshold !== null
                ? `<div class="vpx-ach-next">${t("ach_progress_next", { value: cat.value, target: cat.nextThreshold })}</div>`
                : `<div class="vpx-ach-next vpx-ach-maxed">${t("ach_progress_maxed")}</div>`;

            const rarityPct = cat.tierName ? window.VORYNEX.achievements.getRarity(cat.id + "_" + cat.tierName) : null;
            const rarityLine = rarityPct !== null ? `<div class="vpx-ach-rarity">${t("ach_rarity_label", { pct: rarityPct })}</div>` : "";

            return `
                <div class="vpx-card vpx-ach-card">
                    <div class="vpx-ach-card-header">
                        <span class="vpx-ach-card-icon">${cat.icon}</span>
                        <span class="vpx-ach-card-name">${t(cat.nameKey)}</span>
                    </div>
                    <div class="vpx-ach-tier-row">${tierBadges}</div>
                    ${rarityLine}
                    ${nextLine}
                </div>
            `;
        }).join("");

        const capstoneClass = result.capstoneUnlocked ? " is-unlocked" : " is-locked";

        return `
            <div class="vpx-grid">
                <div class="vpx-card vpx-ach-capstone-card${capstoneClass}">
                    <div class="vpx-ach-capstone-icon">${result.capstoneIcon}</div>
                    <div class="vpx-ach-capstone-name">${t("ach_capstone_name")}</div>
                    <div class="vpx-ach-capstone-desc">${t("ach_capstone_desc")}</div>
                </div>
            </div>
            <div class="vpx-grid vpx-ach-grid">
                ${categoryCards}
            </div>
        `;
    };

    Renderer.viewGoals = function () {

        const onboardingSection = (window.VORYNEX.state && window.VORYNEX.state.getSection)
            ? window.VORYNEX.state.getSection("onboarding") : null;
        const a = (onboardingSection && onboardingSection.answers) ? onboardingSection.answers : {};

        const savedBanner = goalsSavedMessage
            ? `<div class="vpx-profile-saved">${t("goals_saved_message")}</div>`
            : "";

        const isGain = a.goal === "muscle_gain";
        const isMaintenance = a.goal === "maintenance";

        const paceOptions = isGain
            ? [
                { value: "slow", label: t("onb_pace_slow_gain"), icon: "🐢" },
                { value: "moderate", label: t("onb_pace_moderate_gain"), icon: "🚶" },
                { value: "aggressive", label: t("onb_pace_aggressive_gain"), icon: "🏃" }
            ]
            : [
                { value: "slow", label: t("onb_pace_slow_loss"), icon: "🐢" },
                { value: "moderate", label: t("onb_pace_moderate_loss"), icon: "🚶" },
                { value: "aggressive", label: t("onb_pace_aggressive_loss"), icon: "🏃" }
            ];

        const durationOptions = [
            { value: "4", label: t("onb_duration_4") },
            { value: "8", label: t("onb_duration_8") },
            { value: "12", label: t("onb_duration_12") },
            { value: "24", label: t("onb_duration_24") }
        ];

        const paceDurationBlock = isMaintenance ? "" : `
            <div style="margin-bottom:18px;">
                <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_pace")}</div>
                <div class="vpx-onb-options vpx-onb-options-col">
                    ${goalsOptionGroup("paceLevel", paceOptions, a.paceLevel)}
                </div>
                <div class="vpx-onb-slider-hint" style="margin-top:10px;">${t("onb_pace_hint")}</div>
            </div>

            <div style="margin-bottom:24px;">
                <div style="font-size:11px; color:var(--vpx-muted); text-transform:uppercase; letter-spacing:1px; margin-bottom:10px;">
                    ${t("onb_duration_label")}
                </div>
                <div class="vpx-onb-options vpx-onb-options-row5">
                    ${goalsOptionGroup("programWeeks", durationOptions, a.programWeeks)}
                </div>
            </div>
        `;

        return `
            <div class="vpx-grid">
                <div class="vpx-card">
                    <h2>${t("page_goals_title")}</h2>
                    <p style="font-size:12px; color:var(--vpx-muted); margin-bottom:20px;">${t("page_goals_hint")}</p>

                    ${savedBanner}

                    <div style="margin-bottom:18px;">
                        <div style="font-size:12px; color:var(--vpx-white); margin-bottom:8px;">${t("onb_step_goal")}</div>
                        <div class="vpx-onb-options vpx-onb-options-col">
                            ${goalsOptionGroup("goal", [
                                { value: "fat_loss", label: t("onb_goal_fatLoss"), icon: "🔥" },
                                { value: "maintenance", label: t("onb_goal_maintenance"), icon: "⚖️" },
                                { value: "muscle_gain", label: t("onb_goal_muscleGain"), icon: "💪" }
                            ], a.goal)}
                        </div>
                    </div>

                    ${paceDurationBlock}

                    <button type="button" class="vpx-btn-gold" data-goals-action="save">${t("btn_saveChanges")}</button>
                    <button type="button" class="vpx-btn-ghost" style="margin-left:12px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    function bindGoalsEvents() {

        const app = document.getElementById("vpx-app");
        if (!app) return;

        const workingAnswers = {};

        app.querySelectorAll("[data-goals-field]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                const field = btn.getAttribute("data-goals-field");
                const value = btn.getAttribute("data-goals-value");
                workingAnswers[field] = value;

                const group = btn.parentElement;
                if (group) {
                    group.querySelectorAll("[data-goals-field]").forEach(function (sib) {
                        sib.classList.remove("is-active");
                    });
                }
                btn.classList.add("is-active");
            });
        });

        const saveBtn = app.querySelector('[data-goals-action="save"]');
        if (saveBtn) {
            saveBtn.addEventListener("click", function () {

                if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return;

                const onboardingSection = window.VORYNEX.state.getSection("onboarding");
                const currentAnswers = (onboardingSection && onboardingSection.answers) ? onboardingSection.answers : {};

                // Merge: only fields actually touched on this page override
                // the existing answers — personal stats untouched.
                const updatedAnswers = Object.assign({}, currentAnswers, workingAnswers);

                // Maintenance carries no pace/duration — clear any stale
                // values left over from a previous fat_loss/muscle_gain
                // selection so the engine never sees a mismatched pair.
                if (updatedAnswers.goal === "maintenance") {
                    updatedAnswers.paceLevel = null;
                    updatedAnswers.programWeeks = null;
                }

                if (!window.VORYNEX.engine || !window.VORYNEX.engine.calculate) return;

                const result = window.VORYNEX.engine.calculate(updatedAnswers);

                window.VORYNEX.state.setSection("blueprint", result);
                window.VORYNEX.state.setSection("onboarding", { completed: true, answers: updatedAnswers });

                goalsSavedMessage = true;
                Renderer.render();

                setTimeout(function () {
                    goalsSavedMessage = false;
                }, 3000);
            });
        }
    }

    /*
    ==========================================
    EmailJS Configuration
    ------------------------------------------
    Sends the personalized Blueprint by email
    IMMEDIATELY when the person submits their
    email — no backend, no queue/delay. Requires
    a free EmailJS account (emailjs.com):
      1) Email Services → connect Gmail/Outlook
      2) Email Templates → create a template
         using variables: {{to_email}},
         {{calories}}, {{protein}}, {{carbs}},
         {{fat}}, {{water}}, {{goal_phase}},
         {{current_weight}}, {{target_weight}},
         {{weeks_remaining}}, {{site_url}}
      3) Replace the 3 placeholders below with
         your real Service ID / Template ID /
         Public Key from the EmailJS dashboard.
    Until replaced, sending is skipped silently
    (console warning only) — the Shopify lead
    tag below still works either way.
    ==========================================
    */

    const EMAILJS_SERVICE_ID = "service_ms3dnzc";
    const EMAILJS_TEMPLATE_ID = "template_4uc8wf9";
    const EMAILJS_PUBLIC_KEY = "qWs-SN1OKfGrdG_Wn";

    const EMAILJS_CONFIGURED = EMAILJS_SERVICE_ID !== "YOUR_SERVICE_ID"
        && EMAILJS_TEMPLATE_ID !== "YOUR_TEMPLATE_ID"
        && EMAILJS_PUBLIC_KEY !== "YOUR_PUBLIC_KEY";

    /*
    Loaded dynamically from HERE (not from the .liquid file) so the
    whole EmailJS setup lives in this one file — the same file you
    already know how to redeploy (delete + re-create in Shopify
    Assets). No dependency on a separate <script> tag surviving a
    theme edit/publish.

    IMPORTANT: init() must be called EXACTLY once no matter who
    loaded the SDK script (this code, a leftover <script> tag in the
    .liquid file, or anything else on the page) — otherwise send()
    fails with "the public key is required" even though the SDK
    itself is present. `emailjsInitialized` guards against skipping
    that call when window.emailjs already exists from elsewhere.
    */
    let emailjsReadyPromise = null;
    let emailjsInitialized = false;

    function ensureEmailJSInitialized(emailjsObj) {
        if (!emailjsInitialized && emailjsObj && typeof emailjsObj.init === "function") {
            emailjsObj.init({ publicKey: EMAILJS_PUBLIC_KEY });
            emailjsInitialized = true;
            console.log("VORYNEX: EmailJS initialized with public key", EMAILJS_PUBLIC_KEY);
        }
        return emailjsObj;
    }

    function loadEmailJSSdk() {

        // If a real, working SDK is already sitting on window (loaded
        // earlier — by this code OR by anything else on the page,
        // e.g. a leftover script tag), use it — but ALWAYS make sure
        // init() has actually run before handing it back.
        if (window.emailjs && typeof window.emailjs.send === "function") {
            return Promise.resolve(ensureEmailJSInitialized(window.emailjs));
        }

        if (emailjsReadyPromise) return emailjsReadyPromise;

        emailjsReadyPromise = new Promise(function (resolve, reject) {

            const existing = document.getElementById("vpx-emailjs-sdk") || document.querySelector('script[src*="emailjs"]');
            if (existing) {
                existing.addEventListener("load", function () {
                    if (window.emailjs && typeof window.emailjs.send === "function") {
                        resolve(ensureEmailJSInitialized(window.emailjs));
                    } else {
                        reject(new Error("EmailJS script tag loaded, but window.emailjs.send isn't a function."));
                    }
                });
                existing.addEventListener("error", function () { reject(new Error("EmailJS SDK script failed to load (existing tag).")); });
                return;
            }

            const script = document.createElement("script");
            script.id = "vpx-emailjs-sdk";
            script.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
            script.onload = function () {
                if (window.emailjs && typeof window.emailjs.init === "function" && typeof window.emailjs.send === "function") {
                    resolve(ensureEmailJSInitialized(window.emailjs));
                } else {
                    reject(new Error("EmailJS script loaded but window.emailjs is missing send/init — unexpected SDK shape (possibly overwritten by another script on this page)."));
                }
            };
            script.onerror = function () {
                reject(new Error("Failed to load the EmailJS SDK script from the CDN (network/ad-blocker/CSP?)."));
            };
            document.head.appendChild(script);
        });

        // If this attempt fails, don't poison all future attempts —
        // clear the cache so the NEXT send() retries loading from
        // scratch instead of replaying the same rejection forever.
        emailjsReadyPromise = emailjsReadyPromise.catch(function (error) {
            emailjsReadyPromise = null;
            throw error;
        });

        return emailjsReadyPromise;
    }

    if (EMAILJS_CONFIGURED) {
        loadEmailJSSdk().catch(function (e) {
            console.error("VORYNEX: EmailJS SDK failed to load —", e);
        });
    } else {
        console.warn("VORYNEX: EmailJS not configured (placeholder IDs still in vpx-render.js) — personalized email sending is disabled.");
    }

    function buildEmailTemplateParams(email) {

        const data = getData();

        // Every piece of TEXT (not just numbers) is sent as a template
        // variable, sourced from t() — which resolves to whatever
        // language the person currently has selected on the site
        // (EL/EN/DE). The EmailJS template itself must use {{these}}
        // instead of hardcoded words for this to work — see the
        // updated template HTML (multi-language version).
        return {
            to_email: email,
            subject: t("email_subject"),
            greeting: t("email_greeting"),

            section_targets_title: t("page_reports_section_targets"),
            label_calories: t("stat_calories"),
            label_protein: t("stat_protein"),
            label_carbs: t("stat_carbs"),
            label_fat: t("stat_fat"),
            label_water: t("stat_water"),

            section_goal_title: t("page_reports_section_goal"),
            label_goal: t("onb_review_goal"),
            weeks_label: t("goal_weeks"),

            cta_text: t("email_cta_view_plan"),
            disclaimer: t("page_reports_disclaimer"),

            calories: data.targets.calories,
            protein: data.targets.protein,
            carbs: data.targets.carbs,
            fat: data.targets.fat,
            water: data.targets.water,
            goal_phase: t(data.goal.phaseKey),
            current_weight: data.goal.currentWeight,
            target_weight: data.goal.targetWeight,
            weeks_remaining: data.goal.weeksRemaining,
            site_url: window.location.href
        };
    }

    function sendPersonalizedEmail(email) {
        if (!EMAILJS_CONFIGURED) {
            console.warn("VORYNEX: EmailJS isn't configured yet (still has placeholder IDs) — skipping the actual email send. The lead is still captured in Shopify. See the EMAILJS_* constants in vpx-render.js.");
            return Promise.resolve();
        }
        return loadEmailJSSdk().then(function (emailjs) {
            return emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, buildEmailTemplateParams(email));
        })
            .then(function (response) {
                console.log("VORYNEX: EmailJS send SUCCEEDED —", response);
                return response;
            })
            .catch(function (error) {
                console.error("VORYNEX: EmailJS send FAILED —", error);
                throw error;
            });
    }

    /*
    ==========================================
    Lead Capture (Email banner + Reports gate)
    ------------------------------------------
    No backend exists (see project notes) — the
    email is posted directly to Shopify's own
    native customer/newsletter endpoint, the
    same one Shopify's built-in footer signup
    form uses, so captured emails land in the
    merchant's real Shopify Customers list with
    marketing consent — no app/backend needed.
    That endpoint doesn't return structured JSON
    on success, so the UI treats the request as
    fire-and-forget: it optimistically shows
    "subscribed" once the request is sent without
    a network error. Good enough for a lead form,
    NOT something to build billing/entitlements
    logic on top of.
    Both the Dashboard banner and the Reports
    gate share the same "leadCapture" state
    section, so giving an email on either surface
    satisfies both.
    ==========================================
    */

    function isValidEmail(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());
    }

    function submitLeadEmail(email) {
        return fetch("/contact#ContactFooter", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: new URLSearchParams({
                form_type: "customer",
                utf8: "✓",
                "contact[email]": email,
                "contact[tags]": "vpx-blueprint-lead"
            })
        });
    }

    /*
    ==========================================
    Toast Notifications
    ------------------------------------------
    A single fixed stack element (created once,
    reused) so multiple toasts queue vertically
    instead of overlapping. Auto-dismisses after
    3s; the fade-out class matches the CSS
    animation duration before removal.
    ==========================================
    */

    function showToast(message, type) {

        let stack = document.querySelector(".vpx-toast-stack");
        if (!stack) {
            stack = document.createElement("div");
            stack.className = "vpx-toast-stack";
            document.body.appendChild(stack);
        }

        const toast = document.createElement("div");
        toast.className = "vpx-toast" + (type === "error" ? " vpx-toast-error" : "");
        toast.textContent = message;
        stack.appendChild(toast);

        setTimeout(function () {
            toast.classList.add("vpx-toast-leaving");
            setTimeout(function () { toast.remove(); }, 250);
        }, 3000);
    }

    function getLeadCapture() {
        const fallback = { email: null, subscribed: false, dismissed: false };
        if (!window.VORYNEX.state || !window.VORYNEX.state.getSection) return fallback;
        return window.VORYNEX.state.getSection("leadCapture") || fallback;
    }

    let leadCaptureError = false;

    // True only during the SAME session, right after a successful
    // submit/resend — used to show a one-time "sent! check spam too"
    // confirmation instead of just silently hiding the form. Not
    // persisted: a page reload clears it, which is fine since it's
    // just a courtesy heads-up, not a permanent state flag.
    let leadJustSubscribed = false;

    /*
    ==========================================
    Account Sync Banner
    ------------------------------------------
    Shown only when the shopper is NOT logged
    into their Shopify customer account
    (window.VPX_CUSTOMER is null — injected by
    the .liquid file). Encourages registration
    so their Blueprint, exercise logs, and
    check-ins sync to their account (via the
    Cloudflare Worker) instead of staying stuck
    on this one browser/device. Purely additive —
    everything still works without an account,
    this is just a nudge, not a restriction.
    ==========================================
    */

    let accountSyncBannerDismissed = false;

    Renderer.accountSyncBanner = function () {
        if (window.VPX_CUSTOMER || accountSyncBannerDismissed) return "";

        return `
            <div class="vpx-lead-banner" data-vpx-account-banner>
                <div class="vpx-lead-banner-text">${t("account_sync_prompt")}</div>
                <div class="vpx-lead-banner-row">
                    <a href="${localeRoot()}account/register" class="vpx-btn-gold" style="text-decoration:none; display:inline-block;">${t("account_sync_cta")}</a>
                    <button type="button" class="vpx-btn-ghost" data-account-banner-dismiss>${t("lead_btn_dismiss")}</button>
                </div>
            </div>
        `;
    };

    function bindAccountSyncBanner() {
        document.querySelectorAll("[data-account-banner-dismiss]").forEach(function (btn) {
            btn.addEventListener("click", function () {
                accountSyncBannerDismissed = true;
                Renderer.render();
            });
        });
    }

    Renderer.leadCaptureBanner = function () {

        const lead = getLeadCapture();

        if (lead.subscribed) {
            if (!leadJustSubscribed) return "";
            return `
                <div class="vpx-lead-banner">
                    <div class="vpx-lead-banner-text">${t("lead_success_prefix")} <strong>${lead.email}</strong> — ${t("lead_success_spam_note")}</div>
                </div>
            `;
        }

        if (lead.dismissed) return "";

        const errorHtml = leadCaptureError
            ? `<div class="vpx-lead-error">${t("lead_error_invalid")}</div>`
            : "";

        return `
            <div class="vpx-lead-banner" data-vpx-lead-context="banner">
                <div class="vpx-lead-banner-text">${t("lead_banner_text")}</div>
                <div class="vpx-lead-banner-row">
                    <input type="email" class="vpx-lead-input" data-lead-email-input placeholder="${t("lead_email_placeholder")}" />
                    <button type="button" class="vpx-btn-gold" data-lead-action="submit">${t("lead_btn_submit")}</button>
                    <button type="button" class="vpx-btn-ghost" data-lead-action="dismiss">${t("lead_btn_dismiss")}</button>
                </div>
                ${errorHtml}
            </div>
        `;
    };

    function bindLeadCaptureEvents() {

        const app = document.getElementById("vpx-app");
        if (!app) return;

        app.querySelectorAll('[data-lead-action="dismiss"]').forEach(function (btn) {
            btn.addEventListener("click", function () {
                if (!window.VORYNEX.state || !window.VORYNEX.state.setSection) return;
                const lead = getLeadCapture();
                window.VORYNEX.state.setSection("leadCapture", Object.assign({}, lead, { dismissed: true }));
                Renderer.render();
            });
        });

        app.querySelectorAll('[data-lead-action="print"]').forEach(function (btn) {
            btn.addEventListener("click", function () {

                // Build a fresh, complete report and place it directly on
                // <body> (outside any Shopify theme wrapper) so the print
                // CSS can reliably hide everything else and show only this.
                let data = ensureTodaysMealPlan(getData());
                data.workoutPlan = ensureWorkoutPlan(data);

                let printHost = document.getElementById("vpx-print-standalone");
                if (!printHost) {
                    printHost = document.createElement("div");
                    printHost.id = "vpx-print-standalone";
                    document.body.appendChild(printHost);
                }
                printHost.innerHTML = buildReportContentHTML(data);

                trackEvent("vpx_report_downloaded", { report_type: "full_report" });
                window.print();
            });
        });

        app.querySelectorAll('[data-lead-action="print-grocery"]').forEach(function (btn) {
            btn.addEventListener("click", function () {

                const data = getData();
                const groups = buildWeeklyGroceryList(data);
                const groupsHtml = buildGroceryGroupsHtml(groups, false);

                let printHost = document.getElementById("vpx-print-standalone");
                if (!printHost) {
                    printHost = document.createElement("div");
                    printHost.id = "vpx-print-standalone";
                    document.body.appendChild(printHost);
                }
                printHost.innerHTML = `
                    <h2>${t("page_grocery_title")}</h2>
                    <div style="font-size:11px; opacity:0.6; margin-bottom:20px;">${new Date().toLocaleDateString()}</div>
                    ${groupsHtml}
                `;

                trackEvent("vpx_report_downloaded", { report_type: "grocery_list" });
                window.print();
            });
        });

        app.querySelectorAll('[data-lead-action="resend"]').forEach(function (btn) {
            btn.addEventListener("click", function () {

                const wrapper = btn.closest("[data-vpx-lead-context]");
                const input = wrapper ? wrapper.querySelector("[data-lead-email-input]") : null;
                const statusEl = wrapper ? wrapper.querySelector("[data-resend-status]") : null;
                const email = input ? input.value.trim() : "";

                if (!isValidEmail(email)) {
                    if (statusEl) {
                        statusEl.style.display = "block";
                        statusEl.style.color = "var(--vpx-red)";
                        statusEl.textContent = t("lead_error_invalid");
                    }
                    return;
                }

                btn.disabled = true;
                const originalLabel = btn.textContent;
                btn.textContent = t("lead_btn_sending");

                // Shopify CRM tagging is fire-and-forget (non-critical,
                // known to be unreliable on this store due to other
                // apps' fetch interceptors — see project notes).
                submitLeadEmail(email).catch(function () { /* ignore */ });

                // The EmailJS send is what the person actually cares
                // about, so its real outcome — success OR the exact
                // error — is shown directly on the page. No DevTools
                // needed to see what happened.
                sendPersonalizedEmail(email)
                    .then(function () {
                        if (statusEl) statusEl.style.display = "none";
                        showToast(t("page_reports_resend_success") + " " + t("lead_success_spam_note"));
                    })
                    .catch(function (error) {
                        const detail = (error && (error.text || error.message)) || String(error);
                        // Kept as persistent inline text (not a toast) —
                        // this is the exact debugging detail a merchant
                        // may need to screenshot; a 3s auto-dismiss would
                        // work against that.
                        if (statusEl) {
                            statusEl.style.display = "block";
                            statusEl.style.color = "var(--vpx-red)";
                            statusEl.textContent = "EmailJS error: " + detail;
                        }
                    })
                    .then(function () {
                        btn.disabled = false;
                        btn.textContent = originalLabel;
                    });
            });
        });

        app.querySelectorAll('[data-lead-action="submit"]').forEach(function (btn) {
            btn.addEventListener("click", function () {

                const wrapper = btn.closest("[data-vpx-lead-context]");
                const input = wrapper ? wrapper.querySelector("[data-lead-email-input]") : app.querySelector("[data-lead-email-input]");
                const email = input ? input.value.trim() : "";

                if (!isValidEmail(email)) {
                    leadCaptureError = true;
                    Renderer.render();
                    return;
                }

                leadCaptureError = false;
                btn.disabled = true;
                btn.textContent = t("lead_btn_sending");

                Promise.allSettled([
                    submitLeadEmail(email),
                    sendPersonalizedEmail(email)
                ]).then(function (results) {
                    console.log("VORYNEX lead submit results — [0]=Shopify tag, [1]=EmailJS send:", results);
                    if (!window.VORYNEX.state || !window.VORYNEX.state.setSection) return;
                    window.VORYNEX.state.setSection("leadCapture", { email: email, subscribed: true, dismissed: false });
                    leadJustSubscribed = true;
                    trackEvent("vpx_lead_captured", {});
                    Renderer.render();
                });
            });
        });
    }

    /*
    ==========================================
    Reports Page
    ------------------------------------------
    Full printable/shareable summary of the
    Blueprint — targets, goal projection, the
    ACTUAL meal plan (every slot/recipe/food),
    and the ACTUAL training program — gated
    behind the lead-capture email above. The
    exact same buildReportContentHTML() output
    is used both for the on-screen preview and
    for the exported PDF, so what you see is
    what you get.
    "Download as PDF" clones that HTML into a
    dedicated element appended directly to
    <body> (so it isn't nested inside Shopify's
    theme wrappers), then triggers the browser's
    native print dialog ("Save as PDF"). Only
    that element stays visible while printing —
    see the #vpx-print-standalone rules in the
    stylesheet. This avoids the blank-page bug
    that the earlier visibility:hidden approach
    had (hidden content still reserved layout
    height, producing extra blank pages).
    ==========================================
    */

    function buildReportMealPlanHTML(mealPlan) {
        return (mealPlan || []).map(function (m) {
            const itemRows = (m.items || []).map(function (item) {
                return `<div class="vpx-onb-review-row"><span>${item.grams}g ${t(item.nameKey)}</span><strong>${item.kcal} kcal</strong></div>`;
            }).join("");
            return `
                <div style="margin-bottom:16px;">
                    <div style="font-size:13px; font-weight:600; margin-bottom:6px;">
                        ${m.icon} ${t(m.nameKey)} — ${t(m.recipeNameKey)}
                        <span style="font-weight:400; opacity:0.7;">(${m.kcal} kcal)</span>
                    </div>
                    <div class="vpx-onb-review">${itemRows}</div>
                </div>
            `;
        }).join("");
    }

    function buildReportWorkoutHTML(workoutPlan) {
        if (!workoutPlan) return "";
        const dayBlocks = workoutPlan.days.map(function (day) {
            const exerciseRows = day.exercises.map(function (ex) {
                const tempo = ex.tempoKey ? ` · ${t("label_tempo")}: ${t(ex.tempoKey)}` : "";
                return `<div class="vpx-onb-review-row"><span>${t(ex.nameKey)}</span><strong>${ex.sets}×${ex.reps} — ${t("label_rest")}: ${t(ex.restKey)} · ${t("label_rir")}: ${t(ex.rirKey)}${tempo}</strong></div>`;
            }).join("");
            return `
                <div style="margin-bottom:14px;">
                    <div style="font-size:13px; font-weight:600; margin-bottom:6px;">${t(day.nameKey)}</div>
                    <div class="vpx-onb-review">${exerciseRows}</div>
                </div>
            `;
        }).join("");
        return `
            <h3 style="margin-top:20px;">${t("nav_workout")}</h3>
            <div style="font-size:12px; opacity:0.7; margin-bottom:12px;">${t(workoutPlan.splitNameKey)}</div>
            ${dayBlocks}
        `;
    }

    function buildReportSupplementsHTML(recommendations) {
        return (recommendations || []).map(function (rec) {
            const nameKey = COLLECTION_NAME_KEYS[rec.collectionHandle] || rec.collectionHandle;
            return `
                <div style="margin-bottom:10px;">
                    <div style="font-size:13px; font-weight:600;">${t(nameKey)}</div>
                    <div style="font-size:12px; opacity:0.7;">${t(rec.reasonKey)}</div>
                </div>
            `;
        }).join("");
    }

    function buildReportContentHTML(data) {

        const macroRows = (data.macros || []).map(function (m) {
            return `<div class="vpx-onb-review-row"><span>${t(m.labelKey)}</span><strong>${m.value}${m.unit}</strong></div>`;
        }).join("");

        const mealPlanHtml = buildReportMealPlanHTML(data.mealPlan);
        const workoutHtml = buildReportWorkoutHTML(data.workoutPlan);
        const supplementsHtml = buildReportSupplementsHTML(data.supplementRecommendations);

        const reportAvatarHtml = (window.VORYNEX.avatar && window.VORYNEX.avatar.render)
            ? `<div class="vpx-report-avatar">${window.VORYNEX.avatar.render()}</div>`
            : "";

        return `
            <div class="vpx-report-header-row">
                ${reportAvatarHtml}
                <div>
                    <h2>${t("page_reports_title")}</h2>
                    <div style="font-size:11px; opacity:0.6;">${new Date().toLocaleDateString()}</div>
                </div>
            </div>

            <h3>${t("page_reports_section_targets")}</h3>
            <div class="vpx-onb-review">
                <div class="vpx-onb-review-row"><span>${t("stat_calories")}</span><strong>${data.targets.calories} kcal</strong></div>
                ${macroRows}
                <div class="vpx-onb-review-row"><span>${t("stat_water")}</span><strong>${data.targets.water} L</strong></div>
            </div>

            <h3 style="margin-top:20px;">${t("page_reports_section_goal")}</h3>
            <div class="vpx-onb-review">
                <div class="vpx-onb-review-row"><span>${t("onb_review_goal")}</span><strong>${t(data.goal.phaseKey)}</strong></div>
                <div class="vpx-onb-review-row"><span>${t("page_timeline_projected")}</span><strong>${formatWeightKg(data.goal.currentWeight)} → ${formatWeightKg(data.goal.targetWeight)} (${data.goal.weeksRemaining} ${t("goal_weeks")})</strong></div>
            </div>

            <h3 style="margin-top:20px;">${t("nav_mealPlan")}</h3>
            ${mealPlanHtml}

            ${workoutHtml}

            ${supplementsHtml ? `
            <h3 style="margin-top:20px;">${t("card_supplements")}</h3>
            ${supplementsHtml}
            ` : ""}

            <div class="vpx-supp-disclaimer" style="margin-top:20px;">${t("page_reports_disclaimer")}</div>
        `;
    }

    Renderer.viewReportsGate = function () {

        const errorHtml = leadCaptureError
            ? `<div class="vpx-lead-error">${t("lead_error_invalid")}</div>`
            : "";

        return `
            <div class="vpx-grid">
                <div class="vpx-card" style="text-align:center; padding:50px 24px;" data-vpx-lead-context="reports-gate">
                    <div style="font-size:36px; margin-bottom:14px;">📄</div>
                    <h2 style="margin-bottom:8px;">${t("page_reports_gate_title")}</h2>
                    <p style="font-size:13px; color:var(--vpx-muted); margin-bottom:20px; max-width:420px; margin-left:auto; margin-right:auto;">${t("page_reports_gate_text")}</p>
                    <div class="vpx-lead-banner-row" style="justify-content:center; max-width:420px; margin:0 auto;">
                        <input type="email" class="vpx-lead-input" data-lead-email-input placeholder="${t("lead_email_placeholder")}" />
                        <button type="button" class="vpx-btn-gold" data-lead-action="submit">${t("page_reports_gate_btn")}</button>
                    </div>
                    ${errorHtml}
                    <button type="button" class="vpx-btn-ghost" style="margin-top:18px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>
            </div>
        `;
    };

    Renderer.viewReportsFull = function (data) {

        const lead = getLeadCapture();
        const justUnlockedNote = leadJustSubscribed
            ? `<div class="vpx-lead-banner vpx-no-print">${t("lead_success_prefix")} <strong>${lead.email}</strong> — ${t("lead_success_spam_note")}</div>`
            : "";

        return `
            <div class="vpx-grid">
                ${justUnlockedNote}

                <div class="vpx-card vpx-no-print">
                    <button type="button" class="vpx-btn-gold" data-lead-action="print">${t("page_reports_download_btn")}</button>
                    <button type="button" class="vpx-btn-ghost" style="margin-left:12px;" data-vpx-nav="dashboard">${t("btn_backToDashboard")}</button>
                </div>

                <div class="vpx-card" id="vpx-print-area">
                    ${buildReportContentHTML(data)}
                </div>

                <div class="vpx-card vpx-no-print" data-vpx-lead-context="reports-resend">
                    <div style="font-size:12px; color:var(--vpx-muted); margin-bottom:10px;">${t("page_reports_resend_hint")}</div>
                    <div class="vpx-lead-banner-row">
                        <input type="email" class="vpx-lead-input" data-lead-email-input placeholder="${t("lead_email_placeholder")}" />
                        <button type="button" class="vpx-btn-ghost" data-lead-action="resend">${t("page_reports_resend_btn")}</button>
                    </div>
                    <div class="vpx-lead-error" data-resend-status style="display:none;"></div>
                </div>
            </div>
        `;
    };

    Renderer.viewReports = function (data) {
        const lead = getLeadCapture();
        if (!lead.subscribed) return Renderer.viewReportsGate();
        return Renderer.viewReportsFull(data);
    };

    /*
    ==========================================
    Root Renderer
    ==========================================
    */

    Renderer.render = function () {

        const app = document.getElementById("vpx-app");

        if (!app) {
            console.error("#vpx-app not found.");
            return;
        }

        const onboardingDone = window.VORYNEX.state
            && window.VORYNEX.state.getSection
            && window.VORYNEX.state.getSection("onboarding")
            && window.VORYNEX.state.getSection("onboarding").completed;

        if (!onboardingDone) {
            if (!heroDismissed) {
                Renderer.renderHero();
                return;
            }
            Renderer.renderOnboarding();
            return;
        }

        let data = getData();
        data = ensureTodaysMealPlan(data);
        data.workoutPlan = ensureWorkoutPlan(data);

        let mainContent;
        if (currentView === "meal-plan") {
            mainContent = Renderer.viewMealPlanFull(data);
        } else if (currentView === "grocery-list") {
            mainContent = Renderer.viewGroceryList(data);
        } else if (currentView === "supplement-timing") {
            mainContent = Renderer.viewSupplementTiming(data);
        } else if (currentView === "supplements") {
            mainContent = Renderer.viewSupplements(data);
        } else if (currentView === "progress") {
            mainContent = Renderer.viewProgress(data);
        } else if (currentView === "performance-scores") {
            mainContent = Renderer.viewPerformanceScores(data);
        } else if (currentView === "nutrition-plan") {
            mainContent = Renderer.viewNutritionPlan(data);
        } else if (currentView === "settings") {
            mainContent = Renderer.viewSettings();
        } else if (currentView === "timeline") {
            mainContent = Renderer.viewTimeline(data);
        } else if (currentView === "schedule") {
            mainContent = Renderer.viewSchedule();
        } else if (currentView === "workout") {
            mainContent = Renderer.viewWorkout(data);
        } else if (currentView === "coach") {
            mainContent = Renderer.viewCoach(data);
        } else if (currentView === "profile") {
            mainContent = Renderer.viewProfile();
        } else if (currentView === "goals") {
            mainContent = Renderer.viewGoals();
        } else if (currentView === "reports") {
            mainContent = Renderer.viewReports(data);
        } else if (currentView === "dashboard") {
            mainContent = Renderer.viewDashboard(data);
        } else if (currentView === "avatar-customize") {
            mainContent = Renderer.viewAvatarCustomize();
        } else if (currentView === "achievements") {
            mainContent = Renderer.viewAchievements();
        } else {
            mainContent = Renderer.viewComingSoon();
        }

        app.innerHTML = `
            <div class="vpx-shell">
                ${Renderer.sidebar(currentView)}
                <div class="vpx-sidebar-overlay" data-vpx-action="close-sidebar"></div>
                <main class="vpx-main">
                    <div class="vpx-mobile-topbar">
                        <button type="button" class="vpx-hamburger-btn" data-vpx-action="toggle-sidebar" aria-label="Menu">☰</button>
                        <div class="vpx-mobile-logo"><span class="vpx-mark">◆</span> VORYNEX</div>
                        <span style="width:24px;"></span>
                    </div>
                    ${Renderer.header(data)}
                    ${mainContent}
                </main>
            </div>
            ${Renderer.mobileBottomNav(currentView)}
            ${Renderer.viewCoachWidget(data)}
        `;

        bindLangSwitcherEvents(function () {
            Renderer.render();
        });

        bindMealSwapEvents();
        bindRecipeSwitcherEvents();
        bindWeekViewEvents();
        bindGroceryEvents();
        bindCondimentEvents();
        bindRestartQuizEvents();
        bindSidebarToggleEvents();
        bindNavEvents();
        bindCoachEvents(data);
        bindProfileEvents();
        bindGoalsEvents();
        bindLeadCaptureEvents();
        bindScheduleEvents();
        bindExerciseProgressEvents();
        bindWeightCheckinEvents();
        bindDashboardMobileTabs();
        bindAccountSyncBanner();
        bindSettingsEvents();
        bindAvatarEvents();
        bindShareAchievementEvents();
        initDashboardAnimations();

        // Scroll to top ONLY when the actual page/view changed — not on
        // every re-render (e.g. checking off a grocery item, dismissing
        // the lead banner, or a resend confirmation also call
        // Renderer.render(), and jumping the scroll position in those
        // cases would be jarring/wrong). This is what was missing:
        // clicking a nav item while scrolled down previously left the
        // person exactly where they were, on the new page's content,
        // with no obvious sign anything had changed above the fold.
        if (currentView !== lastRenderedView) {
            lastRenderedView = currentView;
            window.scrollTo({ top: 0, behavior: "auto" });
        }
    };

    /*
    ==========================================
    Export
    ==========================================
    */

    window.VORYNEX.render = Renderer;
    console.log("Renderer exported (Dashboard view ready).");

    // Product-link click tracking via event delegation on <body> — a
    // single listener that survives every re-render (the markup is
    // rebuilt each time, so per-element listeners would need constant
    // re-attaching; delegation sidesteps that entirely) and covers
    // every "View Product" link across the Dashboard supplements card,
    // the Supplement Timing page, and the collection fallback links.
    document.addEventListener("click", function (e) {
        const productLink = e.target.closest(".vpx-supp-product-row, .vpx-supp-cta-link, .vpx-timing-cta");
        if (productLink) {
            trackEvent("vpx_product_click", {
                link_url: productLink.href || "",
                link_text: (productLink.textContent || "").trim().slice(0, 80)
            });
        }
    });

    Renderer.render();

    /*
    ==========================================
    Midnight Rollover
    ------------------------------------------
    If the tab stays open past midnight, nothing
    would otherwise trigger a re-render — the
    Dashboard would keep showing yesterday's plan
    until the person clicked something. This
    lightweight check (once a minute) detects the
    date change, snaps the Week View selector to
    the new "today", and re-renders; the meal-plan
    date stamp inside ensureTodaysMealPlan then
    regenerates the correct plan for the new day
    automatically. A page reload achieves the same
    via the date-stamp alone — this timer only
    covers the "left the tab open" case.
    ==========================================
    */

    let vpxLastSeenDate = new Date().toISOString().slice(0, 10);

    setInterval(function () {
        const nowDate = new Date().toISOString().slice(0, 10);
        if (nowDate !== vpxLastSeenDate) {
            vpxLastSeenDate = nowDate;
            mealWeekSelectedDay = new Date().getDay();
            console.log("VORYNEX: date changed — refreshing today's plan automatically.");
            Renderer.render();
        }
    }, 60 * 1000);

})();